import React, { lazy, Suspense } from "react";
const DignityNarrationPlayer = lazy(() => import("@/components/dignity/DignityNarrationPlayer").then(m => ({ default: m.DignityNarrationPlayer })));
import { useTranslation } from 'react-i18next';
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Helmet } from "react-helmet-async";
import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { AlertTriangle, TrendingUp, MapPin, ArrowLeft, Search, Crown, Zap, Filter, ArrowUpDown, Info, Building2, ChevronUp, Settings, RefreshCw, ChevronDown, ChevronRight, Columns3, Star, X, Bed, Hospital, ExternalLink, ClipboardList, Flame, Bone, Activity, Scissors, Baby, Heart, Ribbon, Shield, Pill, Wind, Replace, Dna, Stethoscope, Brain, Microscope, Syringe, Eye, Maximize2, Minimize2, Banknote } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SCENARIO_CATEGORIES } from "@/config/scenarioCategories";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { USAMapNavigator } from "@/components/USAMapNavigator";
import { useBrand } from "@/config/brands";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "@/contexts/ThemeContext";
import { useVirtualizer } from '@tanstack/react-virtual';
import { trackRankingsFilter } from "@/utils/analytics";
import { ProcedureDrilldownModal } from "@/components/ProcedureDrilldownModal";
import { useAhpHeaderOffset } from "@/components/AhpGlobalHeader";
import { loadStaticData, useLiveDb } from "@/lib/offlineData";
import { hapticLight } from "@/lib/haptics";
import { loadScenarioCoverageFor, getCoverageForScenario, getHospitalInfo } from "@/lib/scenarioCoverageStore";
import { blendCoverageRow } from "@/lib/priceSanity";
import { DashboardScenarioCard, DashboardProcedureCodes, InternationalOverlay, useScenarioDetail, type DashboardTheme } from "@/components/rankings/ScenarioDashboard";
import { ScenarioCoverageModal } from "@/components/rankings/ScenarioCoverageModal";
import { ScenarioFamilyModal } from "@/components/ScenarioFamilyModal";
import { ScenarioCareResources } from "@/components/scenarios/ScenarioCareResources";
import { PatrioticLoader } from "@/components/state-comparison/PatrioticLoader";
import { titleCaseHospital } from "@/lib/hospitalName";

interface HospitalRanking {
  hospital_id: string;
  avg_gross_markup_percentage: number;
  // Median-baseline markup — the metric overall_rank_gross is ordered by
  // (resilient to single-hospital anomalous filings). Displayed on the
  // default markup sort so the shown number matches the rank order.
  median_gross_markup_percentage?: number | null;
  expensive_procedures_count: number;
  expensive_procedures_percentage?: number;
  outlier_expensive_count: number;
  outlier_percentage?: number;
  shame_score_calculated: number;
  overall_rank_gross: number;
  total_procedures: number;
  total_available_procedures?: number;
  uncoded_procedures?: number;
  hospital_name: string;
  hospital_city?: string;
  hospital_state?: string;
  cpt_count?: number;
  drg_count?: number;
  revenue_count?: number;
  ndc_count?: number;
  cpt_procedure_count?: number;
  drg_procedure_count?: number;
  rev_procedure_count?: number;
  ndc_procedure_count?: number;
  hcpcs_procedure_count?: number;
  supply_procedure_count?: number;
  drug_procedure_count?: number;
  cpt_avg_markup_percentage?: number;
  drg_avg_markup_percentage?: number;
  rev_avg_markup_percentage?: number;
  ndc_avg_markup_percentage?: number; // Alias for drug_avg_markup_percentage
  hcpcs_avg_markup_percentage?: number;
  supply_avg_markup_percentage?: number;
  drug_avg_markup_percentage?: number;
  // Total available counts for coverage calculation
  cpt_total_available?: number;
  drg_total_available?: number;
  rev_total_available?: number;
  ndc_total_available?: number;
  hcpcs_total_available?: number;
  supply_total_available?: number;
  drug_total_available?: number;
  // Reviews aggregation (third-party rating sources)
  google_rating?: number;
  google_review_count?: number;
  cms_overall_rating?: number;
  // Profile (collapsed-by-default hospital metadata)
  bed_count?: number;
  hospital_type?: string;
  ownership?: string;
  emergency_services?: boolean;
  hospital_system?: string;
  year_founded?: number;
  annual_revenue_usd?: number;
  revenue_year?: number;
  revenue_source?: string;
  // Provider IDs (link out to source-of-truth pages)
  npi_number?: string;
  ccn?: string;
  ein?: string;
  // CMS quality sub-ratings
  cms_mortality_rating?: string;
  cms_safety_rating?: string;
  cms_readmission_rating?: string;
  cms_patient_experience_rating?: string;
  // Quality measures — actual reported %s for the broadest CMS measures, joined from hospital_quality_measures.
  // mortality_score = Hybrid_HWM (Hospital-Wide Mortality, ~5,200 hospitals report it).
  // readmission_score = Hybrid_HWR (Hospital-Wide Readmission, ~5,300 hospitals).
  mortality_score?: number;
  mortality_denom?: number;
  readmission_score?: number;
  readmission_denom?: number;
  // CMS Care Compare per-measure scores baked into rankings.json:
  //   hcahps_recommend_pct = HCAHPS H_RECMND_DY "would definitely recommend" %  (higher better)
  //   hai_risk_sir         = HAI_1_SIR central-line bloodstream infection ratio (lower safer)
  //   er_wait_minutes      = OP_18b median minutes spent in the ED               (lower better)
  hcahps_recommend_pct?: number;
  hai_risk_sir?: number;
  er_wait_minutes?: number;
  // Profits — latest HCRIS fiscal year (FY2024 first)
  finance_fiscal_year?: number;
  hcris_net_patient_revenue_usd?: number;
  medicare_total_payments_usd?: number;
  operating_margin_pct?: number;
  charity_care_usd?: number;
  // Highest total_comp_usd from hospital_990_executives (Schedule J Part II)
  ceo_comp_usd?: number;
  // Scenario mode fields (from scenario_hospital_coverage)
  scenario_total_price?: number;
  scenario_national_price?: number;
  scenario_blended_price?: number;
  scenario_coverage_pct?: number;
  scenario_matched_codes?: number;
  scenario_total_codes?: number;
}

// Helper to format large numbers with k/M suffix
const formatNumberCompact = (num: number | null | undefined): string => {
  // Handle null, undefined, and non-numeric values
  const numValue = num != null ? Number(num) : 0;
  if (!numValue || isNaN(numValue)) return '0';
  if (numValue >= 1000000) {
    return `${(numValue / 1000000).toFixed(1)}M`;
  }
  if (numValue >= 1000) {
    return `${(numValue / 1000).toFixed(0)}k`;
  }
  return numValue.toLocaleString();
};

// Helper to format markup percentages with k suffix for large numbers
const formatMarkupPercentage = (percent: number | null | undefined): string => {
  // Handle null, undefined, and non-numeric values
  if (percent == null) return '+0%';
  const numValue = Number(percent);
  if (isNaN(numValue)) return '+0%';
  if (!numValue && numValue !== 0) return '+0%';

  const absPercent = Math.abs(numValue);
  const sign = numValue >= 0 ? '+' : '';

  if (absPercent >= 1000) {
    return `${sign}${(numValue / 1000).toFixed(1)}k%`;
  }

  return `${sign}${numValue.toFixed(1)}%`;
};

// Helper functions moved outside component to prevent recreation on every render
const getGradientStyle = (markupPercentage: number, maxMarkup: number, minMarkup: number, isAhp = false, isAhpLight = false) => {
  let backgroundColor: string;
  let textColor: string;

  if (markupPercentage <= -10) {
    // Very low markup (negative) - deep green gradient
    const intensity = Math.min(1, Math.abs(markupPercentage + 10) / 50); // Scale for -10% to -60%
    const alpha = 0.3 + intensity * 0.5;
    backgroundColor = `rgba(34, 197, 94, ${alpha})`;
    textColor = isAhpLight ? (alpha >= 0.55 ? "#ffffff" : "#14532d") :
                (isAhp || intensity > 0.4) ? "#ffffff" : "#14532d";
  } else if (markupPercentage < 10) {
    // Near-neutral markup (-10% to 10%) - black/gray gradient
    const distanceFromZero = Math.abs(markupPercentage);
    const intensity = distanceFromZero / 10;
    backgroundColor = `rgba(64, 64, 64, ${0.1 + intensity * 0.3})`;
    textColor = isAhpLight ? "#1B1B1B" : isAhp ? "#ffffff" : "inherit";
  } else if (markupPercentage < 50) {
    // Moderate markup (10% to 50%) - light red gradient
    const intensity = (markupPercentage - 10) / 40;
    const alpha = 0.2 + intensity * 0.3;
    backgroundColor = `rgba(239, 68, 68, ${alpha})`;
    textColor = isAhpLight ? (alpha >= 0.4 ? "#ffffff" : "#7f1d1d") :
                (isAhp || intensity > 0.5) ? "#ffffff" : "#7f1d1d";
  } else {
    // High markup (50%+) - deep red gradient
    const intensity = Math.min(1, (markupPercentage - 50) / 100); // Scale for 50% to 150%+
    backgroundColor = `rgba(239, 68, 68, ${0.5 + intensity * 0.4})`;
    textColor = "#ffffff";
  }

  return { backgroundColor, color: textColor };
};

const getShameLevel = (outlierCount: number, totalProcedures: number) => {
  const outlierPercentage = totalProcedures > 0 ? (outlierCount / totalProcedures) * 100 : 0;

  if (outlierPercentage >= 40) return { level: "EXTREME", color: "bg-red-600", icon: AlertTriangle, percentage: outlierPercentage };
  if (outlierPercentage >= 25) return { level: "SEVERE", color: "bg-orange-500", icon: TrendingUp, percentage: outlierPercentage };
  if (outlierPercentage >= 10) return { level: "HIGH", color: "bg-yellow-500", icon: Zap, percentage: outlierPercentage };
  return { level: "MODERATE", color: "bg-gray-500", icon: Crown, percentage: outlierPercentage };
};

// Helper to get CSS class based on markup percentage (uses CSS classes instead of inline styles for mobile performance)
const getMarkupColorClass = (markupPercentage: number) => {
  if (markupPercentage >= 100) return 'markup-extreme';
  if (markupPercentage >= 50) return 'markup-high';
  if (markupPercentage >= 25) return 'markup-moderate';
  if (markupPercentage >= 10) return 'markup-low';
  if (markupPercentage >= 0) return 'markup-minimal';
  return 'markup-negative';
};

// Slot-machine style digit that randomly cycles 0-9 while loading. Each digit gets a slightly
// different interval so the row of digits feels like several spinning reels instead of one synced clock.
const TickerDigit: React.FC<{ delay: number }> = ({ delay }) => {
  const [d, setD] = useState(() => Math.floor(Math.random() * 10));
  useEffect(() => {
    const id = setInterval(() => setD(Math.floor(Math.random() * 10)), 90 + delay);
    return () => clearInterval(id);
  }, [delay]);
  return <span className="inline-block w-[0.55em] text-center">{d}</span>;
};

// Loading placeholder for the revenue cell — looks like a price ticker mid-roll.
// Renders "$XXX M" or "$X.XB" alternating to suggest unknown magnitude.
const RevenueTicker: React.FC<{ size?: 'sm' | 'md' }> = ({ size = 'md' }) => {
  const [variant, setVariant] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setVariant(v => (v + 1) % 3), 700);
    return () => clearInterval(id);
  }, []);
  const cls = `font-mono tabular-nums text-white/40 ${size === 'sm' ? 'text-[10px] sm:text-xs' : 'text-xs sm:text-base'} font-bold`;
  if (variant === 0) {
    return <span className={cls}>$<TickerDigit delay={0} /><TickerDigit delay={15} /><TickerDigit delay={35} />M</span>;
  }
  if (variant === 1) {
    return <span className={cls}>$<TickerDigit delay={5} />.<TickerDigit delay={25} />B</span>;
  }
  return <span className={cls}>$<TickerDigit delay={10} /><TickerDigit delay={30} />.<TickerDigit delay={50} />B</span>;
};

// Conditional Tooltip wrapper - renders tooltip only on desktop (mobile Safari can't handle thousands of tooltips)
const ConditionalTooltip = ({ children, content, isMobile }: { children: React.ReactNode; content: React.ReactNode; isMobile: boolean }) => {
  if (isMobile) {
    // On mobile, just render the trigger without tooltip
    return <>{children}</>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs" style={{ zIndex: 9999 }}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
};

const HospitalRankings = () => {
  const { state: stateParam, rankType: rankTypeParam, dimension: dimensionParam } = useParams<{ state?: string; rankType?: string; dimension?: string }>();
  // /hospital-rankings/financial/:dimension[/:state] — dedicated SEO-friendly URLs
  // per financial dimension. Each dimension maps to a specific sortBy value and
  // unique title/description meta so search engines can index each leaderboard
  // as its own page. Canonical replacement for the legacy /executive-comp/ URL.
  const FINANCIAL_DIMENSIONS = {
    'exec-bed': {
      sortBy: 'exec_comp_per_bed' as const,
      title: 'Highest Paid Hospital CEOs Per Bed',
      description: 'Hospitals ranked by top executive compensation (from IRS Form 990 filings) divided by staffed bed count. See which non-profit hospital CEOs are paid the most relative to facility size.',
      h1: 'Hospital CEO Pay Per Bed Leaderboard',
      keywords: 'hospital CEO salary, hospital executive compensation, 990 executive pay, hospital pay per bed, healthcare CEO ranking',
    },
    'operating-margin': {
      sortBy: 'operating_margin' as const,
      title: 'Hospital Operating Margin Rankings',
      description: 'Hospitals ranked by operating margin from CMS HCRIS cost reports. See which hospitals are most and least profitable on a per-facility basis.',
      h1: 'Hospital Operating Margin Leaderboard',
      keywords: 'hospital operating margin, hospital profitability, HCRIS operating margin, hospital financial performance',
    },
    'charity-care': {
      sortBy: 'charity_care' as const,
      title: 'Hospital Charity Care Spending',
      description: 'Hospitals ranked by charity care dollars from CMS HCRIS filings. See which hospitals contribute the most uncompensated care to their communities.',
      h1: 'Hospital Charity Care Leaderboard',
      keywords: 'hospital charity care, hospital community benefit, uncompensated care, HCRIS charity care',
    },
    'revenue': {
      sortBy: 'net_patient_revenue' as const,
      title: 'Largest Hospitals by Net Patient Revenue',
      description: 'Hospitals ranked by net patient revenue from HCRIS cost reports. See which hospitals run the largest revenue operations.',
      h1: 'Hospital Net Patient Revenue Leaderboard',
      keywords: 'hospital revenue, largest hospitals, hospital net patient revenue, HCRIS revenue',
    },
  } as const;
  type FinancialDimension = keyof typeof FINANCIAL_DIMENSIONS;
  // The bare literal landing routes (/hospital-rankings/executive-comp and
  // /hospital-rankings/financial) bind NO :dimension/:state param (React Router
  // ranks the static segment above the dynamic :state route), so we derive the
  // dimension from the pathname. Without this the page silently fell back to the
  // default markup sort instead of the CEO-pay / operating-margin leaderboard.
  const _bareRankingsPath = useLocation().pathname.toLowerCase().replace(/\/+$/, '');
  const bareDimension: FinancialDimension | undefined =
    _bareRankingsPath.endsWith('/hospital-rankings/executive-comp') ? 'exec-bed'
    : _bareRankingsPath.endsWith('/hospital-rankings/financial') ? 'operating-margin'
    : undefined;
  const normalizedDimension = (dimensionParam?.toLowerCase() as FinancialDimension | undefined) || bareDimension;
  const isFinancialMode = !!normalizedDimension && normalizedDimension in FINANCIAL_DIMENSIONS;
  const financialMeta = isFinancialMode ? FINANCIAL_DIMENSIONS[normalizedDimension!] : null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const brand = useBrand(); // Get brand config for theme colors
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';
  const ahpHeaderOffset = useAhpHeaderOffset();
  const { t } = useTranslation(['rankings', 'common']);
  const { toast } = useToast();
  const cellBg = isAhp ? 'ahp-navy-cell' : 'bg-black/80';

  // ── Theme color helper (AHP light ↔ dark) ────────────────────────────────
  const ahpC = {
    pageBg:       isAhp ? (isDark ? '#0a1628' : '#FAF9F6') : undefined,
    cardBg:       (a: number) => isDark ? `rgba(10,22,40,${a})` : `rgba(255,255,255,${Math.min(a + 0.15, 1)})`,
    navyBg:       isAhp ? (isDark ? 'rgba(0,20,60,0.95)' : 'rgba(250,249,246,0.98)') : 'rgba(0,20,60,0.95)',
    dropdownBg:   isAhp ? (isDark ? '#08122a' : '#ffffff') : '#080c16',
    text:         isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : '#ffffff',
    textAt:       (a: number) => isAhp ? (isDark ? `rgba(250,249,246,${a})` : `rgba(0,0,0,${a})`) : `rgba(255,255,255,${a})`,
    borderCls:    isAhp ? (isDark ? 'border-white/10' : 'border-black/10') : 'border-white/10',
    borderAt:     (a: number) => isAhp ? (isDark ? `rgba(255,255,255,${a})` : `rgba(0,0,0,${Math.max(a * 0.5, 0.08)})`) : `rgba(255,255,255,${a})`,
    searchBg:     isAhp ? (isDark ? 'rgba(10,22,40,0.6)' : 'rgba(0,0,0,0.04)') : 'rgba(255,255,255,0.06)',
    searchText:   isAhp ? (isDark ? 'rgba(255,255,255,0.9)' : '#1B1B1B') : 'rgba(255,255,255,0.9)',
    videoOverlay: isDark
      ? 'linear-gradient(180deg, rgba(10,22,40,0.92) 0%, rgba(0,20,60,0.85) 50%, rgba(10,22,40,0.95) 100%)'
      : 'linear-gradient(180deg, rgba(250,249,246,0.88) 0%, rgba(250,249,246,0.72) 50%, rgba(250,249,246,0.88) 100%)',
    videoOpacity: isDark ? 0.12 : 0.05,
    skBg:         isAhp ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)') : 'rgba(255,255,255,0.08)',
    skBgStrong:   isAhp ? (isDark ? 'rgba(197,165,90,0.12)' : 'rgba(178,34,52,0.12)') : 'rgba(0,255,65,0.08)',
    skBgRed:      'rgba(239,68,68,0.15)',
    loadingBg:    isAhp ? (isDark ? '#0a1628' : '#FAF9F6') : 'hsl(var(--background))',
    loadingCardBg: isAhp ? (isDark ? 'rgba(10,22,40,0.85)' : 'rgba(255,255,255,0.95)') : 'rgba(0,0,0,0.85)',
    accentDots:   isAhp ? '#C5A55A' : '#00ff41',
    loadingText:  isAhp ? (isDark ? 'rgba(250,249,246,0.4)' : 'rgba(0,0,0,0.4)') : 'rgba(0,255,65,0.6)',
  };

  // Scenario mode: ?scenario=<uuid>
  const scenarioId = searchParams.get('scenario') || null;
  const forceLive = searchParams.get('live') === 'true';
  const liveDb = useLiveDb();
  const isScenarioMode = !!scenarioId;

  // Parse URL: /hospital-rankings/tx/cpt or /hospital-rankings/cpt (no state = usa)
  // Code types: cpt, hcpcs, drg, rev, ndc, supply
  const VALID_CODE_TYPES = ['cpt', 'hcpcs', 'drg', 'rev', 'ndc', 'supply'] as const;
  const isCodeType = (s?: string) => s && VALID_CODE_TYPES.includes(s.toLowerCase() as any);

  // If stateParam is a code type (e.g., /hospital-rankings/cpt), treat it as rankType with state=USA
  const effectiveState = stateParam && isCodeType(stateParam) ? 'USA' : (stateParam ? stateParam.toUpperCase() : 'USA');
  const effectiveRankType = isCodeType(rankTypeParam) ? rankTypeParam!.toLowerCase()
    : isCodeType(stateParam) ? stateParam!.toLowerCase()
    : null;

  const selectedState = effectiveState;
  const isStateFiltered = selectedState !== 'USA';

  // Deep link: ?breakdown=UUID&type=cpt opens the drilldown modal
  const breakdownHospitalId = searchParams.get('breakdown');
  const breakdownType = searchParams.get('type');

  // Detect mobile to disable tooltips (massive memory savings on mobile Safari)
  const [isMobile, setIsMobile] = useState(false);
  const [mapTab, setMapTab] = useState<'usa' | 'intl'>('usa');

  // AHP: Collapsible code breakdown columns (collapsed by default on AHP)
  const [codeColumnsExpanded, setCodeColumnsExpanded] = useState(true);
  // Reviews columns (Google + CMS ratings): collapsed by default — shown as a
  // combined-star summary cell that expands on click.
  const [reviewsColumnsExpanded, setReviewsColumnsExpanded] = useState(false);
  // Profile columns (Beds + Type/System/Revenue): collapsed by default — shown as a
  // compact "Profile" summary cell that expands on click.
  const [profileColumnsExpanded, setProfileColumnsExpanded] = useState(false);
  // Profits — financial data we have via CCN (Medicare $, Operating Margin,
  // Charity Care, CEO Comp, Tax Exemption). Collapsed by default, shows a single
  // summary cell that expands to detail columns. Sits to the LEFT of Quality.
  const [profitsColumnsExpanded, setProfitsColumnsExpanded] = useState(false);
  const profitsColsHidden = !profitsColumnsExpanded;
  const profitsColClass = profitsColsHidden ? 'quality-col-collapsed' : '';
  const profitsHiddenStyle = profitsColsHidden ? { width: 0, padding: 0, overflow: 'hidden' as const, border: 'none' } : undefined;
  // Back-compat alias used by lots of memoization deps; both groups must be expanded
  // to match the legacy "everything visible" behavior.
  const qualityColumnsExpanded = reviewsColumnsExpanded && profileColumnsExpanded;
  const setQualityColumnsExpanded = (v: boolean) => {
    setReviewsColumnsExpanded(v);
    setProfileColumnsExpanded(v);
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showDignityNarration, setShowDignityNarration] = useState(false);
  const [browseCategory, setBrowseCategory] = useState<string | null>(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('browseCategory') || null;
  });
  const [browseSevFilter, setBrowseSevFilter] = useState<Record<string, number | null>>({});
  const [showBrowseDropdown, setShowBrowseDropdown] = useState(false);
  const [browseSearch, setBrowseSearch] = useState('');
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [scenarioBrowserExpanded, setScenarioBrowserExpanded] = useState(false);
  // Tableau-style fullscreen mode for the rankings table — collapses surrounding chrome
  // (page header, dignity guide, methodology footer) so reporters scanning the list can
  // use 100% of vertical space. Toggled by the chevron between Color Guide and Shame badges.
  const [isTableFullscreen, setIsTableFullscreen] = useState(false);
  // Mobile: the CPT/HCPCS/DRG/REV code-type breakdown is collapsed by default —
  // consumers don't need it, but it's one tap away if they're curious.
  const [mobileCodeOpen, setMobileCodeOpen] = useState(false);
  // Mobile: per-card expander showing the multi-dimension breakdown (markup by
  // code type, quality, financial, misc) instead of jumping straight to profile.
  const [expandedRankId, setExpandedRankId] = useState<string | null>(null);
  const [expandTab, setExpandTab] = useState<'markup' | 'quality' | 'financial' | 'misc'>('markup');
  const [currentPage, setCurrentPage] = useState(1);
  // /hospital-rankings/executive-comp/ → sort by exec_comp_per_bed
  // (CEO total comp from hospital_990_executives ÷ hospitals.bed_count). Lets users
  // see which hospitals' top execs are paid the most relative to their facility size,
  // independent of the price-markup ranking. Coverage is currently sparse (~9 of 9.5K
  // hospitals have 990 exec data — task #83 tracks bulk EIN extraction) so the column
  // initially ranks only those hospitals; rest show "—".
  const isExecCompMode = rankTypeParam?.toLowerCase() === 'executive-comp'
    || stateParam?.toLowerCase() === 'executive-comp'
    || normalizedDimension === 'exec-bed';
  const [sortBy, setSortBy] = useState<'markup' | 'shame' | 'google_rating' | 'cms_rating' | 'mortality' | 'readmission' | 'hcahps' | 'hai_risk' | 'er_wait_time' | 'bed_count' | 'scenario_price' | 'exec_comp_per_bed' | 'operating_margin' | 'charity_care' | 'net_patient_revenue'>(
    scenarioId ? 'scenario_price'
      : isFinancialMode ? financialMeta!.sortBy
      : isExecCompMode ? 'exec_comp_per_bed'
      : 'markup'
  );
  const [rankByCodeType, setRankByCodeType] = useState<'overall' | 'cpt' | 'hcpcs' | 'drg' | 'rev' | 'ndc' | 'supply'>(
    effectiveRankType as any || 'overall'
  );
  // Per-hospital CMS-measure cache for the on-demand quality sorts (hcahps,
  // hai_risk, er_wait_time). Populated by useEffect below the first time
  // sortBy switches to one of those keys; reused for subsequent sorts since
  // the data doesn't churn within a session. Map: hospital_id -> Map<measureKey, score>.
  const [cmsMeasureCache, setCmsMeasureCache] = useState<Map<string, Map<string, number>>>(() => new Map());
  const [filterCodeType, setFilterCodeType] = useState<'all' | 'cpt' | 'drg' | 'rev'>('all');
  const [filterCode, setFilterCode] = useState("");
  const [searchFocused, setSearchFocused] = useState(false);
  const [scenarioSearch, setScenarioSearch] = useState("");
  const [showScenarioDropdown, setShowScenarioDropdown] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [thumbHeight, setThumbHeight] = useState(40); // Dynamic thumb height
  // Refs for the custom scrollbar thumb + percentage label — written to directly
  // from the scroll handler so wheel events don't trigger React re-renders of the
  // 6k-line HospitalRankings page (the dominant source of scroll lag).
  const scrollbarThumbRef = useRef<HTMLDivElement>(null);
  const scrollbarPercentRef = useRef<HTMLSpanElement>(null);
  const [konamiActivated, setKonamiActivated] = useState(false);
  const [drilldownModal, setDrilldownModal] = useState<{
    isOpen: boolean;
    hospitalId: string | null;
    hospitalName: string;
    procedureType: string | null;
    avgMarkup: number;
  }>({
    isOpen: false,
    hospitalId: null,
    hospitalName: '',
    procedureType: null,
    avgMarkup: 0,
  });
  const [coverageModal, setCoverageModal] = useState<{
    isOpen: boolean;
    hospitalId: string | null;
    hospitalName: string;
    coveragePct: number | null;
    matchedCodes: number | null;
    totalCodes: number | null;
    hospitalTotalPrice: number | null;
    nationalTotalPrice: number | null;
  }>({
    isOpen: false,
    hospitalId: null,
    hospitalName: '',
    coveragePct: null,
    matchedCodes: null,
    totalCodes: null,
    hospitalTotalPrice: null,
    nationalTotalPrice: null,
  });
  const pageSize = 100;

  // Detect mobile on mount (for disabling tooltips on mobile to save memory)
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || window.innerWidth < 768;
      setIsMobile(isMobileDevice);
      console.log('📱 Mobile detection:', isMobileDevice ? 'MOBILE (tooltips disabled)' : 'DESKTOP (tooltips enabled)');
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ─── Embed/tour postMessage bridge ──────────────────────────────────────
  // /home-beta embeds this page as an iframe (?embed=1) and drives the tour
  // by posting:
  //   { type: "demo:open",     dimension: "<key>" }   — switch sort/code-type lens
  //   { type: "demo:scenario", scenario:  "<uuid>" } — switch into scenario mode
  // We map dimension keys ↔ internal (sortBy, rankByCodeType) state. Keys that
  // aren't representable in the current state machine (e.g. mortality) are
  // ignored gracefully — no crash, the rail click just becomes a no-op.
  // After applying a change locally we also emit `demo:selected` back to the
  // parent so the left-rail can light up the right entry (closing the loop
  // for any state change that originates inside the iframe via user click).
  type RankSortBy = typeof sortBy;
  type RankCodeType = typeof rankByCodeType;
  const applyDemoDimension = useCallback((key: string): { sortBy: RankSortBy; rankByCodeType: RankCodeType } | null => {
    switch (key) {
      case "overall_markup":  return { sortBy: "markup", rankByCodeType: "overall" };
      case "outlier_rate":    return { sortBy: "shame",  rankByCodeType: "overall" };
      case "cpt_markup":      return { sortBy: "markup", rankByCodeType: "cpt" };
      case "hcpcs_markup":    return { sortBy: "markup", rankByCodeType: "hcpcs" };
      case "msdrg_markup":    return { sortBy: "markup", rankByCodeType: "drg" };
      case "rev_markup":      return { sortBy: "markup", rankByCodeType: "rev" };
      case "ndc_markup":      return { sortBy: "markup", rankByCodeType: "ndc" };
      case "annual_revenue":  return { sortBy: "net_patient_revenue", rankByCodeType: "overall" };
      case "operating_margin":return { sortBy: "operating_margin",    rankByCodeType: "overall" };
      case "charity_care":    return { sortBy: "charity_care",        rankByCodeType: "overall" };
      case "ceo_comp":        return { sortBy: "exec_comp_per_bed",   rankByCodeType: "overall" };
      // Quality bench: cms star, mortality, readmission, google rating all
      // sort on existing per-hospital columns that ship in rankings.json.
      // Patient-Experience HCAHPS, HAI Risk, and ER Wait Time still require
      // hospital_cms_data joins we haven't aggregated into rankings.json yet
      // — they fall through to null so the iframe stays on its current
      // view rather than snapping back to default markup. The sidebar
      // already labels those as COMING SOON, so the no-op is honest.
      case "cms_rating":      return { sortBy: "cms_rating",          rankByCodeType: "overall" };
      case "mortality":       return { sortBy: "mortality",           rankByCodeType: "overall" };
      case "readmission":     return { sortBy: "readmission",         rankByCodeType: "overall" };
      case "google_rating":   return { sortBy: "google_rating",       rankByCodeType: "overall" };
      // CMS-Care-Compare-backed dims — fetched on demand from
      // hospital_cms_data when sortBy switches to these (see qualityMeasures
      // map + the useEffect that hydrates them per-hospital).
      case "hcahps":          return { sortBy: "hcahps",              rankByCodeType: "overall" };
      case "hai_risk":        return { sortBy: "hai_risk",            rankByCodeType: "overall" };
      case "er_wait_time":    return { sortBy: "er_wait_time",        rankByCodeType: "overall" };
      // International Multiplier is per-category, not per-hospital — surfaces
      // as a no-op on the rankings table. Sidebar pill is informational.
      default: return null;
    }
  }, []);
  const dimensionKeyFromState = useCallback((sb: RankSortBy, rct: RankCodeType): string | null => {
    if (sb === "markup" && rct === "overall") return "overall_markup";
    if (sb === "shame") return "outlier_rate";
    if (sb === "markup" && rct === "cpt")   return "cpt_markup";
    if (sb === "markup" && rct === "hcpcs") return "hcpcs_markup";
    if (sb === "markup" && rct === "drg")   return "msdrg_markup";
    if (sb === "markup" && rct === "rev")   return "rev_markup";
    if (sb === "markup" && rct === "ndc")   return "ndc_markup";
    if (sb === "net_patient_revenue") return "annual_revenue";
    if (sb === "operating_margin")    return "operating_margin";
    if (sb === "charity_care")        return "charity_care";
    if (sb === "exec_comp_per_bed")   return "ceo_comp";
    if (sb === "cms_rating")          return "cms_rating";
    if (sb === "mortality")           return "mortality";
    if (sb === "readmission")         return "readmission";
    if (sb === "google_rating")       return "google_rating";
    if (sb === "hcahps")              return "hcahps";
    if (sb === "hai_risk")            return "hai_risk";
    if (sb === "er_wait_time")        return "er_wait_time";
    return null;
  }, []);

  // Receive messages from the parent. Only active when this page is actually
  // iframed (window.parent !== window) — keeps the listener cheap on the
  // standalone /hospital-rankings route.
  useEffect(() => {
    if (typeof window === "undefined" || window.parent === window) return;
    const handler = (e: MessageEvent) => {
      const msg = e.data;
      if (!msg || typeof msg !== "object" || typeof msg.type !== "string") return;
      if (msg.type === "demo:open" && typeof msg.dimension === "string") {
        const next = applyDemoDimension(msg.dimension);
        if (!next) return;
        setSortBy(next.sortBy);
        setRankByCodeType(next.rankByCodeType);
        // Clear scenario mode when switching back to a column/metric lens —
        // otherwise the page stays stuck on the previous scenario view.
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.delete("scenario");
          if (!p.has("embed")) p.set("embed", "1");
          return p;
        }, { replace: true });
      } else if (msg.type === "demo:scenario" && typeof msg.scenario === "string") {
        // Scenario mode is URL-driven — switching searchParams lights up the
        // scenario-aware sort/columns automatically through the existing flow.
        setSearchParams((prev) => {
          const p = new URLSearchParams(prev);
          p.set("scenario", msg.scenario);
          if (!p.has("embed")) p.set("embed", "1");
          return p;
        }, { replace: true });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [applyDemoDimension, setSearchParams]);

  // Emit `demo:selected` back to the parent whenever the iframe's active
  // lens changes — covers both parent-triggered changes (round-trip) and
  // user-clicks-inside-the-iframe (so the parent rail can highlight what
  // the user actually opened). Gated on iframed context so the standalone
  // /hospital-rankings page doesn't shout into a void.
  useEffect(() => {
    if (typeof window === "undefined" || window.parent === window) return;
    try {
      if (scenarioId) {
        window.parent.postMessage({ type: "demo:selected", scenario: scenarioId }, "*");
      } else {
        const dim = dimensionKeyFromState(sortBy, rankByCodeType);
        if (dim) window.parent.postMessage({ type: "demo:selected", dimension: dim }, "*");
      }
    } catch {
      // Cross-origin postMessage can throw if the parent navigated away —
      // safe to swallow because the next state change will retry.
    }
  }, [sortBy, rankByCodeType, scenarioId, dimensionKeyFromState]);

  // Hide body scrollbars on this page
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // model-viewer was previously loaded here to render Dignity's 3D GLB. It was
  // dropped on this page because the GLB renderer runs an internal rAF loop that
  // stalls behind the rankings table's layout/paint and produces ~350ms long
  // tasks every frame (idle-blocked main thread ~96% in measurement). PNG-only
  // on this page; scenarios page still owns the 3D rendering where it's the focus.

  // Debounce search term to prevent freezing on each keypress
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce for smoother typing
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Hydrate cmsMeasureCache on demand when sortBy switches to a CMS-Care-Compare
  // dim. Three measures live in hospital_cms_data:
  //   - hcahps      → H_RECMND_DY ("would definitely recommend") % → higher better
  //   - hai_risk    → HAI_1_SIR central-line bloodstream infection SIR → lower safer
  //   - er_wait_time→ OP_18b median ER minutes → lower better
  // Single batch select per measure, parsed to numeric, cached for the session.
  // CMS Score values are JSONB strings, parsed via Number(...) with NaN guards.
  useEffect(() => {
    const measureMap: Record<string, { dataset: string; code: string; scoreField: string }> = {
      hcahps:        { dataset: 'hcahps',           code: 'H_RECMND_DY', scoreField: 'HCAHPS Answer Percent' },
      hai_risk:      { dataset: 'hai',              code: 'HAI_1_SIR',   scoreField: 'Score' },
      er_wait_time:  { dataset: 'timely_effective', code: 'OP_18b',      scoreField: 'Score' },
    };
    // What to fetch: always fetch the measure the user is actively sorting
    // by; also eagerly prefetch the trio whenever Quality columns are
    // expanded so the cells aren't stuck on "—" until someone clicks a
    // sort. The cache de-dups (skipped when ≥1000 rows already loaded for
    // a key), so this is a one-shot per page-mount.
    const needed = new Set<string>();
    if (measureMap[sortBy as keyof typeof measureMap]) needed.add(sortBy);
    // reviewsColsHidden gets derived ~1800 lines below; read the source
    // state here directly to avoid a temporal-dead-zone reference.
    if (reviewsColumnsExpanded) {
      needed.add('hcahps');
      needed.add('hai_risk');
      needed.add('er_wait_time');
    }
    if (needed.size === 0) return;

    let cancelled = false;
    const cacheHas = (key: string, min: number) => {
      let count = 0;
      for (const m of cmsMeasureCache.values()) {
        if (m.has(key)) count++;
        if (count >= min) return true;
      }
      return false;
    };
    const fetchMeasure = async (key: string) => {
      const entry = measureMap[key as keyof typeof measureMap];
      if (!entry) return;
      if (cacheHas(key, 1000)) return;
      try {
        const { data, error } = await supabase
          .from('hospital_cms_data')
          .select('hospital_id, payload')
          .eq('dataset', entry.dataset)
          .eq('measure_code', entry.code)
          .limit(10000);
        if (cancelled || error || !Array.isArray(data)) return;
        setCmsMeasureCache((prev) => {
          const next = new Map(prev);
          for (const row of data) {
            const payload = (row as any).payload as Record<string, unknown> | null;
            if (!payload) continue;
            const raw = payload[entry.scoreField];
            const num = typeof raw === 'number' ? raw : Number(raw);
            if (!Number.isFinite(num)) continue;
            const hid = (row as any).hospital_id as string;
            const inner = new Map(next.get(hid) ?? []);
            inner.set(key, num);
            next.set(hid, inner);
          }
          return next;
        });
      } catch { /* silently fall back to unsorted defaults */ }
    };
    // Fire all needed fetches in parallel.
    Promise.all([...needed].map(fetchMeasure));
    return () => { cancelled = true; };
  }, [sortBy, reviewsColumnsExpanded, cmsMeasureCache]);

  // Konami code detection for matrix effect
  useEffect(() => {
    const konamiCode = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let konamiIndex = 0;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;

      if (key === konamiCode[konamiIndex]) {
        konamiIndex++;
        if (konamiIndex === konamiCode.length) {
          setKonamiActivated(true);
          console.log('🎮 Konami Code Activated! Matrix Effect Enabled!');
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Refs for column width matching and virtual scrolling
  const headerTableRef = useRef<HTMLTableElement>(null);
  const dataTableRef = useRef<HTMLTableElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  // Check system settings for live rankings status
  const { data: systemSettings, isLoading: isLoadingSettings, error: settingsError } = useQuery({
    queryKey: ['system-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', ['live_rankings_enabled', 'rankings_maintenance_message']);

      if (error) {
        console.warn('Could not fetch system settings:', error);
        return [];
      }
      return data;
    }
  });

  const isLiveRankingsEnabled = systemSettings?.find(s => s.setting_key === 'live_rankings_enabled')?.setting_value === 'true';
  const maintenanceMessage = systemSettings?.find(s => s.setting_key === 'rankings_maintenance_message')?.setting_value ||
    'Hospital rankings are currently being updated with the latest pricing data. Please check back shortly for the most accurate rankings.';

  // Scenario metadata query (when in scenario mode)
  const { data: scenarioMeta } = useQuery({
    queryKey: ['scenario-meta', scenarioId, liveDb],
    queryFn: async () => {
      if (!scenarioId) return null;

      // Try static scenarios.json first (unless ?db=live)
      if (!liveDb) {
        try {
          const staticScenarios = await loadStaticData<{ data: Array<{ id: string; name: string; description?: string; estimatedCost?: number; estimated_cost?: number; totalProcedures?: number; total_procedures?: number }> }>('scenarios.json');
          const sd = staticScenarios?.data?.find(s => s.id === scenarioId);
          if (sd) {
            const nationalTotal = Math.round(Number(sd.estimatedCost || sd.estimated_cost)) || 0;
            const procedureCount = sd.totalProcedures || sd.total_procedures || 0;
            return { id: sd.id, name: sd.name, description: sd.description, nationalTotal, procedureCount };
          }
        } catch (e) {
          console.warn('[ScenarioMeta] Static fallback failed:', e);
        }
      }

      // Supabase fallback
      const { data: scenario, error } = await supabase
        .from('scenarios')
        .select('id, name, description, estimated_cost, total_procedures')
        .eq('id', scenarioId)
        .single();
      if (error) { console.error('Scenario meta error:', error); return null; }

      const nationalTotal = Number(scenario.estimated_cost) || 0;
      return { ...scenario, nationalTotal, procedureCount: scenario.total_procedures || 0 };
    },
    enabled: !!scenarioId,
  });

  // Fetch all active scenarios for the dropdown selector
  const { data: allScenarios = [] } = useQuery({
    queryKey: ['rankings-scenarios-list'],
    queryFn: async () => {
      // Supabase defaults to 1000 rows — paginate to get all ~11k scenarios
      const PAGE = 1000;
      let all: any[] = [];
      let from = 0;
      while (true) {
        const { data, error } = await supabase
          .from('scenarios')
          .select('id, name, slug, category, estimated_cost, severity_level, scenario_family_id, family_variant_label, total_procedures')
          .eq('is_active', true)
          .eq('hidden_from_catalog', false)
          .order('category')
          .order('name')
          .range(from, from + PAGE - 1);
        if (error) { console.error('Scenarios fetch error:', error); return all; }
        if (!data || data.length === 0) break;
        all = all.concat(data);
        if (data.length < PAGE) break;
        from += PAGE;
      }
      return all;
    },
  });

  const SEVERITY_COLORS = ['#2b5e49', '#5a9a3c', '#C5A55A', '#e67e22', '#B22234'];
  // Labels live in common.json so every page using the cost-quartile vocabulary
  // (HomeBeta family card, ScenarioCard, ScenarioFamilyModal, Scenarios browse,
  // HospitalRankings) reads the same translated strings.
  const SEVERITY_LABELS = [
    t('common:costQuartile.base'),
    t('common:costQuartile.lowCost'),
    t('common:costQuartile.midCost'),
    t('common:costQuartile.highCost'),
    t('common:costQuartile.highest'),
  ];

  // Build family-grouped structure: base scenarios with their variants.
  // Search ranking (Master 2026-05-26): when a query is present we score
  // each match by where it lands — base-name match beats variant match
  // beats category-only match — and propagate the best score up to the
  // family/category level so categories with strong matches surface first.
  // Previously the filter was a flat `name OR category` includes() and the
  // sort was alphabetical, which buried obvious family-name matches under
  // tangential variant hits (e.g. searching "preg" surfaced "Q Fever in
  // Pregnancy" before Vaginal Pregnancy & Delivery).
  const { familyGroups, standaloneByCategory, categoryScores } = useMemo(() => {
    const q = scenarioSearch.trim().toLowerCase();
    const hasQuery = q.length > 0;

    // Match scoring — higher = better. Returns 0 for no match.
    //   1000: exact name match
    //    900: name starts with query
    //    800: query at a word boundary in name (e.g. "knee" in "Total knee replacement")
    //    700: substring anywhere in name
    //    400: substring in family_variant_label (e.g. variants tagged "with pregnancy")
    //    300: substring in category
    // Anything below 300 fails the match check below. Tags include scenario
    // tags + description if present so non-obvious synonyms still resolve.
    const scoreText = (text: string | null | undefined): number => {
      if (!hasQuery || !text) return hasQuery ? 0 : 1;
      const t = text.toLowerCase();
      if (t === q) return 1000;
      if (t.startsWith(q)) return 900;
      // Word-boundary: query appears after a non-word char (or at start)
      const wbRe = new RegExp(`(^|[^a-z0-9])${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
      if (wbRe.test(t)) return 800;
      if (t.includes(q)) return 700;
      return 0;
    };
    const scoreScenario = (s: any): number => {
      if (!hasQuery) return 1;
      const nameScore = scoreText(s.name);
      if (nameScore > 0) return nameScore;
      const variantLabelScore = scoreText(s.family_variant_label);
      if (variantLabelScore > 0) return Math.max(400, Math.floor(variantLabelScore * 0.5));
      const catScore = scoreText(s.category);
      if (catScore > 0) return 300;
      return 0;
    };

    // First pass — score every scenario. Don't filter yet; we need the
    // unmatched bases for families whose variants matched.
    const scoredById = new Map<string, number>();
    allScenarios.forEach((s: any) => { scoredById.set(s.id, scoreScenario(s)); });

    // Which families have ANY matching member? When a variant matches but
    // the base doesn't (common for "preg" → family base "Uncomplicated
    // Vaginal Delivery" doesn't contain the query, but ~10 variants do),
    // we still want the whole family to surface with its real base as the
    // anchor row. Standalone scenarios use the same gate against their own
    // single score.
    const matchedFamilyIds = new Set<string>();
    const standalone: { s: any; score: number }[] = [];
    allScenarios.forEach((s: any) => {
      const sc = scoredById.get(s.id) ?? 0;
      if (hasQuery && sc <= 0) return;
      if (s.scenario_family_id) {
        matchedFamilyIds.add(s.scenario_family_id);
      } else {
        standalone.push({ s, score: sc });
      }
    });

    // Bucket ALL members of each matched family (matched or not) so the
    // base is always present. Score the family by the strongest match
    // across members — base matches and variant matches now both count
    // at full weight, but with one twist: a base-name match adds a +50
    // bonus so ties resolve in the base's favor. That keeps families
    // whose name literally contains the query above families that only
    // matched on a variant.
    const familyMembers = new Map<string, any[]>();
    if (matchedFamilyIds.size > 0) {
      allScenarios.forEach((s: any) => {
        if (s.scenario_family_id && matchedFamilyIds.has(s.scenario_family_id)) {
          const arr = familyMembers.get(s.scenario_family_id) ?? [];
          arr.push(s);
          familyMembers.set(s.scenario_family_id, arr);
        }
      });
    }

    // Build family groups using the actual severity-0 base, not a random
    // variant. Falls back to lowest-severity member if no base exists.
    type FamilyGroup = { base: any; variants: any[]; category: string; score: number };
    const groups: FamilyGroup[] = [];
    familyMembers.forEach((members) => {
      members.sort((a: any, b: any) => (a.severity_level || 0) - (b.severity_level || 0));
      const base = members.find((m: any) => (m.severity_level || 0) === 0) || members[0];
      const variants = members.filter((m: any) => m.id !== base.id);
      const baseScore = (scoredById.get(base.id) ?? 0) + (scoredById.get(base.id) ? 50 : 0);
      const bestVariantScore = variants.reduce((max, v) => Math.max(max, scoredById.get(v.id) ?? 0), 0);
      const score = Math.max(baseScore, bestVariantScore);
      groups.push({ base, variants, category: base.category || 'Other', score });
    });

    // Sort families: score DESC, then category, then name. When no query is
    // active scores all equal 1 so this collapses to the old alpha sort.
    groups.sort((a, b) => (b.score - a.score)
      || (a.category || '').localeCompare(b.category || '')
      || a.base.name.localeCompare(b.base.name));

    // Standalone scenarios: same score-first sort
    standalone.sort((a, b) => (b.score - a.score)
      || (a.s.category || '').localeCompare(b.s.category || '')
      || a.s.name.localeCompare(b.s.name));

    // Group by category, tracking the BEST score per category so the
    // category headers in the UI render in match-relevance order rather
    // than alphabetical when a query is active.
    const catScores = new Map<string, number>();
    const familyGrouped: Record<string, FamilyGroup[]> = {};
    groups.forEach((g) => {
      const cat = g.category || 'Other';
      (familyGrouped[cat] ||= []).push(g);
      catScores.set(cat, Math.max(catScores.get(cat) ?? 0, g.score));
    });
    const standaloneGrouped: Record<string, any[]> = {};
    standalone.forEach((entry) => {
      const cat = entry.s.category || 'Other';
      (standaloneGrouped[cat] ||= []).push(entry.s);
      catScores.set(cat, Math.max(catScores.get(cat) ?? 0, entry.score));
    });

    return {
      familyGroups: familyGrouped,
      standaloneByCategory: standaloneGrouped,
      categoryScores: catScores,
    };
  }, [allScenarios, scenarioSearch]);

  // Combine all categories for rendering — sorted by best-score DESC when a
  // search query is active, alphabetically when no query.
  const allCategories = useMemo(() => {
    const cats = new Set([...Object.keys(familyGroups), ...Object.keys(standaloneByCategory)]);
    const list = [...cats];
    const hasQuery = scenarioSearch.trim().length > 0;
    if (hasQuery) {
      list.sort((a, b) => (categoryScores.get(b) ?? 0) - (categoryScores.get(a) ?? 0) || a.localeCompare(b));
    } else {
      list.sort();
    }
    return list;
  }, [familyGroups, standaloneByCategory, categoryScores, scenarioSearch]);

  const activeScenario = allScenarios.find((s: any) => s.id === scenarioId);

  // Family members for the active scenario (for the severity nav strip)
  const activeFamily = useMemo(() => {
    if (!activeScenario?.scenario_family_id) return [];
    return allScenarios
      .filter((s: any) => s.scenario_family_id === activeScenario.scenario_family_id)
      .sort((a: any, b: any) => (a.severity_level || 0) - (b.severity_level || 0));
  }, [activeScenario, allScenarios]);

  // Scenario detail for dashboard cards (procedures, phases, etc.)
  const { data: scenarioDetail } = useScenarioDetail(isScenarioMode ? scenarioId : undefined);

  // Query to get all hospital rankings with sorting, and filtering (search is client-side)
  const { data: allRankings, isLoading, isFetching, error: rankingsError } = useQuery({
    queryKey: ['hospital-rankings', sortBy, rankByCodeType, filterCodeType, filterCode, scenarioId, scenarioMeta?.nationalTotal, forceLive, liveDb],
    queryFn: async () => {
      if (forceLive) console.log('🔴 LIVE MODE: Bypassing static cache, querying API directly');
      // Scenario mode: try static cache first, fall back to Supabase
      if (scenarioId && scenarioMeta) {
        const nationalTotal = scenarioMeta.nationalTotal || 0;

        // Try static cache (unless ?db=live or ?live=true)
        if (!liveDb && !forceLive) {
          try {
          const loaded = await loadScenarioCoverageFor(scenarioId);
          if (loaded) {
            const rows = getCoverageForScenario(scenarioId);
            console.log(`[Rankings] Scenario coverage: ${rows.length} rows for ${scenarioId}`);
            if (rows.length > 0) {
              // Load rankings.json for hospital metadata (google_rating, cms_rating, etc.)
              const staticRankings = await loadStaticData<{ data: HospitalRanking[] }>('rankings.json');
              const metaMap = new Map<string, HospitalRanking>();
              if (staticRankings?.data) {
                for (const r of staticRankings.data) metaMap.set(r.hospital_id, r);
              }

              console.log(`[Rankings] Using scenario coverage cache (${rows.length} hospitals)`);
              const ranked: HospitalRanking[] = [];
              for (const row of rows) {
                const hospital = getHospitalInfo(row.h);
                const meta = metaMap.get(row.h);
                const blended = blendCoverageRow({ hp: row.hp, np: row.np, mc: row.mc, tc: row.tc, pm: row.pm, nationalCost: nationalTotal });
                if (!blended) continue; // garbage/unpriceable row
                const blendedPrice = blended.blendedPrice;

                ranked.push({
                  hospital_id: row.h,
                  avg_gross_markup_percentage: row.mp,
                  expensive_procedures_count: meta?.expensive_procedures_count || 0,
                  outlier_expensive_count: meta?.outlier_expensive_count || 0,
                  shame_score_calculated: meta?.shame_score_calculated || 0,
                  overall_rank_gross: ranked.length + 1,
                  total_procedures: row.tc,
                  total_available_procedures: row.tc,
                  hospital_name: hospital?.n || meta?.hospital_name || 'Unknown Hospital',
                  hospital_city: hospital?.c || meta?.hospital_city,
                  hospital_state: hospital?.s || meta?.hospital_state,
                  google_rating: meta?.google_rating,
                  google_review_count: meta?.google_review_count,
                  cms_overall_rating: meta?.cms_overall_rating,
                  bed_count: meta?.bed_count,
                  hospital_type: meta?.hospital_type,
                  ownership: meta?.ownership,
                  emergency_services: meta?.emergency_services,
                  scenario_total_price: row.hp,
                  scenario_national_price: row.np,
                  scenario_blended_price: blendedPrice,
                  scenario_coverage_pct: row.cp,
                  scenario_matched_codes: row.mc,
                  scenario_total_codes: row.tc,
                } as HospitalRanking);
              }
              return ranked;
            }
          }
          } catch (staticErr) {
            console.warn('[Rankings] Static scenario cache error, falling back to Supabase:', staticErr);
          }
        }

        // Supabase fallback (or ?db=live / ?live=true)
        const { data: coverageData, error: covError } = await supabase
          .from('scenario_hospital_coverage')
          .select(`
            hospital_id,
            total_hospital_price,
            total_national_price,
            avg_markup_percentage,
            coverage_percentage,
            matched_codes,
            total_codes,
            hospitals(
              name, city, state,
              google_rating, google_review_count,
              cms_overall_rating, bed_count, hospital_type,
              ownership, emergency_services
            )
          `)
          .eq('scenario_id', scenarioId)
          .gte('coverage_percentage', 50)
          .gt('total_hospital_price', 0)
          .order('total_hospital_price', { ascending: true });

        if (covError) {
          console.warn('[Rankings] Scenario Supabase query error:', covError);
          throw covError;
        }
        if (!coverageData) return [];

        const rankedDb: HospitalRanking[] = [];
        for (const row of coverageData as any[]) {
          const h = row.hospitals;
          const blended = blendCoverageRow({
            hp: Number(row.total_hospital_price),
            np: Number(row.total_national_price),
            mc: Number(row.matched_codes),
            tc: Number(row.total_codes),
            nationalCost: nationalTotal,
          });
          if (!blended) continue; // garbage/unpriceable row
          const blendedPrice = blended.blendedPrice;

          rankedDb.push({
            hospital_id: row.hospital_id,
            avg_gross_markup_percentage: Number(row.avg_markup_percentage),
            expensive_procedures_count: 0,
            outlier_expensive_count: 0,
            shame_score_calculated: 0,
            overall_rank_gross: rankedDb.length + 1,
            total_procedures: Number(row.total_codes) || 0,
            total_available_procedures: Number(row.total_codes) || 0,
            hospital_name: h?.name || 'Unknown Hospital',
            hospital_city: h?.city,
            hospital_state: h?.state,
            google_rating: h?.google_rating,
            google_review_count: h?.google_review_count,
            cms_overall_rating: h?.cms_overall_rating,
            bed_count: h?.bed_count,
            hospital_type: h?.hospital_type,
            ownership: h?.ownership,
            emergency_services: h?.emergency_services,
            scenario_total_price: Number(row.total_hospital_price),
            scenario_national_price: Number(row.total_national_price),
            scenario_blended_price: blendedPrice,
            scenario_coverage_pct: Number(row.coverage_percentage),
            scenario_matched_codes: Number(row.matched_codes),
            scenario_total_codes: Number(row.total_codes),
          } as HospitalRanking);
        }
        return rankedDb;
      }
      // Try static JSON first for the standard (unfiltered) view
      if (filterCodeType === 'all' && !filterCode && !forceLive) {
        try {
          const staticData = await loadStaticData<{ generated_at: string; data: HospitalRanking[] }>('rankings.json');
          if (staticData && Array.isArray(staticData.data) && staticData.data.length > 0) {
            console.log(`[Rankings] Using static data (${staticData.data.length} hospitals)`);
            let mergedData = staticData.data;

            // Apply client-side sorting
            if (sortBy === 'shame') {
              mergedData = mergedData.sort((a, b) => {
                const aOutlierPercentage = a.total_procedures > 0 ? (a.expensive_procedures_count / a.total_procedures) * 100 : 0;
                const bOutlierPercentage = b.total_procedures > 0 ? (b.expensive_procedures_count / b.total_procedures) * 100 : 0;
                return bOutlierPercentage - aOutlierPercentage;
              });
            } else if (sortBy === 'google_rating') {
              mergedData = mergedData.sort((a, b) => (b.google_rating ?? -1) - (a.google_rating ?? -1));
            } else if (sortBy === 'cms_rating') {
              mergedData = mergedData.sort((a, b) => (b.cms_overall_rating ?? -1) - (a.cms_overall_rating ?? -1));
            } else if (sortBy === 'mortality') {
              // Worst-first per Master 2026-05-26: this is a Hall-of-Shame
              // surface, so when ranking by mortality we lead with the
              // hospitals with the HIGHEST risk-adjusted death rate. Rows
              // without a reported score sink via -Infinity.
              mergedData = mergedData.sort((a, b) => {
                const aScore = (a as any).mortality_score ?? Number.NEGATIVE_INFINITY;
                const bScore = (b as any).mortality_score ?? Number.NEGATIVE_INFINITY;
                return bScore - aScore;
              });
            } else if (sortBy === 'readmission') {
              // Worst-first: highest readmission rates lead.
              mergedData = mergedData.sort((a, b) => {
                const aScore = (a as any).readmission_score ?? Number.NEGATIVE_INFINITY;
                const bScore = (b as any).readmission_score ?? Number.NEGATIVE_INFINITY;
                return bScore - aScore;
              });
            } else if (sortBy === 'hcahps') {
              // Worst-first: LOWEST "would recommend" % leads (worst patient
              // experience). hcahps_recommend_pct is baked into rankings.json;
              // cmsMeasureCache is the back-compat fallback while CDN catches up.
              mergedData = mergedData.sort((a, b) => {
                const aScore = (a as any).hcahps_recommend_pct ?? cmsMeasureCache.get(a.hospital_id)?.get('hcahps') ?? Number.POSITIVE_INFINITY;
                const bScore = (b as any).hcahps_recommend_pct ?? cmsMeasureCache.get(b.hospital_id)?.get('hcahps') ?? Number.POSITIVE_INFINITY;
                return aScore - bScore;
              });
            } else if (sortBy === 'hai_risk') {
              // Worst-first: highest Standardized Infection Ratio leads.
              mergedData = mergedData.sort((a, b) => {
                const aScore = (a as any).hai_risk_sir ?? cmsMeasureCache.get(a.hospital_id)?.get('hai_risk') ?? Number.NEGATIVE_INFINITY;
                const bScore = (b as any).hai_risk_sir ?? cmsMeasureCache.get(b.hospital_id)?.get('hai_risk') ?? Number.NEGATIVE_INFINITY;
                return bScore - aScore;
              });
            } else if (sortBy === 'er_wait_time') {
              // Worst-first: longest ER wait leads.
              mergedData = mergedData.sort((a, b) => {
                const aScore = (a as any).er_wait_minutes ?? cmsMeasureCache.get(a.hospital_id)?.get('er_wait_time') ?? Number.NEGATIVE_INFINITY;
                const bScore = (b as any).er_wait_minutes ?? cmsMeasureCache.get(b.hospital_id)?.get('er_wait_time') ?? Number.NEGATIVE_INFINITY;
                return bScore - aScore;
              });
            } else if (sortBy === 'bed_count') {
              mergedData = mergedData.sort((a, b) => (b.bed_count ?? -1) - (a.bed_count ?? -1));
            } else if (sortBy === 'exec_comp_per_bed') {
              mergedData = mergedData.sort((a, b) => {
                const aRatio = a.ceo_comp_usd && a.bed_count && a.bed_count > 0 ? a.ceo_comp_usd / a.bed_count : -1;
                const bRatio = b.ceo_comp_usd && b.bed_count && b.bed_count > 0 ? b.ceo_comp_usd / b.bed_count : -1;
                return bRatio - aRatio;
              });
            } else if (sortBy === 'operating_margin') {
              mergedData = mergedData.sort((a, b) => {
                const aMargin = a.operating_margin_pct ?? Number.NEGATIVE_INFINITY;
                const bMargin = b.operating_margin_pct ?? Number.NEGATIVE_INFINITY;
                return bMargin - aMargin;
              });
            } else if (sortBy === 'charity_care') {
              mergedData = mergedData.sort((a, b) => (b.charity_care_usd ?? -1) - (a.charity_care_usd ?? -1));
            } else if (sortBy === 'net_patient_revenue') {
              mergedData = mergedData.sort((a, b) => (b.hcris_net_patient_revenue_usd ?? -1) - (a.hcris_net_patient_revenue_usd ?? -1));
            }
            // Default (markup DESC) is already pre-sorted in the static file

            // Code-type ranking: re-sort by specific code type markup
            if (rankByCodeType !== 'overall' && sortBy === 'markup') {
              const codeTypeKey: Record<string, keyof HospitalRanking> = {
                cpt: 'cpt_avg_markup_percentage',
                hcpcs: 'hcpcs_avg_markup_percentage',
                drg: 'drg_avg_markup_percentage',
                rev: 'rev_avg_markup_percentage',
                ndc: 'ndc_avg_markup_percentage',
                supply: 'supply_avg_markup_percentage',
              };
              const countKey: Record<string, keyof HospitalRanking> = {
                cpt: 'cpt_procedure_count',
                hcpcs: 'hcpcs_procedure_count',
                drg: 'drg_procedure_count',
                rev: 'rev_procedure_count',
                ndc: 'ndc_procedure_count',
                supply: 'supply_procedure_count',
              };
              const key = codeTypeKey[rankByCodeType];
              const cKey = countKey[rankByCodeType];
              if (key) {
                // Filter out hospitals with insufficient data for this code type (<10 mapped codes)
                mergedData = mergedData.filter(h => {
                  const count = cKey ? ((h as any)[cKey] || 0) : 0;
                  const markup = (h as any)[key];
                  return count >= 10 && markup != null && markup !== 0;
                });
                mergedData = mergedData.sort((a, b) => {
                  const aVal = (a as any)[key] ?? -999;
                  const bVal = (b as any)[key] ?? -999;
                  return bVal - aVal;
                });
              }
            }

            return mergedData;
          }
        } catch (e) {
          console.warn('[Rankings] Static data error, falling back to API:', e);
        }
      }
      if (filterCodeType !== 'all' && filterCode) {
        // Filtered query for specific codes
        const codeColumn = filterCodeType === 'cpt' ? 'cpt_code' :
                          filterCodeType === 'drg' ? 'drg_code' : 'revenue_code';

        // Get hospital analytics for specific code
        let analyticsQuery = supabase
          .from('hospital_procedure_analytics')
          .select(`
            hospital_id,
            hospital_gross_charge,
            gross_markup_percentage
          `)
          .eq('procedure_code', filterCode)
          .eq('procedure_type', filterCodeType)
          .not('gross_markup_percentage', 'is', null);

        const { data: analyticsData, error: analyticsError } = await analyticsQuery;
        if (analyticsError) throw analyticsError;
        if (!analyticsData) return [];

        // Calculate averages per hospital for this specific code
        const hospitalStats = analyticsData.reduce((acc, item) => {
          if (!acc[item.hospital_id]) {
            acc[item.hospital_id] = {
              hospital_id: item.hospital_id,
              total_procedures: 0,
              total_markup: 0,
              total_charge: 0
            };
          }
          acc[item.hospital_id].total_procedures += 1;
          acc[item.hospital_id].total_markup += item.gross_markup_percentage || 0;
          acc[item.hospital_id].total_charge += item.hospital_gross_charge || 0;
          return acc;
        }, {} as Record<string, any>);

        const hospitalRankings = Object.values(hospitalStats).map((stats: any) => ({
          hospital_id: stats.hospital_id,
          avg_gross_markup_percentage: stats.total_markup / stats.total_procedures,
          total_procedures: stats.total_procedures,
          avg_charge: stats.total_charge / stats.total_procedures,
          shame_score_calculated: stats.total_markup / stats.total_procedures, // Simplified for code filtering
          expensive_procedures_count: 0,
          outlier_expensive_count: 0,
          overall_rank_gross: 0
        }));

        // Sort by selected criteria
        const sortedRankings = hospitalRankings.sort((a, b) => {
          if (sortBy === 'shame') {
            const aOutlierPercentage = a.total_procedures > 0 ? (a.expensive_procedures_count / a.total_procedures) * 100 : 0;
            const bOutlierPercentage = b.total_procedures > 0 ? (b.expensive_procedures_count / b.total_procedures) * 100 : 0;
            return bOutlierPercentage - aOutlierPercentage;
          }
          // Sort by database ranking order instead of recalculating
          return (a.overall_rank_gross || 999999) - (b.overall_rank_gross || 999999);
        });

        // Ranks are already set from database - don't override them

        // Remove pagination - use all rankings
        // const from = (currentPage - 1) * pageSize;
        // const to = from + pageSize;
        // const paginatedRankings = sortedRankings.slice(from, to);

        // For filtered queries, we need to batch the hospital lookups to avoid URI too long
        const hospitalIds = sortedRankings.map(r => r.hospital_id);
        const batchSize = 50; // Process in batches of 50
        let hospitalsData: any[] = [];

        for (let i = 0; i < hospitalIds.length; i += batchSize) {
          const batch = hospitalIds.slice(i, i + batchSize);
          const { data: batchData, error: batchError } = await supabase
            .from('hospitals')
            .select('id, name, city, state, cpt_count, drg_count, revenue_count')
            .in('id', batch)
            .eq('exclude_from_rankings', false);

          if (batchError) throw batchError;
          if (batchData) hospitalsData.push(...batchData);
        }

        return sortedRankings.map(ranking => {
          const hospital = hospitalsData?.find(h => h.id === ranking.hospital_id);
          const cptCount = hospital?.cpt_count || 0;
          const drgCount = hospital?.drg_count || 0;
          const revenueCount = hospital?.revenue_count || 0;
          const supplyCount = 0; // Will need to add supply count to hospitals table later

          return {
            ...ranking,
            total_procedures: ranking.total_procedures, // Keep original total from analytics
            hospital_name: ranking.hospital_name || 'Unknown Hospital',
            hospital_city: ranking.city,
            hospital_state: ranking.state,
            cpt_count: cptCount,
            drg_count: drgCount,
            revenue_count: revenueCount,
            ndc_count: supplyCount,
            // Keep database procedure counts (these are the correct ones)
            cpt_procedure_count: ranking.cpt_procedure_count,
            drg_procedure_count: ranking.drg_procedure_count,
            rev_procedure_count: ranking.rev_procedure_count,
            ndc_procedure_count: ranking.ndc_procedure_count,
          };
        }) as HospitalRanking[];
      } else {
        // Standard query from hospital_ranking_summary with denormalized hospital fields + quality join
        const rankingColumns = `
            hospital_id,
            hospital_name,
            city,
            state,
            avg_gross_markup_percentage,
            median_gross_markup_percentage,
            expensive_procedures_count,
            expensive_procedures_percentage,
            outlier_expensive_count,
            outlier_percentage,
            shame_score_calculated,
            overall_rank_gross,
            total_procedures,
            total_available_procedures,
            uncoded_procedures,
            cpt_procedure_count,
            drg_procedure_count,
            rev_procedure_count,
            ndc_procedure_count,
            hcpcs_procedure_count,
            supply_procedure_count,
            drug_procedure_count,
            cpt_total_available,
            drg_total_available,
            rev_total_available,
            ndc_total_available,
            hcpcs_total_available,
            supply_total_available,
            drug_total_available,
            cpt_avg_markup_percentage,
            drg_avg_markup_percentage,
            rev_avg_markup_percentage,
            ndc_avg_markup_percentage,
            hcpcs_avg_markup_percentage,
            supply_avg_markup_percentage,
            drug_avg_markup_percentage`;

        // Try with hospitals join first, fall back without if FK relationship is missing
        const buildQuery = (withJoin: boolean) => {
          const selectStr = withJoin
            ? `${rankingColumns},
            hospitals(
              google_rating,
              google_review_count,
              cms_overall_rating,
              bed_count,
              hospital_type,
              ownership,
              emergency_services,
              hospital_system,
              year_founded,
              annual_revenue_usd,
              revenue_year,
              revenue_source
            )`
            : rankingColumns;

          let q = supabase
            .from('hospital_ranking_summary')
            .select(selectStr)
            .not('avg_gross_markup_percentage', 'is', null)
            .eq('exclude_from_rankings', false);

          if (sortBy === 'shame') {
            q = q.order('outlier_expensive_count', { ascending: false })
                 .order('total_procedures', { ascending: true });
          } else {
            // Match the canonical rank order (median-baseline markup); avg as
            // tiebreak so hospitals missing a median don't float randomly.
            q = q.order('median_gross_markup_percentage', { ascending: false, nullsFirst: false })
                 .order('avg_gross_markup_percentage', { ascending: false });
          }
          return q;
        };

        let { data: rankingsData, error: rankingsError } = await buildQuery(true);

        // If join fails (missing FK), retry without the hospitals join
        if (rankingsError?.code === 'PGRST200') {
          console.warn('[Rankings] hospitals join failed (missing FK), retrying without quality data');
          ({ data: rankingsData, error: rankingsError } = await buildQuery(false));
        }

        if (rankingsError) throw rankingsError;
        if (!rankingsData) return [];

        // Map the joined data (search filtering is now client-side)
        let mergedData = rankingsData.map(ranking => {
          const hospital = ranking.hospitals;
          // Use counts from ranking summary data, not hospitals table
          const cptCount = ranking.cpt_procedure_count || 0;
          const drgCount = ranking.drg_procedure_count || 0;
          const revenueCount = ranking.rev_procedure_count || 0;
          const ndcCount = ranking.ndc_procedure_count || 0;
          const hcpcsCount = ranking.hcpcs_procedure_count || 0;
          const supplyCount = ranking.supply_procedure_count || 0;
          const drugCount = ranking.drug_procedure_count || 0;

          return {
            hospital_id: ranking.hospital_id,
            avg_gross_markup_percentage: ranking.avg_gross_markup_percentage,
            median_gross_markup_percentage: ranking.median_gross_markup_percentage,
            expensive_procedures_count: ranking.expensive_procedures_count,
            expensive_procedures_percentage: ranking.expensive_procedures_percentage,
            outlier_expensive_count: ranking.outlier_expensive_count,
            outlier_percentage: ranking.outlier_percentage,
            shame_score_calculated: ranking.shame_score_calculated,
            overall_rank_gross: ranking.overall_rank_gross,
            total_procedures: ranking.total_procedures, // Use actual total from ranking summary
            total_available_procedures: ranking.total_available_procedures,
            uncoded_procedures: ranking.uncoded_procedures,
            hospital_name: ranking.hospital_name || 'Unknown Hospital',
            hospital_city: ranking.city,
            hospital_state: ranking.state,
            cpt_count: cptCount,
            drg_count: drgCount,
            revenue_count: revenueCount,
            ndc_count: ndcCount,
            // Keep database procedure counts (these are the correct ones)
            cpt_procedure_count: ranking.cpt_procedure_count,
            drg_procedure_count: ranking.drg_procedure_count,
            rev_procedure_count: ranking.rev_procedure_count,
            ndc_procedure_count: ranking.ndc_procedure_count,
            hcpcs_procedure_count: ranking.hcpcs_procedure_count,
            supply_procedure_count: ranking.supply_procedure_count,
            drug_procedure_count: ranking.drug_procedure_count,
            cpt_total_available: ranking.cpt_total_available,
            drg_total_available: ranking.drg_total_available,
            rev_total_available: ranking.rev_total_available,
            ndc_total_available: ranking.ndc_total_available,
            hcpcs_total_available: ranking.hcpcs_total_available,
            supply_total_available: ranking.supply_total_available,
            drug_total_available: ranking.drug_total_available,
            cpt_avg_markup_percentage: ranking.cpt_avg_markup_percentage,
            drg_avg_markup_percentage: ranking.drg_avg_markup_percentage,
            rev_avg_markup_percentage: ranking.rev_avg_markup_percentage,
            ndc_avg_markup_percentage: ranking.drug_avg_markup_percentage, // Use drug_avg_markup_percentage (same as NDC)
            hcpcs_avg_markup_percentage: ranking.hcpcs_avg_markup_percentage,
            supply_avg_markup_percentage: ranking.supply_avg_markup_percentage,
            drug_avg_markup_percentage: ranking.drug_avg_markup_percentage,
            // Reviews + profile metadata from hospitals join
            google_rating: hospital?.google_rating,
            google_review_count: hospital?.google_review_count,
            cms_overall_rating: hospital?.cms_overall_rating,
            bed_count: hospital?.bed_count,
            hospital_type: hospital?.hospital_type,
            ownership: hospital?.ownership,
            emergency_services: hospital?.emergency_services,
            hospital_system: hospital?.hospital_system,
            year_founded: hospital?.year_founded,
            annual_revenue_usd: hospital?.annual_revenue_usd,
            revenue_year: hospital?.revenue_year,
            revenue_source: hospital?.revenue_source,
          };
        });

        // Apply client-side sorting for non-markup sorts
        if (sortBy === 'shame') {
          mergedData = mergedData.sort((a, b) => {
            const aOutlierPercentage = a.total_procedures > 0 ? (a.expensive_procedures_count / a.total_procedures) * 100 : 0;
            const bOutlierPercentage = b.total_procedures > 0 ? (b.expensive_procedures_count / b.total_procedures) * 100 : 0;
            return bOutlierPercentage - aOutlierPercentage;
          });
        } else if (sortBy === 'google_rating') {
          mergedData = mergedData.sort((a, b) => {
            const aVal = a.google_rating ?? -1;
            const bVal = b.google_rating ?? -1;
            return bVal - aVal; // DESC, nulls last
          });
        } else if (sortBy === 'cms_rating') {
          mergedData = mergedData.sort((a, b) => {
            const aVal = a.cms_overall_rating ?? -1;
            const bVal = b.cms_overall_rating ?? -1;
            return bVal - aVal;
          });
        } else if (sortBy === 'bed_count') {
          mergedData = mergedData.sort((a, b) => {
            const aVal = a.bed_count ?? -1;
            const bVal = b.bed_count ?? -1;
            return bVal - aVal;
          });
        } else if (sortBy === 'exec_comp_per_bed') {
          // Per the executive-comp sort mode (URL: /hospital-rankings/executive-comp/).
          // Hospitals without 990 exec data OR without bed_count fall to the bottom (-1 sort key)
          // so the top ranks reflect actual computable values, not nulls.
          mergedData = mergedData.sort((a, b) => {
            const aRatio = a.ceo_comp_usd && a.bed_count && a.bed_count > 0 ? a.ceo_comp_usd / a.bed_count : -1;
            const bRatio = b.ceo_comp_usd && b.bed_count && b.bed_count > 0 ? b.ceo_comp_usd / b.bed_count : -1;
            return bRatio - aRatio;
          });
        }

        return mergedData as HospitalRanking[];
      }
    },
    placeholderData: keepPreviousData,
    retry: isScenarioMode ? 2 : 1,
  });

  // Back to top button scroll detection and scroll progress tracking
  useEffect(() => {
    // Don't try to attach listeners while data is still loading
    if (isLoading) {
      console.log('⏳ Still loading data, waiting...');
      return;
    }

    // rAF-throttle: collapse a burst of scroll events into one frame's worth of
    // work so a fast mouse-wheel doesn't trigger N React re-renders per second.
    // Thumb position + percent label are written DIRECTLY to the DOM via refs to
    // avoid re-rendering the 6k-line HospitalRankings component on every tick.
    let winScrollRaf = 0;
    let lastShowBackToTop: boolean | null = null;
    const handleWindowScroll = () => {
      if (winScrollRaf) return;
      winScrollRaf = requestAnimationFrame(() => {
        winScrollRaf = 0;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const next = scrollTop > 500;
        if (next !== lastShowBackToTop) {
          lastShowBackToTop = next;
          setShowBackToTop(next);
        }
      });
    };

    let tableScrollRaf = 0;
    let lastProgress = -1;
    let lastThumb = -1;
    let lastHintHidden: boolean | null = null;
    const handleTableScroll = () => {
      if (tableScrollRaf) return;
      tableScrollRaf = requestAnimationFrame(() => {
        tableScrollRaf = 0;
        const tableContainer = document.querySelector('.hospital-rankings-scroll') as HTMLElement | null;
        if (!tableContainer) return;

        const scrollTop = tableContainer.scrollTop;
        const scrollHeight = tableContainer.scrollHeight - tableContainer.clientHeight;
        const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
        const roundedProgress = Math.round(progress);

        const scrollbarTrack = document.querySelector('.scrollbar-track') as HTMLElement | null;
        let calculatedThumbHeight = lastThumb;
        if (scrollbarTrack) {
          const trackHeight = scrollbarTrack.getBoundingClientRect().height;
          const visibleRatio = tableContainer.clientHeight / tableContainer.scrollHeight;
          calculatedThumbHeight = Math.round(Math.max(40, Math.min(trackHeight * 0.8, trackHeight * visibleRatio)));
        }

        // Direct DOM writes — no React re-render.
        const thumb = scrollbarThumbRef.current;
        if (thumb) {
          const useThumb = calculatedThumbHeight > 0 ? calculatedThumbHeight : 40;
          thumb.style.top = `calc(${roundedProgress}% - ${(roundedProgress / 100) * useThumb}px)`;
          if (calculatedThumbHeight !== lastThumb && calculatedThumbHeight > 0) {
            thumb.style.height = `${calculatedThumbHeight}px`;
          }
        }
        const pct = scrollbarPercentRef.current;
        if (pct && roundedProgress !== lastProgress) {
          pct.textContent = `${roundedProgress}%`;
        }
        lastProgress = roundedProgress;
        if (calculatedThumbHeight > 0) lastThumb = calculatedThumbHeight;

        // Hide horizontal scroll hint — direct DOM, only when the hidden-state flips
        const hint = tableContainer.parentElement?.querySelector('.scroll-hint-fade') as HTMLElement | null;
        if (hint) {
          const shouldHide = tableContainer.scrollLeft > 20;
          if (shouldHide !== lastHintHidden) {
            lastHintHidden = shouldHide;
            hint.style.transition = 'opacity 0.3s';
            hint.style.opacity = shouldHide ? '0' : '1';
          }
        }
      });
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    // Multiple retry attempts to ensure DOM is ready
    const attachScrollListener = () => {
      const tableContainer = document.querySelector('.hospital-rankings-scroll') as HTMLElement;
      if (tableContainer) {
        console.log('✅ Found table container, attaching scroll listener', {
          scrollHeight: tableContainer.scrollHeight,
          clientHeight: tableContainer.clientHeight
        });
        tableContainer.addEventListener('scroll', handleTableScroll, { passive: true });
        handleTableScroll(); // Initial calculation
        return true;
      }
      console.warn('❌ Table container not found, will retry...');
      return false;
    };

    // Try immediately
    if (!attachScrollListener()) {
      // Retry after 500ms (give React more time to render)
      const timer1 = setTimeout(attachScrollListener, 500);
      // Retry after 1000ms
      const timer2 = setTimeout(attachScrollListener, 1000);
      // Retry after 2000ms
      const timer3 = setTimeout(attachScrollListener, 2000);

      return () => {
        window.removeEventListener('scroll', handleWindowScroll);
        const tableContainer = document.querySelector('.hospital-rankings-scroll') as HTMLElement;
        if (tableContainer) {
          tableContainer.removeEventListener('scroll', handleTableScroll);
        }
        if (winScrollRaf) cancelAnimationFrame(winScrollRaf);
        if (tableScrollRaf) cancelAnimationFrame(tableScrollRaf);
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }

    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
      const tableContainer = document.querySelector('.hospital-rankings-scroll') as HTMLElement;
      if (tableContainer) {
        tableContainer.removeEventListener('scroll', handleTableScroll);
      }
      if (winScrollRaf) cancelAnimationFrame(winScrollRaf);
      if (tableScrollRaf) cancelAnimationFrame(tableScrollRaf);
    };
  }, [isLoading]); // Re-run when loading state changes

  // Query to get total count for pagination (search filtering is client-side)
  const { data: totalCount = 0 } = useQuery({
    queryKey: ['hospital-rankings-count', filterCodeType, filterCode],
    queryFn: async () => {
      if (filterCodeType !== 'all' && filterCode) {
        // Count for filtered results - need to filter out excluded hospitals
        const { data: analyticsData } = await supabase
          .from('hospital_procedure_analytics')
          .select('hospital_id')
          .eq('procedure_code', filterCode)
          .eq('procedure_type', filterCodeType)
          .not('gross_markup_percentage', 'is', null);

        if (!analyticsData) return 0;

        // Get unique hospital IDs from analytics
        const uniqueHospitalIds = [...new Set(analyticsData.map(d => d.hospital_id))];

        // Filter out excluded hospitals
        const { data: validHospitals } = await supabase
          .from('hospitals')
          .select('id')
          .in('id', uniqueHospitalIds)
          .eq('exclude_from_rankings', false);

        return validHospitals?.length || 0;
      } else {
        // Standard count (search filtering is now client-side)
        const { count, error } = await supabase
          .from('hospital_ranking_summary')
          .select('*', { count: 'exact', head: true })
          .not('avg_gross_markup_percentage', 'is', null)
          .eq('exclude_from_rankings', false);
        if (error) throw error;
        return count || 0;
      }
    }
  });

  // DB FALLBACK ENRICHMENT — two-phase fetch so the top of the list shows real numbers fast:
  //   1. Priority: top 25 hospital_ids via small IN clause (returns in ~200ms)
  //   2. Bulk: paged scan of all hospitals + HCRIS finances (~3-5s)
  // PHASE 1 — REMOVED (2026-05-26). This used to fire 4 supabase queries in
  // parallel for the top 25 hospitals (hospitals + hospital_finances +
  // hospital_990_executives + hospital_quality_measures) to "top off"
  // rankings.json with fresh values. Every field it pulled is now baked
  // into rankings.json by generate_static_cache.py's generate_rankings(),
  // so the top-off was redundant and producing the "shuffle after load"
  // effect on the top 25 cells. Const-null preserves the merge-shape in
  // the rankings useMemo without a network round-trip.
  const priorityEnrichment: Map<string, Partial<HospitalRanking>> | null = null;

  // PHASE 2 — REMOVED (2026-05-26 earlier in the day). Was a bulk paged
  // fetch over every hospital + every 990 exec row + every HCRIS row to
  // enrich the rankings table. All the fields it pulled (annual_revenue_usd,
  // operating_margin_pct, charity_care_usd, ceo_comp_usd,
  // medicare_total_payments_usd, hcris_net_patient_revenue_usd,
  // finance_fiscal_year, mortality_score, readmission_score, npi/ccn/ein)
  // are now baked into rankings.json by generate_static_cache.py's
  // generate_rankings() — same joins, same source tables. Reading from the
  // static cache avoids the multi-second DB round-trip that previously
  // caused the financial column to render slot-machine placeholders and
  // then shrink-pop when the bulk fetch finished, jumping every row in the
  // viewport. priorityEnrichment was also dropped later the same day after
  // confirming every field it fetched is now in rankings.json too — the
  // top-25 cells were briefly "shuffling" on load while the priority fetch
  // landed and overwrote the cache values with identical fresh ones.
  const bulkEnrichment: Map<string, Partial<HospitalRanking>> | null = null;

  // Apply state filtering and scenario sorting (also merges in DB-fallback enrichment when available)
  const rankings = useMemo(() => {
    if (!allRankings) return allRankings;

    // Merge enrichment from DB fallback. Only fields missing on the static row get filled — preserves
    // any future cache-baked values that arrive in rankings.json.
    // Phase ordering: priority for top 25 takes precedence (arrives first); bulk is the long-tail backstop.
    const merged: Map<string, Partial<HospitalRanking>> | null =
      (priorityEnrichment || bulkEnrichment)
        ? (() => {
            const m = new Map<string, Partial<HospitalRanking>>();
            if (bulkEnrichment) for (const [k, v] of bulkEnrichment) m.set(k, v);
            if (priorityEnrichment) for (const [k, v] of priorityEnrichment) m.set(k, v);
            return m;
          })()
        : null;
    let result: HospitalRanking[] = merged
      ? allRankings.map(r => {
          const e = merged.get(r.hospital_id);
          if (!e) return r;
          return {
            ...r,
            hospital_system: r.hospital_system ?? e.hospital_system,
            annual_revenue_usd: r.annual_revenue_usd ?? e.annual_revenue_usd,
            revenue_year: r.revenue_year ?? e.revenue_year,
            revenue_source: r.revenue_source ?? e.revenue_source,
            npi_number: r.npi_number ?? e.npi_number,
            ccn: r.ccn ?? e.ccn,
            ein: r.ein ?? e.ein,
            cms_mortality_rating: r.cms_mortality_rating ?? e.cms_mortality_rating,
            cms_safety_rating: r.cms_safety_rating ?? e.cms_safety_rating,
            cms_readmission_rating: r.cms_readmission_rating ?? e.cms_readmission_rating,
            mortality_score: r.mortality_score ?? e.mortality_score,
            mortality_denom: r.mortality_denom ?? e.mortality_denom,
            readmission_score: r.readmission_score ?? e.readmission_score,
            readmission_denom: r.readmission_denom ?? e.readmission_denom,
            finance_fiscal_year: r.finance_fiscal_year ?? e.finance_fiscal_year,
            hcris_net_patient_revenue_usd: r.hcris_net_patient_revenue_usd ?? e.hcris_net_patient_revenue_usd,
            medicare_total_payments_usd: r.medicare_total_payments_usd ?? e.medicare_total_payments_usd,
            operating_margin_pct: r.operating_margin_pct ?? e.operating_margin_pct,
            charity_care_usd: r.charity_care_usd ?? e.charity_care_usd,
            ceo_comp_usd: r.ceo_comp_usd ?? e.ceo_comp_usd,
          };
        })
      : allRankings;

    // Filter by selected state
    if (selectedState !== 'USA') {
      result = result.filter(hospital =>
        hospital.hospital_state?.toUpperCase() === selectedState
      );
    }

    // Sort by scenario blended price when in scenario mode
    if (sortBy === 'scenario_price') {
      result = [...result].sort((a, b) => {
        // Treat NaN/null/undefined as Infinity so garbage never sorts first
        const av = Number.isFinite(a.scenario_blended_price as number) ? (a.scenario_blended_price as number) : Infinity;
        const bv = Number.isFinite(b.scenario_blended_price as number) ? (b.scenario_blended_price as number) : Infinity;
        return av === bv ? 0 : av - bv;
      });
    }

    // ── Quality-measure dense rankings ──────────────────────────────────
    // For each Hall-of-Shame quality lens (worst-first), compute a #N rank
    // per hospital so the cells can show "#42" alongside the value. Dense
    // rank: ties share the same number, the next distinct value gets the
    // next consecutive integer (not skipping). "Worst" direction depends
    // on the metric — higher = worse for mortality/readmission/HAI/ER
    // wait; lower = worse for HCAHPS recommend %. Skipping a TODO: bake
    // these ranks into rankings.json during the static cache rebuild so
    // we don't recompute on every render. ~5k-row pass is fast enough
    // for now but worth caching once we touch the generator.
    type R = HospitalRanking & { _quality_ranks?: Record<string, number> };
    const denseRank = (
      rows: HospitalRanking[],
      getScore: (h: HospitalRanking) => number | null | undefined,
      direction: 'desc' | 'asc', // desc = higher worse, asc = lower worse
    ): Map<string, number> => {
      const ranks = new Map<string, number>();
      const scored: { id: string; score: number }[] = [];
      for (const h of rows) {
        const s = getScore(h);
        if (s == null || !Number.isFinite(s)) continue;
        scored.push({ id: h.hospital_id, score: Number(s) });
      }
      scored.sort((a, b) => direction === 'desc' ? b.score - a.score : a.score - b.score);
      let rank = 0;
      let prev: number | null = null;
      for (const e of scored) {
        if (prev === null || e.score !== prev) {
          rank += 1;
          prev = e.score;
        }
        ranks.set(e.id, rank);
      }
      return ranks;
    };
    const mortalityRanks   = denseRank(result, (h: any) => h.mortality_score, 'desc');
    const readmissionRanks = denseRank(result, (h: any) => h.readmission_score, 'desc');
    const hcahpsRanks      = denseRank(result, (h: any) => h.hcahps_recommend_pct ?? cmsMeasureCache.get(h.hospital_id)?.get('hcahps'), 'asc');
    const haiRanks         = denseRank(result, (h: any) => h.hai_risk_sir ?? cmsMeasureCache.get(h.hospital_id)?.get('hai_risk'), 'desc');
    const erWaitRanks      = denseRank(result, (h: any) => h.er_wait_minutes ?? cmsMeasureCache.get(h.hospital_id)?.get('er_wait_time'), 'desc');
    result = result.map((h) => {
      const r: Record<string, number> = {};
      const m = mortalityRanks.get(h.hospital_id);   if (m) r.mortality = m;
      const rd = readmissionRanks.get(h.hospital_id); if (rd) r.readmission = rd;
      const hp = hcahpsRanks.get(h.hospital_id);      if (hp) r.hcahps = hp;
      const ha = haiRanks.get(h.hospital_id);         if (ha) r.hai_risk = ha;
      const er = erWaitRanks.get(h.hospital_id);      if (er) r.er_wait_time = er;
      return { ...h, _quality_ranks: r } as R;
    });

    return result;
  }, [allRankings, priorityEnrichment, bulkEnrichment, selectedState, sortBy, cmsMeasureCache]);

  // Set of hospital_ids whose financial/quality data is considered "resolved".
  //
  // Previously this was built from the priority + bulk enrichment query maps,
  // so until bulkEnrichment finished paging through ~5K hospitals + ~10K execs
  // + HCRIS rows (which took multiple seconds), every cell outside the top 25
  // rendered a slot-machine RevenueTicker. Tickers are taller than the static
  // `$X.XB` text — when the value arrived the cell shrank, cascading the row
  // height contraction down the visible table. The "crazy render thing".
  //
  // The fix: trust rankings.json. Every hospital in allRankings is treated as
  // resolved the moment the static JSON lands. Null financial fields just
  // mean "no data available for that hospital", same outcome the bulk fetch
  // would have produced. The bulk DB query is now redundant — the static
  // cache (generate_static_cache.py → generate_rankings()) already pulls the
  // same fields from the same source tables at build time. priorityEnrichment
  // stays as a fresh-top-off for the top 25 (cheap, ~200ms, ~1KB URL).
  const resolvedHospitalIds = useMemo(() => {
    const s = new Set<string>();
    if (allRankings) for (const h of allRankings) s.add(h.hospital_id);
    if (priorityEnrichment) for (const k of priorityEnrichment.keys()) s.add(k);
    return s;
  }, [allRankings, priorityEnrichment]);

  // Auto-open drilldown modal from URL deep link
  useEffect(() => {
    if (breakdownHospitalId && breakdownType && rankings?.length) {
      const hospital = rankings.find((h: any) => h.hospital_id === breakdownHospitalId);
      if (hospital) {
        const typeMap: Record<string, number | null | undefined> = {
          cpt: hospital.cpt_avg_markup_percentage,
          drg: hospital.drg_avg_markup_percentage,
          hcpcs: hospital.hcpcs_avg_markup_percentage,
          revenue: hospital.rev_avg_markup_percentage,
          ndc: hospital.ndc_avg_markup_percentage || hospital.drug_avg_markup_percentage,
        };
        setDrilldownModal({
          isOpen: true,
          hospitalId: breakdownHospitalId,
          hospitalName: hospital.hospital_name || 'Hospital',
          procedureType: breakdownType,
          avgMarkup: typeMap[breakdownType] ?? 0,
        });
      }
    }
  }, [breakdownHospitalId, breakdownType, rankings]);

  // Effect to sync column widths between header and data table
  useEffect(() => {
    const syncColumnWidths = () => {
      if (!headerTableRef.current || !dataTableRef.current) return;

      const headerCells = headerTableRef.current.querySelectorAll('th');
      const dataRows = dataTableRef.current.querySelectorAll('tbody tr');

      if (dataRows.length === 0) return;

      // Get the first data row to measure column widths
      const firstDataRow = dataRows[0] as HTMLTableRowElement;
      const dataCells = firstDataRow.querySelectorAll('td');

      // Preserve any existing padding on first row
      const existingPaddingTop = window.getComputedStyle(firstDataRow).paddingTop;

      // Reset any previous width constraints
      headerCells.forEach((headerCell) => {
        headerCell.style.width = '';
        headerCell.style.minWidth = '';
        headerCell.style.maxWidth = '';
      });

      // Force reflow
      headerTableRef.current.offsetHeight;

      // Apply widths from data table to header with padding compensation
      headerCells.forEach((headerCell, index) => {
        if (index < dataCells.length) {
          const dataCell = dataCells[index] as HTMLTableCellElement;
          const dataRect = dataCell.getBoundingClientRect();

          // Use the data cell width but account for any padding differences
          const targetWidth = dataRect.width;

          headerCell.style.width = `${targetWidth}px`;
          headerCell.style.minWidth = `${targetWidth}px`;
          headerCell.style.maxWidth = `${targetWidth}px`;
          headerCell.style.boxSizing = 'border-box';
        }
      });

      // Restore padding on first row if it was lost
      if (firstDataRow && existingPaddingTop) {
        firstDataRow.style.paddingTop = existingPaddingTop;
      }
    };

    // Initial sync with multiple attempts to ensure proper alignment
    const timer1 = setTimeout(syncColumnWidths, 100);
    const timer2 = setTimeout(syncColumnWidths, 300);
    const timer3 = setTimeout(syncColumnWidths, 500);

    // Sync on window resize
    const handleResize = () => {
      setTimeout(syncColumnWidths, 100);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('resize', handleResize);
    };
  }, [rankings, sortBy, filterCodeType, filterCode, codeColumnsExpanded, reviewsColumnsExpanded, profileColumnsExpanded, isScenarioMode]); // Re-sync when data or column visibility changes

  // Filtered hospital suggestions for autocomplete with full data
  // When state-filtered, in-state results appear first, out-of-state below a separator
  const filteredSuggestions = useMemo(() => {
    if (!debouncedSearchTerm.trim() || !allRankings) return [];
    const searchLower = debouncedSearchTerm.toLowerCase();
    const matches = allRankings
      .filter(h => h.hospital_name?.toLowerCase().includes(searchLower))
      .map(r => ({
        id: r.hospital_id,
        name: r.hospital_name,
        city: r.hospital_city,
        state: r.hospital_state,
        total_procedures: r.total_procedures,
        total_available_procedures: r.total_available_procedures || r.total_procedures,
        avg_markup: r.avg_gross_markup_percentage,
        outlier_percentage: r.total_procedures > 0
          ? ((r.outlier_expensive_count || r.expensive_procedures_count || 0) / r.total_procedures) * 100
          : 0,
        isOutOfState: isStateFiltered && r.hospital_state?.toUpperCase() !== selectedState,
      }));
    // Sort: in-state first, then out-of-state
    if (isStateFiltered) {
      matches.sort((a, b) => (a.isOutOfState === b.isOutOfState ? 0 : a.isOutOfState ? 1 : -1));
    }
    return matches.slice(0, 12);
  }, [allRankings, debouncedSearchTerm, isStateFiltered, selectedState]);

  // Handle hospital selection from autocomplete — scrolls table to the selected hospital
  const handleHospitalSelect = (hospitalName: string, hospitalId: string) => {
    // Close dropdown and update search text first, but DON'T set selectedHospitalId yet
    // (it triggers renderTableRow memo recomputation which can reset scroll position)
    setSearchTerm(hospitalName);
    setSearchOpen(false);

    // Check if hospital is outside the currently filtered state
    const hospital = allRankings?.find(h => h.hospital_id === hospitalId);
    if (isStateFiltered && hospital && hospital.hospital_state?.toUpperCase() !== selectedState) {
      const hospitalState = hospital.hospital_state?.toUpperCase() || '';
      toast({
        title: `Switching to all states`,
        description: `${hospitalName} is in ${hospitalState}, removing ${selectedState} filter`,
        duration: 4000,
      });
      // Navigate to USA view, then scroll to hospital after rankings update
      navigate('/hospital-rankings/usa', { replace: true });
      // Wait for rankings to re-render with all states, then scroll
      setTimeout(() => {
        const newRankings = allRankings;
        if (!newRankings) return;
        const idx = newRankings.findIndex(h => h.hospital_id === hospitalId);
        if (idx === -1) return;
        const totalSize = rowVirtualizer.getTotalSize();
        const avgRowSize = totalSize / (newRankings.length || 1);
        setTimeout(() => {
          const container = parentRef.current;
          if (!container) return;
          const header = container.querySelector('.sticky');
          const headerHeight = header ? header.getBoundingClientRect().height : 108;
          const targetRowTop = headerHeight + idx * avgRowSize;
          const centerOffset = (container.clientHeight - avgRowSize) / 2;
          container.scrollTop = Math.max(0, targetRowTop - centerOffset);
          container.dispatchEvent(new Event('scroll', { bubbles: true }));
          setTimeout(() => { setSelectedHospitalId(hospitalId); }, 100);
        }, 200);
      }, 300);
      return;
    }

    if (!rankings) return;
    const targetIndex = rankings.findIndex(h => h.hospital_id === hospitalId);
    if (targetIndex === -1) return;

    // Use the virtualizer's total size to calculate the correct position.
    // The virtualizer tracks sizes internally (measured + estimated).
    const totalSize = rowVirtualizer.getTotalSize();
    const avgRowSize = totalSize / (rankings?.length || 1);

    // Step 1: Scroll to the approximate position using the virtualizer's own metrics
    setTimeout(() => {
      const container = parentRef.current;
      if (!container) return;

      // Get header height (sticky header takes space in document flow)
      const header = container.querySelector('.sticky');
      const headerHeight = header ? header.getBoundingClientRect().height : 108;

      // Calculate target scroll using the virtualizer's average row size
      const targetRowTop = headerHeight + targetIndex * avgRowSize;
      const centerOffset = (container.clientHeight - avgRowSize) / 2;
      const targetScrollTop = Math.max(0, targetRowTop - centerOffset);

      container.scrollTop = targetScrollTop;
      container.dispatchEvent(new Event('scroll', { bubbles: true }));

      // Step 2: After the virtualizer renders the new viewport, set selectedHospitalId
      // and find + highlight the actual row
      setTimeout(() => {
        setSelectedHospitalId(hospitalId);

        // Ensure the table container is visible in the page
        const rect = container.getBoundingClientRect();
        if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // Step 3: Find and highlight the target row
        const highlightRow = (attempts = 0) => {
          const targetRow = document.getElementById(`hospital-${hospitalId}`);
          if (!targetRow) {
            if (attempts < 30) {
              // Row not rendered yet — nudge the scroll slightly and retry
              if (attempts % 5 === 4) {
                // Every 5th attempt, re-dispatch scroll event to force virtualizer sync
                container.dispatchEvent(new Event('scroll', { bubbles: true }));
              }
              setTimeout(() => highlightRow(attempts + 1), 100);
            }
            return;
          }

          // Scroll the row to center if it's not centered
          const rowRect = targetRow.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const rowCenter = rowRect.top + rowRect.height / 2;
          const containerCenter = containerRect.top + containerRect.height / 2;
          if (Math.abs(rowCenter - containerCenter) > 100) {
            const adjustment = rowCenter - containerCenter;
            container.scrollTop += adjustment;
            container.dispatchEvent(new Event('scroll', { bubbles: true }));
          }

          // Apply highlight animation
          targetRow.style.transition = 'all 0.3s ease-in-out';
          targetRow.style.border = '4px solid #3b82f6';
          targetRow.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.8)';
          targetRow.style.transform = 'scale(1.02)';
          targetRow.style.zIndex = '10';
          targetRow.style.position = 'relative';

          setTimeout(() => { targetRow.classList.add('animate-pulse'); }, 200);

          setTimeout(() => {
            targetRow.classList.remove('animate-pulse');
            targetRow.style.border = '2px solid #3b82f6';
            targetRow.style.boxShadow = '0 0 10px rgba(59, 130, 246, 0.4)';
            targetRow.style.transform = '';
          }, 2000);

          setTimeout(() => {
            targetRow.style.border = '';
            targetRow.style.boxShadow = '';
            targetRow.style.transition = '';
            targetRow.style.zIndex = '';
            targetRow.style.position = '';
            setSelectedHospitalId(null);
          }, 8000);
        };

        setTimeout(() => highlightRow(), 100);
      }, 100);
    }, 100);
  };

  // Custom animateTo function for smooth scrolling
  const animateTo = (targetY: number, duration: number = 1000) => {
    const startY = window.pageYOffset;
    const distance = targetY - startY;
    const startTime = performance.now();

    const easeInOutCubic = (t: number) => {
      return t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
    };

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeInOutCubic(progress);

      window.scrollTo(0, startY + distance * easedProgress);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  // Scroll to top function - scroll the table container, not the page
  const scrollToTop = () => {
    const tableContainer = document.querySelector('.hospital-rankings-scroll');
    if (tableContainer) {
      tableContainer.scrollTo({
        top: -500, // Extra offset to ensure we hit the very top
        behavior: 'smooth'
      });
    } else {
      // Fallback to page scroll if container not found
      animateTo(0, 800);
    }
  };

  // Scroll to bottom function
  const scrollToBottom = () => {
    const tableContainer = document.querySelector('.hospital-rankings-scroll');
    if (tableContainer) {
      tableContainer.scrollTo({
        top: tableContainer.scrollHeight + 500, // Extra offset to ensure we hit the very bottom
        behavior: 'smooth'
      });
    } else {
      // Fallback to page scroll if container not found
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // Scroll to specific percentage
  const scrollToPercent = (percent: number) => {
    const tableContainer = document.querySelector('.hospital-rankings-scroll');
    if (tableContainer) {
      const scrollHeight = tableContainer.scrollHeight - tableContainer.clientHeight;
      const targetScroll = (percent / 100) * scrollHeight;
      tableContainer.scrollTo({
        top: targetScroll,
        behavior: 'smooth'
      });
    }
  };

  // Drag handler for scrollbar thumb
  const handleThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Prevent triggering track click
    setIsDragging(true);

    const tableContainer = document.querySelector('.hospital-rankings-scroll') as HTMLElement;
    const scrollbarTrack = document.querySelector('.scrollbar-track') as HTMLElement;
    if (!tableContainer || !scrollbarTrack) return;

    const startY = e.clientY;
    const startScrollTop = tableContainer.scrollTop;
    const trackHeight = scrollbarTrack.getBoundingClientRect().height;
    const scrollHeight = tableContainer.scrollHeight - tableContainer.clientHeight;

    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Calculate how far the mouse has moved
      const deltaY = moveEvent.clientY - startY;

      // Convert mouse movement to scroll movement
      // The ratio is: scroll range / track range
      const scrollRatio = scrollHeight / trackHeight;
      const deltaScroll = deltaY * scrollRatio;

      // Apply the scroll
      const newScrollTop = Math.max(0, Math.min(scrollHeight, startScrollTop + deltaScroll));
      tableContainer.scrollTop = newScrollTop;
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Touch drag handler for scrollbar thumb (mobile)
  const handleThumbTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    setIsDragging(true);

    const tableContainer = document.querySelector('.hospital-rankings-scroll') as HTMLElement;
    const scrollbarTrack = document.querySelector('.scrollbar-track') as HTMLElement;
    if (!tableContainer || !scrollbarTrack) return;

    const startY = e.touches[0].clientY;
    const startScrollTop = tableContainer.scrollTop;
    const trackHeight = scrollbarTrack.getBoundingClientRect().height;
    const scrollHeight = tableContainer.scrollHeight - tableContainer.clientHeight;

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const deltaY = moveEvent.touches[0].clientY - startY;
      const scrollRatio = scrollHeight / trackHeight;
      const newScrollTop = Math.max(0, Math.min(scrollHeight, startScrollTop + deltaY * scrollRatio));
      tableContainer.scrollTop = newScrollTop;
    };

    const handleTouchEnd = () => {
      setIsDragging(false);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd);
  };

  // Memoize maxMarkup and minMarkup to prevent unnecessary recalculations
  const maxMarkup = useMemo(() =>
    rankings ? Math.max(...rankings.map(r => r.avg_gross_markup_percentage)) : 100,
    [rankings]
  );

  const minMarkup = useMemo(() =>
    rankings ? Math.min(...rankings.map(r => r.avg_gross_markup_percentage)) : 0,
    [rankings]
  );

  // Click handler for opening procedure drill-down modal
  const handleMarkupClick = (hospitalId: string, hospitalName: string, procedureType: string, avgMarkup: number) => {
    hapticLight();
    setDrilldownModal({
      isOpen: true,
      hospitalId,
      hospitalName,
      procedureType,
      avgMarkup,
    });
    // Update URL for shareability
    const params = new URLSearchParams(searchParams);
    params.set('breakdown', hospitalId);
    params.set('type', procedureType);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleDrilldownClose = () => {
    setDrilldownModal({ ...drilldownModal, isOpen: false });
    const params = new URLSearchParams(searchParams);
    params.delete('breakdown');
    params.delete('type');
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Virtual scrolling setup - only renders visible rows for performance
  const rowVirtualizer = useVirtualizer({
    count: rankings?.length || 0,
    getScrollElement: () => parentRef.current,
    estimateSize: () => isMobile ? 84 : 99, // Mobile renders cards (~84px); desktop measured from actual rendered rows
    overscan: 5, // Render 5 extra rows above/below viewport for smooth scrolling
  });

  // Chargemaster columns default to OPEN (codeColumnsExpanded=true above)
  // but user can still collapse them. Hidden also in scenario mode.
  const codeColsHidden = isScenarioMode || !codeColumnsExpanded;
  const codeColClass = codeColsHidden ? 'code-col-collapsed' : (isAhp ? 'code-col-expanded' : '');
  const qualityColsHidden = !qualityColumnsExpanded;
  const qualityColClass = qualityColsHidden ? 'quality-col-collapsed' : '';
  const qualityColStyle = qualityColsHidden ? { width: 0, padding: 0, overflow: 'hidden' as const, border: 'none' } : undefined;
  // Per-group flags so Reviews (Google+CMS) and Profile (Beds+Type) can collapse independently
  const reviewsColsHidden = !reviewsColumnsExpanded;
  const profileColsHidden = !profileColumnsExpanded;
  const reviewsColClass = reviewsColsHidden ? 'quality-col-collapsed' : '';
  const profileColClass = profileColsHidden ? 'quality-col-collapsed' : '';
  const reviewsHiddenStyle = reviewsColsHidden ? { width: 0, padding: 0, overflow: 'hidden' as const, border: 'none' } : undefined;
  const profileHiddenStyle = profileColsHidden ? { width: 0, padding: 0, overflow: 'hidden' as const, border: 'none' } : undefined;

  // Code column active/inactive state based on rankByCodeType
  // Overall: CPT+HCPCS+DRG+Revenue active, NDC+Supply greyed
  // Specific type: only that type active, rest greyed
  const isCodeColActive = (codeType: string): boolean => {
    if (rankByCodeType === 'overall') return ['cpt', 'hcpcs', 'drg', 'rev'].includes(codeType);
    return rankByCodeType === codeType;
  };
  // Inactive code columns get a grey overlay that covers the colored markup boxes
  const codeColInactiveStyle: React.CSSProperties = { position: 'relative' as const };
  const CodeColOverlay = () => (
    <div style={{
      position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
      backgroundColor: isDark ? 'rgba(15,23,42,0.82)' : 'rgba(230,230,235,0.82)',
    }} />
  );

  // Centralized colgroup renderer for header and body tables
  const renderColgroup = (isMobile: boolean) => {
    if (isMobile) {
      return (
        <colgroup className="md:hidden">
          <col style={{ width: '34px' }} /> {/* US Rank — compact */}
          {isStateFiltered && <col style={{ width: '34px' }} />} {/* State Rank */}
          <col style={{ width: '120px' }} /> {/* Hospital + Location combined */}
          <col style={{ width: '0px' }} /> {/* Location — hidden on mobile (merged into Hospital) */}
          <col className="markup-col markup-col-first markup-col-last" style={{ width: '76px' }} /> {/* Avg Markup — wider to fit box */}
          {isScenarioMode && <col style={{ width: '75px' }} />} {/* Est. Total (scenario) */}
          {/* Prices Mapped — MOVED into Chargemaster section, see below */}
          {/* Profits block: summary first, then expanded sub-cols. Revenue leads (it's a financial/profits metric, not metadata). */}
          <col className={`profits-col ${profitsColsHidden ? 'profits-first profits-last' : ''}`} style={profitsColsHidden ? { width: '50px' } : { width: '0px' }} /> {/* Profits Summary (collapsed) */}
          <col className={`profits-col ${!profitsColsHidden ? 'profits-first' : ''}`} style={profitsColsHidden ? { width: '0px' } : { width: '70px' }} /> {/* Annual Revenue */}
          <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* Total Medicare $ */}
          <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '54px' }} /> {/* Operating Margin */}
          <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '50px' }} /> {/* Charity Care */}
          <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '50px' }} /> {/* CEO Comp */}
          <col className={`profits-col ${!profitsColsHidden ? 'profits-last' : ''}`} style={profitsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* Exec / Bed */}
          {/* Quality columns */}
          {/* Reviews block: summary first, then expanded sub-cols */}
          <col className={`quality-col ${reviewsColsHidden ? 'quality-first quality-last' : ''}`} style={reviewsColsHidden ? { width: '44px' } : { width: '0px' }} /> {/* Reviews Summary (collapsed) */}
          <col className={`quality-col ${!reviewsColsHidden ? 'quality-first' : ''}`} style={reviewsColsHidden ? { width: '0px' } : { width: '50px' }} /> {/* Google Rating */}
          <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '44px' }} /> {/* CMS Rating */}
          <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* Mortality (Hybrid_HWM) */}
          <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* Readmission (Hybrid_HWR) */}
          <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* HCAHPS (recommend %) — H_RECMND_DY */}
          <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* HAI Risk (SIR) — HAI_1_SIR */}
          <col className={`quality-col ${!reviewsColsHidden ? 'quality-last' : ''}`} style={reviewsColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* ER Wait (median min) — OP_18b */}
          {/* About block: summary first, then expanded sub-cols (metadata + provider IDs) */}
          <col className={`about-col ${profileColsHidden ? 'about-first about-last' : ''}`} style={profileColsHidden ? { width: '88px' } : { width: '0px' }} /> {/* Profile Summary (collapsed) */}
          <col className={`about-col ${!profileColsHidden ? 'about-first' : ''}`} style={profileColsHidden ? { width: '0px' } : { width: '40px' }} /> {/* Beds */}
          <col className="about-col" style={profileColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* Type */}
          <col className="about-col" style={profileColsHidden ? { width: '0px' } : { width: '54px' }} /> {/* NPI */}
          <col className="about-col" style={profileColsHidden ? { width: '0px' } : { width: '50px' }} /> {/* CCN */}
          <col className={`about-col ${!profileColsHidden ? 'about-last' : ''}`} style={profileColsHidden ? { width: '0px' } : { width: '50px' }} /> {/* EIN */}
          {/* Chargemaster (Pricing) columns — bordered as a visual section */}
          <col className="chargemaster-col chargemaster-first" style={codeColsHidden ? { width: '0px' } : { width: '72px' }} /> {/* Total Procedures (moved here) */}
          <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '52px' }} /> {/* CPT */}
          <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '52px' }} /> {/* MS-DRG */}
          <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '52px' }} /> {/* HCPCS */}
          <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '48px' }} /> {/* REV */}
          <col className="chargemaster-col chargemaster-last" style={codeColsHidden ? { width: '0px' } : { width: '48px' }} /> {/* NDC */}
          <col style={(isScenarioMode || !codeColsHidden) ? { width: '0px' } : { width: '52px' }} /> {/* Code Summary (AHP collapsed) — hidden in scenario mode */}
          <col style={{ width: isScenarioMode ? '0px' : '80px' }} /> {/* Price Outliers */}
          <col style={{ width: (isScenarioMode || isAhp) ? '0px' : '72px' }} /> {/* Shame Level */}
        </colgroup>
      );
    }
    return (
      <colgroup className="hidden md:table-column-group">
        <col style={{ width: '80px' }} /> {/* US Rank */}
        {isStateFiltered && <col style={{ width: '70px' }} />} {/* State Rank */}
        <col style={{ width: '220px' }} /> {/* Hospital */}
        <col style={{ width: '180px' }} /> {/* Location */}
        <col className="markup-col markup-col-first markup-col-last" style={{ width: '120px' }} /> {/* Avg Markup */}
        {isScenarioMode && <col style={{ width: '110px' }} />} {/* Est. Total (scenario) */}
        {/* Prices Mapped — MOVED into Chargemaster section, see below */}
        {/* Profits block: summary first, then expanded sub-cols. Revenue leads (financial metric, not metadata). */}
        <col className={`profits-col ${profitsColsHidden ? 'profits-first profits-last' : ''}`} style={profitsColsHidden ? { width: '110px' } : { width: '0px' }} /> {/* Profits Summary (collapsed) */}
        <col className={`profits-col ${!profitsColsHidden ? 'profits-first' : ''}`} style={profitsColsHidden ? { width: '0px' } : { width: '110px' }} /> {/* Annual Revenue */}
        <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '110px' }} /> {/* Total Medicare $ */}
        <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '100px' }} /> {/* Operating Margin */}
        <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '100px' }} /> {/* Charity Care */}
        <col className="profits-col" style={profitsColsHidden ? { width: '0px' } : { width: '100px' }} /> {/* CEO Comp */}
        <col className={`profits-col ${!profitsColsHidden ? 'profits-last' : ''}`} style={profitsColsHidden ? { width: '0px' } : { width: '110px' }} /> {/* Exec / Bed */}
        {/* Quality columns */}
        {/* Reviews block: summary first, then expanded sub-cols */}
        <col className={`quality-col ${reviewsColsHidden ? 'quality-first quality-last' : ''}`} style={reviewsColsHidden ? { width: '110px' } : { width: '0px' }} /> {/* Reviews Summary (collapsed) */}
        <col className={`quality-col ${!reviewsColsHidden ? 'quality-first' : ''}`} style={reviewsColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* Google Rating */}
        <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '70px' }} /> {/* CMS Rating */}
        <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '95px' }} /> {/* Mortality (Hybrid_HWM) */}
        <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '95px' }} /> {/* Readmission (Hybrid_HWR) */}
        <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* HCAHPS (recommend %) — H_RECMND_DY */}
        <col className="quality-col" style={reviewsColsHidden ? { width: '0px' } : { width: '85px' }} /> {/* HAI Risk (SIR) — HAI_1_SIR */}
        <col className={`quality-col ${!reviewsColsHidden ? 'quality-last' : ''}`} style={reviewsColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* ER Wait (median min) — OP_18b */}
        {/* About block: summary first, then expanded sub-cols (metadata + provider IDs) */}
        <col className={`about-col ${profileColsHidden ? 'about-first about-last' : ''}`} style={profileColsHidden ? { width: '150px' } : { width: '0px' }} /> {/* Profile Summary (collapsed) */}
        <col className={`about-col ${!profileColsHidden ? 'about-first' : ''}`} style={profileColsHidden ? { width: '0px' } : { width: '60px' }} /> {/* Beds */}
        <col className="about-col" style={profileColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* Type */}
        <col className="about-col" style={profileColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* NPI */}
        <col className="about-col" style={profileColsHidden ? { width: '0px' } : { width: '80px' }} /> {/* CCN */}
        <col className={`about-col ${!profileColsHidden ? 'about-last' : ''}`} style={profileColsHidden ? { width: '0px' } : { width: '80px' }} /> {/* EIN */}
        {/* Chargemaster (Pricing) columns — bordered as a visual section */}
        <col className="chargemaster-col chargemaster-first" style={codeColsHidden ? { width: '0px' } : { width: '140px' }} /> {/* Total Procedures (moved here) */}
        <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '100px' }} /> {/* CPT */}
        <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '100px' }} /> {/* MS-DRG */}
        <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '100px' }} /> {/* HCPCS */}
        <col className="chargemaster-col" style={codeColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* REV */}
        <col className="chargemaster-col chargemaster-last" style={codeColsHidden ? { width: '0px' } : { width: '90px' }} /> {/* NDC */}
        <col style={(isScenarioMode || !codeColsHidden) ? { width: '0px' } : { width: '120px' }} /> {/* Code Summary (AHP collapsed) — hidden in scenario mode */}
        <col style={{ width: isScenarioMode ? '0px' : '150px' }} /> {/* Price Outliers */}
        <col style={{ width: (isScenarioMode || isAhp) ? '0px' : '120px' }} /> {/* Shame Level */}
      </colgroup>
    );
  };

  // Memoize table row renderer function (not all rows, just the function)
  const renderTableRow = useMemo(() => (hospital: HospitalRanking, index: number) => {
      // In scenario mode: sorted cheapest→most expensive, so invert colors (green=cheap at top, red=expensive at bottom)
      let gradientStyle: { backgroundColor: string; color: string };
      if (isScenarioMode) {
        const total = rankings?.length || 1;
        const fraction = Math.min(1, index / Math.max(1, total - 1)); // 0=cheapest(green) → 1=most expensive(red)
        const r = Math.round(34 + fraction * (239 - 34));
        const g = Math.round(197 - fraction * (197 - 68));
        const b = Math.round(94 - fraction * (94 - 68));
        const alpha = 0.3 + fraction * 0.45;
        gradientStyle = {
          backgroundColor: `rgba(${r}, ${g}, ${b}, ${alpha})`,
          color: (isAhp && !isDark)
            ? (alpha >= 0.5 ? '#ffffff' : (fraction > 0.55 ? '#7f1d1d' : '#14532d'))
            : (fraction > 0.55 ? '#ffffff' : (isAhp ? '#ffffff' : '#14532d')),
        };
      } else {
        gradientStyle = getGradientStyle(hospital.avg_gross_markup_percentage, maxMarkup, minMarkup, isAhp, isAhp && !isDark);
      }
      const shame = getShameLevel(hospital.expensive_procedures_count || 0, hospital.total_procedures || 0);
      const ShameIcon = shame.icon;
      const firstRowPadding = ''; // Removed padding to fix gap at top

      return (
        <TableRow
          key={hospital.hospital_id}
          id={`hospital-${hospital.hospital_id}`}
          style={{
            ...gradientStyle,
            // Removed willChange to prevent Safari from creating 2-3k GPU layers (causes crash)
            // Only use contain for layout isolation without forcing layer creation
            contain: 'layout style paint',
          }}
          className={`hover:opacity-80 transition-opacity ${
            selectedHospitalId === hospital.hospital_id ? 'ring-2 ring-blue-500' : ''
          }`}
        >
           <TableCell className={`text-center font-bold px-0.5 sm:px-2 md:px-4 ${firstRowPadding}`}>
            <div className="flex flex-col items-center justify-center">
              {rankByCodeType !== 'overall' ? (
                <>
                  <span className="text-[10px] sm:text-sm md:text-base lg:text-lg font-bold leading-none">#{index + 1}</span>
                  <span className="text-[7px] sm:text-[9px] md:text-[10px] opacity-50 leading-none mt-0.5">
                    {rankByCodeType.toUpperCase()} of {rankings?.length}
                  </span>
                  <span className="text-[7px] sm:text-[8px] opacity-35 leading-none mt-0.5">
                    #{hospital.overall_rank_gross} overall
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[10px] sm:text-sm md:text-base lg:text-lg font-bold leading-none">#{hospital.overall_rank_gross}</span>
                  <span className="text-[7px] sm:text-[9px] md:text-[10px] opacity-75 leading-none mt-0.5">/{allRankings?.length || rankings?.length}</span>
                </>
              )}
            </div>
          </TableCell>


          {/* State Rank — only shown when a state is selected */}
          {isStateFiltered && (
            <TableCell className={`text-center font-bold ${firstRowPadding}`}>
              <div className="flex flex-col items-center justify-center">
                <span className="text-xs sm:text-sm md:text-base lg:text-lg font-bold">#{index + 1}</span>
                <span className="text-[8px] sm:text-[9px] md:text-[10px] opacity-75">/{rankings?.length}</span>
              </div>
            </TableCell>
          )}

          <TableCell className={`${firstRowPadding} px-0.5 sm:px-2 md:px-4`}>
            <Link
              to={`/hospital/${hospital.hospital_id}`}
              className="font-semibold hover:underline text-current text-[9px] sm:text-xs md:text-sm leading-tight block"
              style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: isMobile ? 1 : 3, WebkitBoxOrient: 'vertical' } as React.CSSProperties}
            >
              {titleCaseHospital(hospital.hospital_name)}
            </Link>
            {/* Mobile: location under hospital name */}
            {isMobile && (
              <span className="text-[8px] leading-none opacity-60 block mt-0.5 truncate">
                {hospital.hospital_city ? hospital.hospital_city.substring(0, 14) : ''}, {hospital.hospital_state}
              </span>
            )}
          </TableCell>

          {/* Location — separate column on desktop, hidden on mobile (merged above) */}
          <TableCell className={`${firstRowPadding} px-1 sm:px-2 md:px-4`} style={isMobile ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}>
            {!isMobile && (
              <div className="flex items-start gap-1 text-current">
                <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span className="text-xs md:text-sm leading-tight">
                  {hospital.hospital_city}, {hospital.hospital_state}
                </span>
              </div>
            )}
          </TableCell>

          <TableCell className={`text-center ${firstRowPadding} ${cellBg}`}>
            {(() => {
              let colorClass: string;
              let displayValue: string;

              if (isScenarioMode) {
                // Rank-based color: index 0 = green (cheapest/best), last = red (most expensive)
                const total = rankings?.length || 1;
                const fraction = Math.min(1, index / Math.max(1, total - 1));
                if (fraction < 0.1) colorClass = 'markup-negative';
                else if (fraction < 0.25) colorClass = 'markup-minimal';
                else if (fraction < 0.5) colorClass = 'markup-low';
                else if (fraction < 0.75) colorClass = 'markup-moderate';
                else if (fraction < 0.9) colorClass = 'markup-high';
                else colorClass = 'markup-extreme';
                displayValue = formatMarkupPercentage(hospital.avg_gross_markup_percentage || 0);
              } else {
                let markupPercentage: number;
                if (sortBy === 'markup' && rankByCodeType !== 'overall') {
                  const codeTypeMarkupMap: Record<string, number | null | undefined> = {
                    cpt: hospital.cpt_avg_markup_percentage,
                    hcpcs: hospital.hcpcs_avg_markup_percentage,
                    drg: hospital.drg_avg_markup_percentage,
                    rev: hospital.rev_avg_markup_percentage,
                    ndc: hospital.ndc_avg_markup_percentage,
                    supply: hospital.supply_avg_markup_percentage,
                  };
                  markupPercentage = codeTypeMarkupMap[rankByCodeType] ?? 0;
                } else if (sortBy === 'markup') {
                  // Show the metric the rank is ordered by (median-baseline);
                  // fall back to avg until the static file carries the field.
                  markupPercentage = hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage ?? 0;
                } else {
                  markupPercentage = hospital.total_procedures > 0
                    ? (hospital.expensive_procedures_count / hospital.total_procedures) * 100
                    : 0;
                }
                colorClass = getMarkupColorClass(markupPercentage);
                displayValue = formatMarkupPercentage(markupPercentage);
              }

              return (
                <div className={`markup-box ${colorClass}`}>
                  <div className="font-mono font-bold text-[10px] sm:text-sm md:text-base lg:text-lg">
                    {displayValue}
                  </div>
                </div>
              );
            })()}
          </TableCell>


          {/* Scenario: Est. Total column — clickable to open coverage modal */}
          {isScenarioMode && (
            <TableCell className={`text-center ${firstRowPadding} ${cellBg}`}>
              {hospital.scenario_blended_price != null ? (
                <div
                  className="space-y-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setCoverageModal({
                    isOpen: true,
                    hospitalId: hospital.hospital_id,
                    hospitalName: hospital.hospital_name,
                    coveragePct: hospital.scenario_coverage_pct ?? null,
                    matchedCodes: hospital.scenario_matched_codes ?? null,
                    totalCodes: hospital.scenario_total_codes ?? null,
                    hospitalTotalPrice: hospital.scenario_total_price ?? null,
                    nationalTotalPrice: hospital.scenario_national_price ?? null,
                  })}
                >
                  <div className="font-mono font-bold text-xs sm:text-sm md:text-base text-white">
                    {hospital.scenario_coverage_pct != null && hospital.scenario_coverage_pct < 100 ? '~' : ''}
                    ${hospital.scenario_blended_price.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </div>
                  {scenarioMeta?.nationalTotal ? (
                    <div className={`text-[9px] sm:text-xs font-mono ${
                      hospital.scenario_blended_price > scenarioMeta.nationalTotal ? 'text-red-400' : 'text-green-400'
                    }`}>
                      {hospital.scenario_blended_price > scenarioMeta.nationalTotal ? '+' : ''}
                      {(((hospital.scenario_blended_price - scenarioMeta.nationalTotal) / scenarioMeta.nationalTotal) * 100).toFixed(0)}%
                    </div>
                  ) : null}
                  {hospital.scenario_matched_codes != null && hospital.scenario_total_codes != null && (
                    <div className="text-[8px] sm:text-[10px] font-mono" style={{ color: 'rgba(197, 165, 90, 0.6)' }}>
                      {hospital.scenario_matched_codes}/{hospital.scenario_total_codes} codes
                    </div>
                  )}
                </div>
              ) : <span className="text-white/30">—</span>}
            </TableCell>
          )}

          {/* Profits summary (collapsed) — shows headline revenue + expand affordance.
              Revenue is the primary financial signal; rendered with magnitude tiers so $10B systems pop in red. */}
          <TableCell
            className={`text-center ${firstRowPadding} ${cellBg} profits-cell`}
            style={profitsColsHidden ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
          >
            {profitsColsHidden && (() => {
              // Prefer manually-researched annual revenue (rare); fall back to HCRIS net patient revenue (common)
              const usd = hospital.annual_revenue_usd ?? hospital.hcris_net_patient_revenue_usd;
              const usingHcris = hospital.annual_revenue_usd == null && hospital.hcris_net_patient_revenue_usd != null;
              const isResolved = resolvedHospitalIds.has(hospital.hospital_id);
              // Still loading from DB → slot-machine placeholder
              if (usd == null && !isResolved) {
                return (
                  <div className="flex flex-col items-center justify-center leading-tight">
                    <div className="flex items-center gap-0.5">
                      <Banknote className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-500/60" />
                      <span className="text-[7px] sm:text-[9px] font-semibold text-white/50">Financial</span>
                    </div>
                    <RevenueTicker size="md" />
                  </div>
                );
              }
              let formatted: string | null = null;
              let colorCls = 'text-white/85';
              let weightCls = 'font-bold';
              if (usd != null) {
                if (usd >= 1_000_000_000) {
                  const b = usd / 1_000_000_000;
                  formatted = `$${b >= 100 ? b.toFixed(0) : b >= 10 ? b.toFixed(1) : b.toFixed(2)}B`;
                } else if (usd >= 1_000_000) {
                  formatted = `$${Math.round(usd / 1_000_000)}M`;
                } else if (usd >= 1_000) {
                  formatted = `$${Math.round(usd / 1_000)}K`;
                } else {
                  formatted = `$${usd}`;
                }
                if (usd >= 10_000_000_000)        { colorCls = 'text-red-400';     weightCls = 'font-extrabold tracking-tight'; }
                else if (usd >= 2_000_000_000)    { colorCls = 'text-orange-300';  weightCls = 'font-extrabold'; }
                else if (usd >= 500_000_000)      { colorCls = 'text-amber-200';   weightCls = 'font-bold'; }
                else if (usd >= 50_000_000)       { colorCls = 'text-white/95';    weightCls = 'font-bold'; }
                else                               { colorCls = 'text-white/65';    weightCls = 'font-semibold'; }
              }
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={
                    <div className="space-y-1 max-w-xs text-xs">
                      <p className="font-semibold">Financial — annual revenue</p>
                      <p className="text-xs">Source: <strong>{usingHcris ? 'CMS HCRIS Form 2552 net patient revenue' : (hospital.revenue_source || 'manual research')}</strong>{usingHcris && hospital.finance_fiscal_year ? ` (FY${hospital.finance_fiscal_year})` : hospital.revenue_year ? ` (${hospital.revenue_year})` : ''}.</p>
                      <p className="text-[10px] opacity-60 pt-1 border-t mt-1">Click revenue → full breakdown + IRS Form 990. Click "Financial" label → expand columns.</p>
                    </div>
                  }
                >
                  <div className="flex flex-col items-center justify-center leading-tight">
                    {/* Group label expands the column section (chevron). Keep it independent from the revenue link. */}
                    <button
                      type="button"
                      className="flex items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity bg-transparent border-0 p-0 text-inherit"
                      onClick={(e) => { e.stopPropagation(); setProfitsColumnsExpanded(true); }}
                      title="Expand Financial columns"
                    >
                      <Banknote className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-500" />
                      <span className="text-[7px] sm:text-[9px] font-semibold text-white/80">Financial</span>
                    </button>
                    {/* Revenue value links to the hospital's financial tab — the canonical source-data view. */}
                    {formatted ? (
                      <Link
                        to={`/hospital/${hospital.hospital_id}?tab=financial`}
                        onClick={(e) => e.stopPropagation()}
                        className={`font-mono ${weightCls} ${colorCls} text-xs sm:text-base mt-0.5 hover:opacity-80 transition-opacity`}
                      >
                        {formatted}
                      </Link>
                    ) : (
                      <span className="text-white/30 text-[10px] sm:text-xs font-mono mt-0.5">—</span>
                    )}
                    {usd != null && (() => {
                      const yr = usingHcris ? hospital.finance_fiscal_year : hospital.revenue_year;
                      return yr ? (
                        <span className="text-[6px] sm:text-[8px] text-white/40 font-mono leading-none">
                          {usingHcris ? 'FY' : "'"}{String(yr).slice(-2)}
                        </span>
                      ) : null;
                    })()}
                  </div>
                </ConditionalTooltip>
              );
            })()}
          </TableCell>
          {/* Profits: Annual Revenue (FIRST detail col — moved from About since revenue is a financial metric).
              Format scales with magnitude (K/M/B); color escalates: white → amber → orange → red as $ climbs into mega-system territory. */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
            {(() => {
              const usd = hospital.annual_revenue_usd ?? hospital.hcris_net_patient_revenue_usd;
              const isResolved = resolvedHospitalIds.has(hospital.hospital_id);
              if (usd == null && !isResolved) {
                return <RevenueTicker size="md" />;
              }
              if (usd == null) {
                return <span className="text-white/20 text-[8px] sm:text-[10px]" title="Not yet researched.">—</span>;
              }
              const usingHcris = hospital.annual_revenue_usd == null;
              let formatted: string;
              if (usd >= 1_000_000_000) {
                const b = usd / 1_000_000_000;
                formatted = `$${b >= 100 ? b.toFixed(0) : b >= 10 ? b.toFixed(1) : b.toFixed(2)}B`;
              } else if (usd >= 1_000_000) {
                formatted = `$${Math.round(usd / 1_000_000)}M`;
              } else if (usd >= 1_000) {
                formatted = `$${Math.round(usd / 1_000)}K`;
              } else {
                formatted = `$${usd}`;
              }
              // Magnitude-driven styling: bigger systems get brighter/warmer treatment.
              let colorCls = 'text-white/85';
              let sizeCls = 'text-xs sm:text-sm';
              let weightCls = 'font-bold';
              if (usd >= 10_000_000_000) {
                // $10B+ — system-level mega-corp
                colorCls = 'text-red-400'; sizeCls = 'text-sm sm:text-lg'; weightCls = 'font-extrabold tracking-tight';
              } else if (usd >= 2_000_000_000) {
                // $2B–$10B — major system
                colorCls = 'text-orange-300'; sizeCls = 'text-sm sm:text-base'; weightCls = 'font-extrabold';
              } else if (usd >= 500_000_000) {
                // $500M–$2B — large hospital
                colorCls = 'text-amber-200'; sizeCls = 'text-xs sm:text-base'; weightCls = 'font-bold';
              } else if (usd >= 50_000_000) {
                // $50M–$500M — mid-size
                colorCls = 'text-white/95'; sizeCls = 'text-xs sm:text-sm'; weightCls = 'font-bold';
              } else {
                // <$50M — small / critical access
                colorCls = 'text-white/65'; sizeCls = 'text-[10px] sm:text-xs'; weightCls = 'font-semibold';
              }
              const yr = usingHcris ? hospital.finance_fiscal_year : hospital.revenue_year;
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={
                    <div className="space-y-1 max-w-xs text-xs">
                      <p className="font-semibold">{titleCaseHospital(hospital.hospital_name)} — Annual Revenue</p>
                      <p>${usd.toLocaleString()} <span className="opacity-70">({yr ? (usingHcris ? `FY${yr}` : yr) : 'year unknown'})</span></p>
                      <p className="opacity-70">Source: {usingHcris ? 'CMS HCRIS Form 2552 (net patient revenue)' : (hospital.revenue_source || 'manual research')}</p>
                      {hospital.hospital_system && !usingHcris && <p className="opacity-70">Note: manually-researched revenue may be system-wide ({hospital.hospital_system}), not facility-specific.</p>}
                      <p className="opacity-50 text-[10px] pt-1 border-t mt-1">Click to view full financial breakdown + IRS Form 990 →</p>
                    </div>
                  }
                >
                  <Link
                    to={`/hospital/${hospital.hospital_id}?tab=financial`}
                    onClick={(e) => e.stopPropagation()}
                    className="block cursor-pointer hover:opacity-80 transition-opacity leading-tight"
                  >
                    <div className={`font-mono ${weightCls} ${sizeCls} ${colorCls}`}>{formatted}</div>
                    {yr && (
                      <div className="text-[7px] sm:text-[9px] text-white/40 leading-none mt-0.5 font-mono">
                        {usingHcris ? 'FY' : "'"}{String(yr).slice(-2)}
                      </div>
                    )}
                  </Link>
                </ConditionalTooltip>
              );
            })()}
          </TableCell>
          {/* Profits: Total Medicare $ — HCRIS Worksheet E Part A line 4900 (IPPS inpatient only) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
            {hospital.medicare_total_payments_usd != null ? (() => {
              const usd = Number(hospital.medicare_total_payments_usd);
              const fmt = usd >= 1_000_000_000 ? `$${(usd / 1_000_000_000).toFixed(1)}B` :
                          usd >= 1_000_000     ? `$${Math.round(usd / 1_000_000)}M` :
                          usd >= 1_000          ? `$${Math.round(usd / 1_000)}K` : `$${usd}`;
              return <span className="font-mono text-[10px] sm:text-sm font-bold text-white/85">{fmt}</span>;
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>
          {/* Profits: Operating Margin — HCRIS net_income / net_pat_rev × 100. Negative = losing money. */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
            {hospital.operating_margin_pct != null ? (() => {
              const v = Number(hospital.operating_margin_pct);
              // Tier color: red (<0), white (0-3), amber (3-7), emerald (>=7) — bigger margins flag in green like Cleveland Clinic-tier.
              const cls = v < 0 ? 'text-red-400 font-bold' :
                          v >= 7 ? 'text-emerald-400 font-bold' :
                          v >= 3 ? 'text-amber-300 font-semibold' : 'text-white/85 font-semibold';
              return <span className={`font-mono text-[10px] sm:text-sm ${cls}`}>{v >= 0 ? '+' : ''}{v.toFixed(1)}%</span>;
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>
          {/* Profits: Charity Care — HCRIS S-10 (cost preferred, charges fallback) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
            {hospital.charity_care_usd != null ? (() => {
              const usd = Number(hospital.charity_care_usd);
              const fmt = usd >= 1_000_000_000 ? `$${(usd / 1_000_000_000).toFixed(1)}B` :
                          usd >= 1_000_000     ? `$${Math.round(usd / 1_000_000)}M` :
                          usd >= 1_000          ? `$${Math.round(usd / 1_000)}K` : `$${usd}`;
              return <span className="font-mono text-[10px] sm:text-sm font-semibold text-white/80">{fmt}</span>;
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>
          {/* Profits: CEO Comp — populated via DB enrichment from hospital_990_executives (highest total_comp_usd). */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
            {hospital.ceo_comp_usd != null ? (() => {
              const usd = Number(hospital.ceo_comp_usd);
              const fmt = usd >= 1_000_000 ? `$${(usd / 1_000_000).toFixed(1)}M` :
                          usd >= 1_000      ? `$${Math.round(usd / 1_000)}K` : `$${usd}`;
              // CEO comp tier color: amber for $1M+, red for $5M+ (egregious), white for <$1M.
              const cls = usd >= 5_000_000 ? 'text-red-400 font-bold' :
                          usd >= 1_000_000 ? 'text-amber-300 font-bold' :
                          'text-white/85 font-semibold';
              return <span className={`font-mono text-[10px] sm:text-sm ${cls}`}>{fmt}</span>;
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>
          {/* Profits: Exec comp ÷ bed — proxy for "how much top exec pay each bed of capacity is supporting".
              Sortable independently via /hospital-rankings/executive-comp/. Tier color: amber for
              $5K+/bed, red for $20K+/bed (egregious for a typical 200-bed community hospital). */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
            {hospital.ceo_comp_usd != null && hospital.bed_count != null && hospital.bed_count > 0 ? (() => {
              const ratio = Number(hospital.ceo_comp_usd) / Number(hospital.bed_count);
              const fmt = ratio >= 1000 ? `$${Math.round(ratio / 1000)}K` : `$${Math.round(ratio)}`;
              const cls = ratio >= 20_000 ? 'text-red-400 font-bold' :
                          ratio >= 5_000  ? 'text-amber-300 font-bold' :
                          'text-white/85 font-semibold';
              return <span className={`font-mono text-[10px] sm:text-sm ${cls}`}>{fmt}<span className="opacity-60 text-[8px] ml-0.5">/bed</span></span>;
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>

          {/* Reviews summary (collapsed) — at start of Reviews block, combined Google + CMS star rating */}
          <TableCell
            className={`text-center ${firstRowPadding} ${cellBg} quality-cell`}
            style={reviewsColsHidden ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
          >
            {reviewsColsHidden && (() => {
              const g = hospital.google_rating;
              const c = hospital.cms_overall_rating;
              const hasGoogle = g != null && g > 0;
              const hasCms = c != null && c > 0;
              const combined = (hasGoogle && hasCms) ? (Number(g) + Number(c)) / 2
                              : hasGoogle ? Number(g)
                              : hasCms ? Number(c)
                              : null;
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={
                    <div className="space-y-1 max-w-xs">
                      <p className="font-semibold">{titleCaseHospital(hospital.hospital_name)} — Reviews</p>
                      <p className="text-xs">Google: {hasGoogle ? `${Number(g).toFixed(1)} ★ (${hospital.google_review_count?.toLocaleString() || '0'} reviews)` : 'no data'}</p>
                      <p className="text-xs">CMS: {hasCms ? `${c} ★ (federal quality rating)` : 'no data'}</p>
                      {combined != null && <p className="text-xs pt-1 border-t opacity-80">Combined: {combined.toFixed(1)} ★</p>}
                    </div>
                  }
                >
                  <div
                    className="flex flex-col items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setReviewsColumnsExpanded(true)}
                  >
                    <Star className="w-3.5 h-3.5 text-[#facc15]" fill="#facc15" />
                    <span className="text-[9px] sm:text-xs font-mono text-white/70 font-semibold">
                      {combined != null ? `${combined.toFixed(1)}` : '—'}
                    </span>
                    <span className="text-[7px] sm:text-[9px] text-white/40 leading-none">
                      {hasGoogle && hasCms ? 'G+CMS' : hasGoogle ? 'Google' : hasCms ? 'CMS' : 'no data'}
                    </span>
                  </div>
                </ConditionalTooltip>
              );
            })()}
          </TableCell>

          {/* Reviews: Google Rating */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {hospital.google_rating != null ? (
              <div className="space-y-0.5">
                <div className="font-mono font-bold text-xs sm:text-sm" style={{ color: '#facc15' }}>
                  {Number(hospital.google_rating).toFixed(1)} <Star className="w-3 h-3 inline-block -mt-0.5" fill="#facc15" stroke="#facc15" />
                </div>
                {hospital.google_review_count != null && (
                  <div className="text-[9px] sm:text-xs text-white/40">
                    ({hospital.google_review_count >= 1000
                      ? `${(hospital.google_review_count / 1000).toFixed(1)}k`
                      : hospital.google_review_count})
                  </div>
                )}
              </div>
            ) : <span className="text-white/20">—</span>}
          </TableCell>

          {/* Reviews: CMS Rating */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {hospital.cms_overall_rating != null ? (
              <div className="flex items-center justify-center gap-0.5">
                {[1, 2, 3, 4, 5].map(i => (
                  <Star
                    key={i}
                    className="w-2.5 h-2.5 sm:w-3 sm:h-3"
                    fill={i <= hospital.cms_overall_rating! ? '#facc15' : 'transparent'}
                    stroke={i <= hospital.cms_overall_rating! ? '#facc15' : '#555'}
                    strokeWidth={1.5}
                  />
                ))}
              </div>
            ) : <span className="text-white/20">—</span>}
          </TableCell>

          {/* Reviews: Mortality (Hybrid_HWM, 30-day risk-adjusted death rate) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {hospital.mortality_score != null ? (() => {
              const s = Number(hospital.mortality_score);
              const rank = (hospital as any)._quality_ranks?.mortality;
              // Tone: <12 (better than typical) = green, 12-13.5 = neutral, >13.5 = red. National median ~12.1.
              const cls = s < 12 ? 'text-emerald-300'
                       : s > 13.5 ? 'text-red-300'
                       : 'text-amber-200';
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">{titleCaseHospital(hospital.hospital_name)}</p><p>30-day risk-adjusted hospital-wide mortality rate: <strong>{s.toFixed(2)}%</strong>{hospital.mortality_denom ? ` (${hospital.mortality_denom.toLocaleString()} patients)` : ''}.</p>{rank ? <p className="opacity-70">Hall-of-Shame rank: <strong>#{rank}</strong> worst nationwide.</p> : null}<p className="opacity-60">U.S. median ≈ 12.1%. Higher means worse outcomes. Click to see the full leaderboard.</p></div>}
                >
                  <Link to="/quality/measure/Hybrid_HWM" className="block hover:opacity-80 flex flex-col items-center leading-tight">
                    <span className={`font-mono text-[10px] sm:text-sm font-semibold ${cls}`}>{s.toFixed(1)}%</span>
                    {rank ? <span className="text-[8px] sm:text-[9px] text-white/40">#{rank}</span> : null}
                  </Link>
                </ConditionalTooltip>
              );
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>

          {/* Reviews: Readmission (Hybrid_HWR, 30-day all-cause readmission rate) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {hospital.readmission_score != null ? (() => {
              const s = Number(hospital.readmission_score);
              const rank = (hospital as any)._quality_ranks?.readmission;
              // National median ~15.0, p25-p75 ~14.6-15.4.
              const cls = s < 14.6 ? 'text-emerald-300'
                       : s > 15.4 ? 'text-red-300'
                       : 'text-amber-200';
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">{titleCaseHospital(hospital.hospital_name)}</p><p>30-day all-cause hospital-wide readmission rate: <strong>{s.toFixed(2)}%</strong>{hospital.readmission_denom ? ` (${hospital.readmission_denom.toLocaleString()} patients)` : ''}.</p>{rank ? <p className="opacity-70">Hall-of-Shame rank: <strong>#{rank}</strong> worst nationwide.</p> : null}<p className="opacity-60">U.S. median ≈ 15.0%. Higher means worse. Click to see the full leaderboard.</p></div>}
                >
                  <Link to="/quality/measure/Hybrid_HWR" className="block hover:opacity-80 flex flex-col items-center leading-tight">
                    <span className={`font-mono text-[10px] sm:text-sm font-semibold ${cls}`}>{s.toFixed(1)}%</span>
                    {rank ? <span className="text-[8px] sm:text-[9px] text-white/40">#{rank}</span> : null}
                  </Link>
                </ConditionalTooltip>
              );
            })() : (
              <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>
            )}
          </TableCell>

          {/* Reviews: HCAHPS would-recommend % — H_RECMND_DY. Baked into
           *  rankings.json as hcahps_recommend_pct; falls back to the
           *  cmsMeasureCache prefetch during cache-rollover windows. Lower
           *  % = worse here. */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {(() => {
              const v = (hospital as any).hcahps_recommend_pct ?? cmsMeasureCache.get(hospital.hospital_id)?.get('hcahps');
              if (v == null) return <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>;
              const rank = (hospital as any)._quality_ranks?.hcahps;
              // Anything above 78% lands top-quartile; below 65% bottom-quartile.
              const cls = v >= 78 ? 'text-emerald-300' : v < 65 ? 'text-red-300' : 'text-amber-200';
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">{titleCaseHospital(hospital.hospital_name)}</p><p>HCAHPS &quot;would definitely recommend&quot; rate: <strong>{Math.round(v)}%</strong>.</p>{rank ? <p className="opacity-70">Hall-of-Shame rank: <strong>#{rank}</strong> worst patient experience.</p> : null}<p className="opacity-60">CMS national average ≈ 72%. Lower means worse experience.</p></div>}
                >
                  <div className="flex flex-col items-center leading-tight">
                    <span className={`font-mono text-[10px] sm:text-sm font-semibold ${cls}`}>{Math.round(v)}%</span>
                    {rank ? <span className="text-[8px] sm:text-[9px] text-white/40">#{rank}</span> : null}
                  </div>
                </ConditionalTooltip>
              );
            })()}
          </TableCell>

          {/* Reviews: HAI Risk (Standardized Infection Ratio) — HAI_1_SIR.
           *  Higher = worse (more infections than national average). */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {(() => {
              const v = (hospital as any).hai_risk_sir ?? cmsMeasureCache.get(hospital.hospital_id)?.get('hai_risk');
              if (v == null) return <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>;
              const rank = (hospital as any)._quality_ranks?.hai_risk;
              const cls = v <= 0.8 ? 'text-emerald-300' : v > 1.2 ? 'text-red-300' : 'text-amber-200';
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">{titleCaseHospital(hospital.hospital_name)}</p><p>Central-line bloodstream infection SIR: <strong>{v.toFixed(2)}</strong>.</p>{rank ? <p className="opacity-70">Hall-of-Shame rank: <strong>#{rank}</strong> highest infection ratio.</p> : null}<p className="opacity-60">1.0 = national average. Above 1.0 means more infections than average.</p></div>}
                >
                  <div className="flex flex-col items-center leading-tight">
                    <span className={`font-mono text-[10px] sm:text-sm font-semibold ${cls}`}>{v.toFixed(2)}</span>
                    {rank ? <span className="text-[8px] sm:text-[9px] text-white/40">#{rank}</span> : null}
                  </div>
                </ConditionalTooltip>
              );
            })()}
          </TableCell>

          {/* Reviews: ER Wait Time (median minutes) — OP_18b. Longer = worse. */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} quality-cell ${reviewsColClass}`}
            style={reviewsHiddenStyle}
          >
            {(() => {
              const v = (hospital as any).er_wait_minutes ?? cmsMeasureCache.get(hospital.hospital_id)?.get('er_wait_time');
              if (v == null) return <span className="font-mono text-[9px] sm:text-xs text-white/25">—</span>;
              const rank = (hospital as any)._quality_ranks?.er_wait_time;
              // National median ~150 min. Below 120 = good, above 240 = bad.
              const cls = v <= 120 ? 'text-emerald-300' : v > 240 ? 'text-red-300' : 'text-amber-200';
              return (
                <ConditionalTooltip
                  isMobile={isMobile}
                  content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">{titleCaseHospital(hospital.hospital_name)}</p><p>Median ER visit length: <strong>{Math.round(v)} min</strong>.</p>{rank ? <p className="opacity-70">Hall-of-Shame rank: <strong>#{rank}</strong> longest waits nationwide.</p> : null}<p className="opacity-60">CMS national median ≈ 150 min. Higher means longer waits.</p></div>}
                >
                  <div className="flex flex-col items-center leading-tight">
                    <span className={`font-mono text-[10px] sm:text-sm font-semibold ${cls}`}>{Math.round(v)}m</span>
                    {rank ? <span className="text-[8px] sm:text-[9px] text-white/40">#{rank}</span> : null}
                  </div>
                </ConditionalTooltip>
              );
            })()}
          </TableCell>

          {/* Profile summary (collapsed) — at start of Profile block, type badge + revenue, click to expand */}
          <TableCell
            className={`text-center ${firstRowPadding} ${cellBg} about-cell`}
            style={profileColsHidden ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
          >
            {profileColsHidden && (
              <ConditionalTooltip
                isMobile={isMobile}
                content={
                  <div className="space-y-1 max-w-xs text-xs">
                    <p className="font-semibold">{titleCaseHospital(hospital.hospital_name)} — Profile</p>
                    {hospital.hospital_system && <p>System: <strong>{hospital.hospital_system}</strong></p>}
                    {hospital.hospital_type && <p>Type: {hospital.hospital_type}</p>}
                    {hospital.ownership && <p>Ownership: {hospital.ownership}</p>}
                    {hospital.year_founded && <p>Founded: {hospital.year_founded}</p>}
                    {hospital.bed_count != null && hospital.bed_count > 0 && <p>Beds: {hospital.bed_count.toLocaleString()}</p>}
                    {hospital.annual_revenue_usd != null && (
                      <p>Annual revenue ({hospital.revenue_year || '?'}): <strong>${(hospital.annual_revenue_usd / 1_000_000).toFixed(0)}M</strong>{hospital.revenue_source && <span className="opacity-70"> · {hospital.revenue_source}</span>}</p>
                    )}
                    {!hospital.hospital_system && !hospital.year_founded && !hospital.annual_revenue_usd && hospital.bed_count == null && (
                      <p className="opacity-60">Profile data not yet researched.</p>
                    )}
                    <p className="text-[10px] opacity-50 pt-1 border-t">Click to expand columns</p>
                  </div>
                }
              >
                <div
                  className="flex flex-col items-center gap-0.5 cursor-pointer hover:opacity-80 transition-opacity w-full px-0.5"
                  onClick={() => setProfileColumnsExpanded(true)}
                >
                  {/* Parent system / LLC — visible primary info (no hover required) */}
                  {hospital.hospital_system ? (
                    <span
                      className="text-[9px] sm:text-[11px] leading-tight font-semibold text-white/85 truncate max-w-full text-center"
                      title={hospital.hospital_system}
                    >
                      {hospital.hospital_system}
                    </span>
                  ) : (
                    <span className="text-[8px] sm:text-[10px] text-white/35 italic leading-tight">Independent</span>
                  )}
                  {/* Type badge + ownership — secondary info */}
                  <div className="flex flex-wrap items-center justify-center gap-0.5">
                    {hospital.hospital_type ? (
                      <span className={`inline-block px-1 py-0.5 rounded text-[7px] sm:text-[9px] font-medium truncate max-w-[90px] ${
                        hospital.hospital_type.includes('Acute') ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                        hospital.hospital_type.includes('Critical') ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' :
                        hospital.hospital_type.includes('Psychiatric') ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                        hospital.hospital_type.includes('Children') ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30' :
                        'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                      }`}>
                        {hospital.hospital_type.replace(' Hospitals', '').replace(' Hospital', '')}
                      </span>
                    ) : !hospital.hospital_system && (
                      <span className="text-[8px] sm:text-[10px] text-white/40">About</span>
                    )}
                  </div>
                  {/* Beds count (revenue moved to Profits section) */}
                  {hospital.bed_count != null && hospital.bed_count > 0 && (
                    <span className="text-[7px] sm:text-[9px] text-white/45 leading-none font-mono whitespace-nowrap">
                      {hospital.bed_count.toLocaleString()} beds
                    </span>
                  )}
                  {/* Provider IDs — clickable chips that open source-of-truth
                      pages. Master 2026-05-26: show the actual identifier
                      next to each label so journalists / advocacy researchers
                      can read the number without first uncollapsing the
                      column. CCN was already wired; restating in code-review
                      style so the trio reads consistently.
                      stopPropagation() so clicking a chip doesn't also expand
                      the column.

                      Each chip targets a public-record portal:
                        NPI → NPPES NPI Registry (CMS, public)
                        CCN → CMS Care Compare hospital profile (public)
                        EIN → ProPublica Nonprofit Explorer (IRS Form 990 mirror) */}
                  {(hospital.npi_number || hospital.ccn || hospital.ein) && (
                    <div className="flex flex-wrap items-center justify-center gap-1 mt-1">
                      {hospital.npi_number && (
                        <a
                          href={`https://npiregistry.cms.hhs.gov/provider-view/${hospital.npi_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title={`NPI ${hospital.npi_number} → NPPES NPI Registry`}
                          className="inline-flex items-center gap-1 text-[7px] sm:text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/65 border border-white/10 hover:bg-white/15 hover:text-white transition-colors leading-none whitespace-nowrap"
                        >
                          <span className="font-semibold tracking-wider opacity-70">NPI</span>
                          <span>{hospital.npi_number}</span>
                        </a>
                      )}
                      {hospital.ccn && (
                        <a
                          href={`https://www.medicare.gov/care-compare/details/hospital/${hospital.ccn}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title={`CCN ${hospital.ccn} → CMS Care Compare`}
                          className="inline-flex items-center gap-1 text-[7px] sm:text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/65 border border-white/10 hover:bg-white/15 hover:text-white transition-colors leading-none whitespace-nowrap"
                        >
                          <span className="font-semibold tracking-wider opacity-70">CCN</span>
                          <span>{hospital.ccn}</span>
                        </a>
                      )}
                      {hospital.ein && (
                        <a
                          href={`https://projects.propublica.org/nonprofits/organizations/${hospital.ein.replace(/-/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          title={`EIN ${hospital.ein} → ProPublica Nonprofit Explorer (IRS Form 990)`}
                          className="inline-flex items-center gap-1 text-[7px] sm:text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-white/65 border border-white/10 hover:bg-white/15 hover:text-white transition-colors leading-none whitespace-nowrap"
                        >
                          <span className="font-semibold tracking-wider opacity-70">EIN</span>
                          <span>{hospital.ein}</span>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </ConditionalTooltip>
            )}
          </TableCell>

          {/* Profile: Beds */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} about-cell ${profileColClass}`}
            style={profileHiddenStyle}
          >
            {hospital.bed_count != null ? (
              <div className="font-mono text-xs sm:text-sm text-white/80">
                {hospital.bed_count.toLocaleString()}
              </div>
            ) : <span className="text-white/20">—</span>}
          </TableCell>

          {/* Profile: Type (own column when expanded) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} about-cell ${profileColClass}`}
            style={profileHiddenStyle}
          >
            <ConditionalTooltip
              isMobile={isMobile}
              content={
                <div className="space-y-1 max-w-xs text-xs">
                  <p className="font-semibold">{titleCaseHospital(hospital.hospital_name)}</p>
                  {hospital.hospital_type && <p>Type: <strong>{hospital.hospital_type}</strong></p>}
                  {hospital.ownership && <p>Ownership: {hospital.ownership}</p>}
                  {hospital.hospital_system && <p>System: {hospital.hospital_system}</p>}
                  {!hospital.hospital_type && !hospital.ownership && <p className="opacity-60">Type data not yet researched.</p>}
                </div>
              }
            >
              <div className="cursor-help">
                {hospital.hospital_type ? (
                  <div className={`inline-block px-1.5 py-0.5 rounded text-[8px] sm:text-[10px] font-medium truncate max-w-[80px] ${
                    hospital.hospital_type.includes('Acute') ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                    hospital.hospital_type.includes('Critical') ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30' :
                    hospital.hospital_type.includes('Psychiatric') ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                    hospital.hospital_type.includes('Children') ? 'bg-pink-500/20 text-pink-300 border border-pink-500/30' :
                    'bg-gray-500/20 text-gray-300 border border-gray-500/30'
                  }`}>
                    {hospital.hospital_type.replace(' Hospitals', '').replace(' Hospital', '')}
                  </div>
                ) : <span className="text-white/20">—</span>}
                {hospital.ownership && (
                  <div className="text-[7px] sm:text-[9px] mt-0.5 opacity-60 truncate max-w-[80px]" title={hospital.ownership}>
                    {hospital.ownership.replace('Voluntary ', '').replace(' Private', '').replace(' Government', '')}
                  </div>
                )}
              </div>
            </ConditionalTooltip>
          </TableCell>

          {/* About: NPI cell — links to NPPES Registry */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} about-cell ${profileColClass}`} style={profileHiddenStyle}>
            {hospital.npi_number ? (
              <a
                href={`https://npiregistry.cms.hhs.gov/provider-view/${hospital.npi_number}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`Open NPPES record for NPI ${hospital.npi_number}`}
                className="font-mono text-[9px] sm:text-[11px] text-white/85 hover:text-white underline decoration-white/30 hover:decoration-white underline-offset-2 transition-colors"
              >
                {hospital.npi_number}
              </a>
            ) : <span className="text-white/20 font-mono text-[9px] sm:text-[11px]">—</span>}
          </TableCell>

          {/* About: CCN cell — links to CMS Care Compare */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} about-cell ${profileColClass}`} style={profileHiddenStyle}>
            {hospital.ccn ? (
              <a
                href={`https://www.medicare.gov/care-compare/details/hospital/${hospital.ccn}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`Open Care Compare for CCN ${hospital.ccn}`}
                className="font-mono text-[9px] sm:text-[11px] text-white/85 hover:text-white underline decoration-white/30 hover:decoration-white underline-offset-2 transition-colors"
              >
                {hospital.ccn}
              </a>
            ) : <span className="text-white/20 font-mono text-[9px] sm:text-[11px]">—</span>}
          </TableCell>

          {/* About: EIN cell — links to ProPublica Nonprofit Explorer (Form 990 record) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} about-cell ${profileColClass}`} style={profileHiddenStyle}>
            {hospital.ein ? (
              <a
                href={`https://projects.propublica.org/nonprofits/organizations/${hospital.ein.replace(/-/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title={`Open ProPublica Nonprofit Explorer for EIN ${hospital.ein}`}
                className="font-mono text-[9px] sm:text-[11px] text-white/85 hover:text-white underline decoration-white/30 hover:decoration-white underline-offset-2 transition-colors"
              >
                {hospital.ein}
              </a>
            ) : <span className="text-white/20 font-mono text-[9px] sm:text-[11px]">—</span>}
          </TableCell>

          {/* Total Procedures — moved into Chargemaster section, first column */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} chargemaster-cell ${codeColClass}`}
            style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
          >
            <div className="font-mono text-[9px] sm:text-xs md:text-sm space-y-0.5 sm:space-y-1">
              <div className="text-white font-semibold">
                {formatNumberCompact(hospital.total_procedures)}
                <span className={`${isAhp ? 'text-[#C5A55A]' : 'text-blue-400'} text-[7px] sm:text-[9px] md:text-xs ml-0.5 sm:ml-1`}>ranked</span>
              </div>
              <div className={`${isAhp ? 'text-white/50' : 'text-white/40'} text-[8px] sm:text-xs border-t border-white/10 pt-0.5 sm:pt-1`}>
                {formatNumberCompact(hospital.total_available_procedures)} total
              </div>
            </div>
          </TableCell>

          {/* CPT Column */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} chargemaster-cell ${codeColClass}`}
            style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : (!isCodeColActive('cpt') ? codeColInactiveStyle : undefined)}
          >
            {!codeColsHidden && !isCodeColActive('cpt') && <CodeColOverlay />}
            <div className="flex flex-col items-center gap-1">
              <ConditionalTooltip
                isMobile={isMobile}
                content={
                  <div className="space-y-1">
                    <p className="font-semibold">CPT Procedure Coverage</p>
                    <p className="text-sm">{hospital.cpt_procedure_count?.toLocaleString() || '0'} CPT procedures were statistically relevant and ranked against national averages out of {hospital.cpt_total_available?.toLocaleString() || '0'} total CPT codes listed by this hospital.</p>
                  </div>
                }
              >
                <div className="px-1 sm:px-2 py-1 rounded font-mono text-[9px] sm:text-xs md:text-sm font-bold cursor-help" style={{
                  backgroundColor: '#000000',
                  color: '#60a5fa',
                  borderColor: '#60a5fa',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}>
                  {formatNumberCompact(hospital.cpt_procedure_count)}/{formatNumberCompact(hospital.cpt_total_available)}
                </div>
              </ConditionalTooltip>
              <div className="text-xs">
                {(() => {
                  const markup = hospital.cpt_avg_markup_percentage;
                  if (markup == null || markup === 0) {
                    return (
                      <div className="markup-box text-[9px] sm:text-xs md:text-sm markup-neutral font-mono font-bold">
                        {markup === 0 ? '0.0%' : '-%'}
                      </div>
                    );
                  }
                  const colorClass = getMarkupColorClass(markup);
                  return (
                    <div
                      className={`markup-box text-[9px] sm:text-xs md:text-sm ${colorClass} font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity`}
                      onClick={() => handleMarkupClick(hospital.hospital_id, hospital.hospital_name, 'cpt', markup)}
                    >
                      {formatMarkupPercentage(markup)} <span className="text-[8px] sm:text-[10px]">🔍</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TableCell>

          {/* MS-DRG Column */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} chargemaster-cell ${codeColClass}`}
            style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : (!isCodeColActive('drg') ? codeColInactiveStyle : undefined)}
          >
            {!codeColsHidden && !isCodeColActive('drg') && <CodeColOverlay />}
            <div className="flex flex-col items-center gap-1">
              <ConditionalTooltip
                isMobile={isMobile}
                content={
                  <div className="space-y-1">
                    <p className="font-semibold">MS-DRG Procedure Coverage</p>
                    <p className="text-sm">{hospital.drg_procedure_count?.toLocaleString() || '0'} MS-DRG procedures were statistically relevant and ranked against national averages out of {hospital.drg_total_available?.toLocaleString() || '0'} total MS-DRG codes listed by this hospital.</p>
                  </div>
                }
              >
                <div className="px-1 sm:px-2 py-1 rounded font-mono text-[9px] sm:text-xs md:text-sm font-bold cursor-help" style={{
                  backgroundColor: '#000000',
                  color: '#c084fc',
                  borderColor: '#c084fc',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}>
                  {formatNumberCompact(hospital.drg_procedure_count)}/{formatNumberCompact(hospital.drg_total_available)}
                </div>
              </ConditionalTooltip>
              <div className="text-xs">
                {(() => {
                  const markup = hospital.drg_avg_markup_percentage;
                  if (markup == null || markup === 0) {
                    return (
                      <div
                        className="markup-box text-[9px] sm:text-xs md:text-sm markup-neutral font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => handleMarkupClick(hospital.hospital_id, hospital.hospital_name, 'drg', 0)}
                      >
                        {markup === 0 ? '0.0%' : '-%'}
                      </div>
                    );
                  }
                  const colorClass = getMarkupColorClass(markup);
                  return (
                    <div
                      className={`markup-box text-[9px] sm:text-xs md:text-sm ${colorClass} font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity`}
                      onClick={() => handleMarkupClick(hospital.hospital_id, hospital.hospital_name, 'drg', markup)}
                    >
                      {formatMarkupPercentage(markup)} <span className="text-[8px] sm:text-[10px]">🔍</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TableCell>

          {/* HCPCS Column (green #4ade80) */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} chargemaster-cell ${codeColClass}`}
            style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : (!isCodeColActive('hcpcs') ? codeColInactiveStyle : undefined)}
          >
            {!codeColsHidden && !isCodeColActive('hcpcs') && <CodeColOverlay />}
            <div className="flex flex-col items-center gap-1">
              <ConditionalTooltip
                isMobile={isMobile}
                content={
                  <div className="space-y-1">
                    <p className="font-semibold">HCPCS Procedure Coverage</p>
                    <p className="text-sm">{hospital.hcpcs_procedure_count?.toLocaleString() || '0'} HCPCS procedures were statistically relevant and ranked against national averages out of {hospital.hcpcs_total_available?.toLocaleString() || '0'} total HCPCS codes listed by this hospital.</p>
                  </div>
                }
              >
                <div className="px-1 sm:px-2 py-1 rounded font-mono text-[9px] sm:text-xs md:text-sm font-bold cursor-help" style={{
                  backgroundColor: '#000000',
                  color: '#4ade80',
                  borderColor: '#4ade80',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}>
                  {formatNumberCompact(hospital.hcpcs_procedure_count)}/{formatNumberCompact(hospital.hcpcs_total_available)}
                </div>
              </ConditionalTooltip>
              <div className="text-xs">
                {(() => {
                  const markup = hospital.hcpcs_avg_markup_percentage;
                  if (markup == null || markup === 0) {
                    return (
                      <div className="markup-box text-[9px] sm:text-xs md:text-sm markup-neutral font-mono font-bold">
                        {markup === 0 ? '0.0%' : '-%'}
                      </div>
                    );
                  }
                  const colorClass = getMarkupColorClass(markup);
                  return (
                    <div
                      className={`markup-box text-[9px] sm:text-xs md:text-sm ${colorClass} font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity`}
                      onClick={() => handleMarkupClick(hospital.hospital_id, hospital.hospital_name, 'hcpcs', markup)}
                    >
                      {formatMarkupPercentage(markup)} <span className="text-[8px] sm:text-[10px]">🔍</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TableCell>

          {/* REV Column - Changed to yellow #facc15 */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} chargemaster-cell ${codeColClass}`}
            style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : (!isCodeColActive('rev') ? codeColInactiveStyle : undefined)}
          >
            {!codeColsHidden && !isCodeColActive('rev') && <CodeColOverlay />}
            <div className="flex flex-col items-center gap-1">
              <ConditionalTooltip
                isMobile={isMobile}
                content={
                  <div className="space-y-1">
                    <p className="font-semibold">Revenue Code Coverage</p>
                    <p className="text-sm">{hospital.rev_procedure_count?.toLocaleString() || '0'} revenue code procedures were statistically relevant and ranked against national averages out of {hospital.rev_total_available?.toLocaleString() || '0'} total revenue codes listed by this hospital.</p>
                  </div>
                }
              >
                <div className="px-1 sm:px-2 py-1 rounded font-mono text-[9px] sm:text-xs md:text-sm font-bold cursor-help" style={{
                  backgroundColor: '#000000',
                  color: '#facc15',
                  borderColor: '#facc15',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}>
                  {formatNumberCompact(hospital.rev_procedure_count)}/{formatNumberCompact(hospital.rev_total_available)}
                </div>
              </ConditionalTooltip>
              <div className="text-xs">
                {(() => {
                  const markup = hospital.rev_avg_markup_percentage;
                  if (markup == null || markup === 0) {
                    return (
                      <div className="markup-box text-[9px] sm:text-xs md:text-sm markup-neutral font-mono font-bold">
                        {markup === 0 ? '0.0%' : '-%'}
                      </div>
                    );
                  }
                  const colorClass = getMarkupColorClass(markup);
                  return (
                    <div
                      className={`markup-box text-[9px] sm:text-xs md:text-sm ${colorClass} font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity`}
                      onClick={() => handleMarkupClick(hospital.hospital_id, hospital.hospital_name, 'revenue', markup)}
                    >
                      {formatMarkupPercentage(markup)} <span className="text-[8px] sm:text-[10px]">🔍</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TableCell>

          {/* NDC Drugs Column - Pink #fb7185 */}
          <TableCell className={`text-center ${firstRowPadding} ${cellBg} chargemaster-cell ${codeColClass}`}
            style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : (!isCodeColActive('ndc') ? codeColInactiveStyle : undefined)}
          >
            {!codeColsHidden && !isCodeColActive('ndc') && <CodeColOverlay />}
            <div className="flex flex-col items-center gap-1">
              <ConditionalTooltip
                isMobile={isMobile}
                content={
                  <div className="space-y-1">
                    <p className="font-semibold">NDC Drug Coverage</p>
                    <p className="text-sm">{hospital.ndc_procedure_count?.toLocaleString() || '0'} NDC drug codes were statistically relevant and ranked against national averages out of {hospital.ndc_total_available?.toLocaleString() || '0'} total NDC codes listed by this hospital.</p>
                  </div>
                }
              >
                <div className="px-1 sm:px-2 py-1 rounded font-mono text-[9px] sm:text-xs md:text-sm font-bold cursor-help" style={{
                  backgroundColor: '#000000',
                  color: '#fb7185',
                  borderColor: '#fb7185',
                  borderWidth: '1px',
                  borderStyle: 'solid'
                }}>
                  {formatNumberCompact(hospital.ndc_procedure_count)}/{formatNumberCompact(hospital.ndc_total_available)}
                </div>
              </ConditionalTooltip>
              <div className="text-xs">
                {(() => {
                  const markup = hospital.drug_avg_markup_percentage; // Use drug_avg_markup_percentage (same as NDC)
                  if (markup == null || markup === 0) {
                    return (
                      <div className="markup-box text-[9px] sm:text-xs md:text-sm markup-neutral font-mono font-bold">
                        {markup === 0 ? '0.0%' : '-%'}
                      </div>
                    );
                  }
                  const colorClass = getMarkupColorClass(markup);
                  return (
                    <div
                      className={`markup-box text-[9px] sm:text-xs md:text-sm ${colorClass} font-mono font-bold cursor-pointer hover:opacity-80 transition-opacity`}
                      onClick={() => handleMarkupClick(hospital.hospital_id, hospital.hospital_name, 'ndc', markup)}
                    >
                      {formatMarkupPercentage(markup)} <span className="text-[8px] sm:text-[10px]">🔍</span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </TableCell>

          {/* Code Breakdown Summary (AHP collapsed view) — hidden in scenario mode */}
          <TableCell
            className={`text-center ${firstRowPadding} ${cellBg}`}
            style={(codeColsHidden && !isScenarioMode) ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
          >
            {codeColsHidden && !isScenarioMode && (
              <div
                className="flex flex-col items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => setCodeColumnsExpanded(true)}
              >
                <Columns3 className="w-4 h-4 text-[#C5A55A]" />
                <span className="text-[9px] sm:text-xs font-mono text-white/70">
                  {formatNumberCompact((hospital.cpt_procedure_count || 0) + (hospital.drg_procedure_count || 0) + (hospital.hcpcs_procedure_count || 0) + (hospital.rev_procedure_count || 0) + (hospital.ndc_procedure_count || 0))} codes
                </span>
                <ChevronRight className="w-3 h-3 text-white/40" />
              </div>
            )}
          </TableCell>

          {!isScenarioMode && (
            <TableCell className={`text-center ${firstRowPadding} ${cellBg} outer-cell`}>
              {(() => {
                const outlierPercentage = hospital.total_procedures > 0
                  ? (hospital.expensive_procedures_count / hospital.total_procedures) * 100
                  : 0;

                let textColor: string;
                let borderColor: string;
                let bgColor: string;
                if (outlierPercentage >= 50) {
                  textColor = '#fca5a5'; borderColor = '#dc2626'; bgColor = 'rgba(220, 38, 38, 0.15)';
                } else if (outlierPercentage >= 25) {
                  textColor = '#fdba74'; borderColor = '#ea580c'; bgColor = 'rgba(234, 88, 12, 0.15)';
                } else if (outlierPercentage >= 15) {
                  textColor = '#fde68a'; borderColor = '#f59e0b'; bgColor = 'rgba(245, 158, 11, 0.12)';
                } else if (outlierPercentage >= 10) {
                  textColor = '#fef08a'; borderColor = '#eab308'; bgColor = 'rgba(234, 179, 8, 0.10)';
                } else if (outlierPercentage >= 5) {
                  textColor = '#d9f99d'; borderColor = '#84cc16'; bgColor = 'rgba(132, 204, 22, 0.10)';
                } else {
                  textColor = '#bbf7d0'; borderColor = '#16a34a'; bgColor = 'rgba(22, 163, 74, 0.12)';
                }

                return (
                  <div className="px-1.5 sm:px-3 py-1 sm:py-2 rounded space-y-0.5 sm:space-y-1" style={{ backgroundColor: bgColor, borderColor, borderWidth: '1px', borderStyle: 'solid' }}>
                    <div className="font-mono font-bold text-[10px] sm:text-sm md:text-base lg:text-lg" style={{ color: textColor }}>
                      {outlierPercentage.toFixed(1)}%
                    </div>
                    <div className="text-[8px] sm:text-xs" style={{ color: textColor }}>
                      ({hospital.expensive_procedures_count}/{hospital.total_procedures?.toLocaleString()})
                    </div>
                  </div>
                );
              })()}
            </TableCell>
          )}

          {!isScenarioMode && !isAhp && (
            <TableCell className={`text-center ${firstRowPadding} ${cellBg}`}>
              <Badge className={`${shame.color} text-white font-bold px-1.5 sm:px-3 py-0.5 sm:py-1 flex items-center gap-0.5 sm:gap-1 w-fit mx-auto text-[8px] sm:text-xs`}>
                <ShameIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                {shame.level}
              </Badge>
            </TableCell>
          )}
        </TableRow>
      );
  }, [rankings, allRankings, isStateFiltered, selectedState, selectedHospitalId, sortBy, maxMarkup, minMarkup, codeColumnsExpanded, isAhp, isDark, cellBg, codeColsHidden, codeColClass, reviewsColumnsExpanded, profileColumnsExpanded, profitsColumnsExpanded, reviewsColsHidden, profileColsHidden, profitsColsHidden, reviewsColClass, profileColClass, profitsColClass, isScenarioMode, scenarioMeta, resolvedHospitalIds]);

  // Show loading state while checking system settings
  if (isLoadingSettings) {
    return (
      <TooltipProvider>
        <Helmet>
          <title>{t('rankings:meta.pageTitle', { brandName: brand.name })}</title>
          <meta name="description" content={t('rankings:loading')} />
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
          <meta name="theme-color" content={brand.colors.background} />
          <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        </Helmet>
        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5 py-8 px-4">
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center py-16">
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="text-muted-foreground">{t('rankings:loading')}</p>
              </div>
            </div>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  // Show maintenance message if live rankings are disabled AND no data available
  // Static JSON cache means data is almost always available even during DB maintenance
  if (isLiveRankingsEnabled !== true && !allRankings?.length) {
    return (
      <TooltipProvider>
        <Helmet>
          <title>{t('rankings:pageTitle')} | {brand.name}</title>
          <meta name="description" content="Hospital rankings are currently being updated. Please check back shortly." />
          <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
          <meta name="theme-color" content={brand.colors.background} />
          <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
        </Helmet>

        <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-primary/5 py-8 px-4">
          <div className="max-w-4xl mx-auto space-y-8">

            {/* Header */}
            <div className="text-center space-y-4">
              <div className="flex items-center justify-center gap-4 mb-6">
                <Button asChild variant="outline">
                  <Link to="/" className="flex items-center gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    {t('common:common.back')}
                  </Link>
                </Button>
              </div>

              <h1 className="text-4xl lg:text-5xl font-bold text-foreground glitch" data-text={isFinancialMode && financialMeta ? financialMeta.h1 : t('rankings:pageTitle')}>
                {isFinancialMode && financialMeta ? financialMeta.h1 : t('rankings:pageTitle')}
              </h1>
            </div>

            {/* Maintenance Message */}
            <Card className="bg-background/80 border-orange-200">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  <div className="flex justify-center">
                    <div className="p-4 bg-orange-100 dark:bg-orange-950/30 rounded-full">
                      <Settings className="w-12 h-12 text-orange-600 dark:text-orange-400" />
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold text-foreground">
                      {t('rankings:pageTitle')} - Maintenance
                    </h2>

                    <Alert className="border-orange-200 bg-orange-50 dark:bg-orange-950/30 max-w-2xl mx-auto">
                      <AlertTriangle className="h-4 w-4 text-orange-600" />
                      <AlertDescription className="text-orange-800 dark:text-orange-200">
                        {maintenanceMessage}
                      </AlertDescription>
                    </Alert>
                  </div>

                  <div className="flex gap-4 justify-center">
                    <Button onClick={() => window.location.reload()} variant="outline" className="gap-2">
                      <RefreshCw className="w-4 h-4" />
                      Refresh Page
                    </Button>

                    <Button asChild variant="default">
                      <Link to="/">
                        Return to Hospital Map
                      </Link>
                    </Button>
                  </div>

                  <div className="pt-4 text-sm text-muted-foreground">
                    <p>
                      Our team is working to ensure you have access to the most accurate and up-to-date hospital pricing data.
                      <br />
                      Thank you for your patience.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </TooltipProvider>
    );
  }

  if (isLoading) {
    const patrioticRed = '#B22234';
    const patrioticBlue = '#002868';
    const patrioticWhite = isDark ? 'rgba(250,249,246,0.12)' : 'rgba(178,34,52,0.06)';
    return (
      <div className="min-h-screen pb-24" style={{ background: ahpC.loadingBg }}>
        <style>{`
          @keyframes rankShimmer {
            0% { background-position: -600px 0; }
            100% { background-position: 600px 0; }
          }
          .rank-shimmer-red {
            background: linear-gradient(90deg, transparent 0%, rgba(178,34,52,0.08) 50%, transparent 100%);
            background-size: 600px 100%;
            animation: rankShimmer 1.8s ease-in-out infinite;
          }
          .rank-shimmer-blue {
            background: linear-gradient(90deg, transparent 0%, rgba(0,40,104,0.07) 50%, transparent 100%);
            background-size: 600px 100%;
            animation: rankShimmer 1.8s ease-in-out infinite;
          }
          .rank-shimmer-neutral {
            background: linear-gradient(90deg, transparent 0%, rgba(128,128,128,0.07) 50%, transparent 100%);
            background-size: 600px 100%;
            animation: rankShimmer 1.8s ease-in-out infinite;
          }
        `}</style>
        <div className="mx-auto" style={{ maxWidth: 'calc(100% - 40px)' }}>
          <div className={`rounded-xl border overflow-hidden ${ahpC.borderCls}`} style={{ background: ahpC.loadingCardBg, backdropFilter: 'blur(8px)' }}>
            {/* Patriotic top stripe */}
            <div className="flex h-1.5 w-full overflow-hidden">
              {Array.from({ length: 13 }).map((_, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: i % 2 === 0 ? patrioticRed : (isDark ? 'rgba(250,249,246,0.15)' : '#fff') }} />
              ))}
            </div>
            {/* Header skeleton */}
            <div className={`px-4 py-4 lg:px-6 lg:py-6 border-b ${ahpC.borderCls}`}>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-shrink-0 space-y-3">
                  <div className="h-10 rounded-lg w-80 rank-shimmer-blue" style={{ background: ahpC.skBgStrong }} />
                  <div className="h-3 rounded w-64 rank-shimmer-neutral" style={{ background: ahpC.skBg }} />
                  <div className="h-3 rounded w-48 rank-shimmer-neutral" style={{ background: ahpC.skBg }} />
                </div>
                <div className="flex-1 lg:max-w-lg space-y-3">
                  <div className="h-10 rounded-lg rank-shimmer-neutral" style={{ background: ahpC.skBg }} />
                  <div className="h-10 rounded-lg rank-shimmer-neutral" style={{ background: ahpC.skBg }} />
                  <div className="flex gap-2">
                    <div className="h-9 rounded w-40 rank-shimmer-neutral" style={{ background: ahpC.skBg }} />
                    <div className="h-9 rounded w-32 rank-shimmer-neutral" style={{ background: ahpC.skBg }} />
                  </div>
                </div>
              </div>
            </div>
            {/* Legend row skeleton */}
            <div className={`px-4 py-3 border-b ${ahpC.borderCls} flex gap-3`}>
              {[80, 72, 60, 68].map((w, i) => (
                <div key={i} className="h-6 rounded-full rank-shimmer-neutral" style={{ width: w, background: ahpC.skBg, animationDelay: `${i * 120}ms` }} />
              ))}
            </div>
            {/* Column headers */}
            <div className={`grid grid-cols-12 gap-3 px-4 py-3 border-b ${ahpC.borderCls}`}>
              {[1, 3, 2, 2, 1, 1, 1, 1].map((span, i) => (
                <div key={i} className={`col-span-${span} h-4 rounded rank-shimmer-neutral`} style={{ background: ahpC.skBg, animationDelay: `${i * 80}ms` }} />
              ))}
            </div>
            {/* Table rows — alternating patriotic shimmer colors */}
            {Array.from({ length: 14 }).map((_, rowIndex) => (
              <div key={rowIndex} className={`grid grid-cols-12 gap-3 px-4 py-5 border-b ${ahpC.borderCls}`} style={{ borderColor: ahpC.borderAt(0.05) }}>
                <div className={`col-span-1 h-7 rounded ${rowIndex % 3 === 0 ? 'rank-shimmer-blue' : 'rank-shimmer-neutral'}`} style={{ background: ahpC.skBgStrong, animationDelay: `${rowIndex * 40}ms` }} />
                <div className="col-span-3 h-7 rounded rank-shimmer-neutral" style={{ background: ahpC.skBg, animationDelay: `${rowIndex * 40 + 60}ms` }} />
                <div className={`col-span-2 h-7 rounded ${rowIndex % 3 === 1 ? 'rank-shimmer-red' : 'rank-shimmer-neutral'}`} style={{ background: ahpC.skBgRed, animationDelay: `${rowIndex * 40 + 120}ms` }} />
                <div className="col-span-1 h-6 rounded rank-shimmer-neutral" style={{ background: ahpC.skBg, animationDelay: `${rowIndex * 40 + 180}ms` }} />
                <div className="col-span-1 h-7 rounded rank-shimmer-neutral" style={{ background: ahpC.skBg, animationDelay: `${rowIndex * 40 + 240}ms` }} />
                <div className="col-span-1 h-7 rounded rank-shimmer-neutral" style={{ background: ahpC.skBg, animationDelay: `${rowIndex * 40 + 300}ms` }} />
                <div className={`col-span-2 h-7 rounded ${rowIndex % 3 === 2 ? 'rank-shimmer-red' : 'rank-shimmer-neutral'}`} style={{ background: ahpC.skBg, animationDelay: `${rowIndex * 40 + 360}ms` }} />
                <div className="col-span-1 h-7 rounded-full rank-shimmer-neutral" style={{ background: ahpC.skBgRed, animationDelay: `${rowIndex * 40 + 420}ms` }} />
              </div>
            ))}
            {/* Patriotic loader animation */}
            <PatrioticLoader
              message={isScenarioMode ? 'Loading Scenario Rankings...' : 'Loading Hospital Rankings...'}
              subMessage="Analyzing pricing data across 9,500+ hospitals"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <Helmet>
        <title>{isFinancialMode && financialMeta
          ? `${financialMeta.title}${stateParam ? ` — ${stateParam.toUpperCase()}` : ''} | ${brand.name}`
          : isScenarioMode && scenarioMeta
          ? `${scenarioMeta.name} Rankings | ${brand.name}`
          : t('rankings:meta.pageTitle', { brandName: brand.name })
        }</title>
        <meta name="robots" content="index,follow" />
        <meta name="description" content={isFinancialMode && financialMeta
          ? `${financialMeta.description}${stateParam ? ` Filtered to ${stateParam.toUpperCase()}.` : ''}`
          : isScenarioMode && scenarioMeta
          ? `Rankings of all US hospitals by price for ${scenarioMeta.name}. Compare costs across 9,500+ hospitals and find the best value near you.`
          : t('rankings:meta.description')
        } />
        <meta name="keywords" content={isFinancialMode && financialMeta
          ? financialMeta.keywords
          : isScenarioMode && scenarioMeta
          ? `${scenarioMeta.name} hospital prices, ${scenarioMeta.name} cost comparison, hospital rankings, healthcare price transparency`
          : "hospital rankings, healthcare prices, medical costs, price transparency, hospital price comparison"
        } />
        {/* Canonical must include state when present — sitemap submits 670K
            URLs in `/hospital-rankings/{state}` and `/hospital-rankings/{state}?scenario={id}`
            forms, but prior code emitted `/hospital-rankings` and `/hospital-rankings?scenario={id}`
            (state dropped), forcing Google's JS render pass to treat every
            state page as a duplicate of the bare ranking page. Audited
            2026-06-14 during the migration deep-dive — likely a contributor
            to the month-long indexation freeze. The form below mirrors bot
            SSR's canonicalPath in functions/lib/pages/rankings.ts. */}
        <link rel="canonical" href={isFinancialMode && normalizedDimension
          ? `${brand.primaryDomain}/hospital-rankings/financial/${normalizedDimension}${stateParam ? `/${stateParam.toLowerCase()}` : ''}`
          : isScenarioMode && scenarioId && stateParam
          ? `${brand.primaryDomain}/hospital-rankings/${stateParam.toLowerCase()}?scenario=${scenarioId}`
          : isScenarioMode && scenarioId
          ? `${brand.primaryDomain}/hospital-rankings?scenario=${scenarioId}`
          : stateParam
          ? `${brand.primaryDomain}/hospital-rankings/${stateParam.toLowerCase()}`
          : `${brand.primaryDomain}/hospital-rankings`
        } />
        <meta property="og:title" content={isFinancialMode && financialMeta
          ? `${financialMeta.title}${stateParam ? ` — ${stateParam.toUpperCase()}` : ''} | ${brand.name}`
          : isScenarioMode && scenarioMeta ? `${scenarioMeta.name} Rankings | ${brand.name}` : t('rankings:meta.ogTitle')} />
        <meta property="og:description" content={isFinancialMode && financialMeta
          ? financialMeta.description
          : isScenarioMode && scenarioMeta
          ? `Rankings of all US hospitals by price for ${scenarioMeta.name}.`
          : t('rankings:meta.description')
        } />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={isFinancialMode && normalizedDimension
          ? `${brand.primaryDomain}/hospital-rankings/financial/${normalizedDimension}${stateParam ? `/${stateParam.toLowerCase()}` : ''}`
          : isScenarioMode && scenarioId && stateParam
          ? `${brand.primaryDomain}/hospital-rankings/${stateParam.toLowerCase()}?scenario=${scenarioId}`
          : isScenarioMode && scenarioId
          ? `${brand.primaryDomain}/hospital-rankings?scenario=${scenarioId}`
          : stateParam
          ? `${brand.primaryDomain}/hospital-rankings/${stateParam.toLowerCase()}`
          : `${brand.primaryDomain}/hospital-rankings`
        } />
        <meta property="og:image" content={isAhp ? 'https://americanhospitalprices.com/consumer/og-rankings.png' : brand.social.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={brand.social.twitter} />
        <meta name="twitter:title" content={isFinancialMode && financialMeta
          ? `${financialMeta.title} | ${brand.name}`
          : isScenarioMode && scenarioMeta ? `${scenarioMeta.name} Rankings | ${brand.name}` : t('rankings:meta.ogTitle')} />
        <meta name="twitter:description" content={isFinancialMode && financialMeta ? financialMeta.description : t('rankings:meta.description')} />
        <meta name="twitter:image" content={isAhp ? 'https://americanhospitalprices.com/consumer/og-rankings.png' : brand.social.ogImage} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          "name": isScenarioMode && scenarioMeta ? `${scenarioMeta.name} Hospital Price Rankings` : t('rankings:meta.ogTitle'),
          "description": t('rankings:meta.description'),
          "url": `${brand.primaryDomain}/hospital-rankings`,
          "creator": { "@type": "Organization", "name": brand.name, "url": brand.primaryDomain },
          "temporalCoverage": "2024/..",
          "spatialCoverage": "United States",
          "variableMeasured": "Hospital price markup percentage"
        })}</script>
        {/* Viewport meta for app-like experience - locked zoom for WebViewGold */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover" />
        {/* Theme color for mobile browsers and WebViewGold - uses brand background color */}
        <meta name="theme-color" content={brand.colors.background} />
        <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      </Helmet>

      <div className={`min-h-screen px-2 lg:px-4 pb-24 ${isAhp ? 'relative overflow-hidden' : 'py-2 lg:py-8 bg-gradient-to-br from-background via-background/95 to-primary/5'}`}
        style={isAhp ? { backgroundColor: ahpC.pageBg } : undefined}
      >
        {/* AHP: Flag video background with overlay */}
        {isAhp && (
          <>
            <video
              autoPlay
              loop
              muted
              playsInline
              className="fixed inset-0 w-full h-full object-cover pointer-events-none"
              style={{ opacity: ahpC.videoOpacity, zIndex: 0 }}
            >
              <source src="/consumer/hero-flag-faded.mp4" type="video/mp4" />
            </video>
            <div className="fixed inset-0 pointer-events-none" style={{
              background: ahpC.videoOverlay,
              zIndex: 0,
            }} />
          </>
        )}
        <div className={`space-y-2 lg:space-y-8 ${isAhp ? 'relative' : ''}`} style={{ maxWidth: isMobile ? '100%' : 'calc(100% - 40px)', marginLeft: 0, marginRight: 'auto', marginTop: 15, maxHeight: 'calc(100vh - 160px)', overflow: 'hidden', ...(isAhp ? { zIndex: 1 } : {}) }}>

          {/* Rankings Table */}
          <Card className={isAhp ? ahpC.borderCls : 'bg-background/80 border-border/50'} style={isAhp ? { backgroundColor: ahpC.cardBg(0.75), backdropFilter: 'blur(8px)' } : undefined}>
            <CardHeader
              className={`space-y-1 sm:space-y-1.5 ${isTableFullscreen ? 'lg:space-y-1 lg:py-2' : 'lg:space-y-4 lg:py-6'} py-1.5 sm:py-2 px-2 sm:px-3 lg:px-6`}
              style={{ paddingBottom: 0, overflow: 'visible' }}
            >
              {/* Breadcrumb: Rankings > Category > Family > Scenario — back navigation */}
              {isScenarioMode && searchParams.get('browseCategory') && (() => {
                const cat = searchParams.get('browseCategory')!;
                const expandFamily = searchParams.get('expandFamily');
                const familyName = activeScenario?.name?.replace(/\s*[-—–].*$/, '').replace(/\s*\(.*\)$/, '') || '';
                return (
                  <div className="flex items-center gap-1 text-[10px] sm:text-xs pb-1" style={{ color: ahpC.textAt(0.4) }}>
                    <button
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        params.delete('scenario');
                        params.delete('browseCategory');
                        params.delete('expandFamily');
                        navigate(`/hospital-rankings/${selectedState.toLowerCase()}?${params.toString()}`);
                      }}
                      className="hover:underline transition-colors"
                      style={{ color: ahpC.textAt(0.5) }}
                    >
                      Rankings
                    </button>
                    <ChevronRight className="w-3 h-3" style={{ color: ahpC.textAt(0.2) }} />
                    <button
                      onClick={() => {
                        const params = new URLSearchParams(searchParams);
                        params.delete('scenario');
                        navigate(`/hospital-rankings/${selectedState.toLowerCase()}?${params.toString()}`);
                      }}
                      className="hover:underline transition-colors"
                      style={{ color: ahpC.textAt(0.5) }}
                    >
                      {cat.replace(/ & .*/, '')}
                    </button>
                    {expandFamily && (
                      <>
                        <ChevronRight className="w-3 h-3" style={{ color: ahpC.textAt(0.2) }} />
                        <button
                          onClick={() => {
                            const params = new URLSearchParams(searchParams);
                            params.delete('scenario');
                            navigate(`/hospital-rankings/${selectedState.toLowerCase()}?${params.toString()}`);
                          }}
                          className="hover:underline transition-colors"
                          style={{ color: ahpC.textAt(0.5) }}
                        >
                          {familyName || 'Family'}
                        </button>
                      </>
                    )}
                    <ChevronRight className="w-3 h-3" style={{ color: ahpC.textAt(0.2) }} />
                    <span className="font-medium truncate" style={{ color: ahpC.textAt(0.7) }}>
                      {activeScenario?.name || 'Scenario'}
                    </span>
                  </div>
                );
              })()}
              {/* Scenario dashboard grid CSS — activates at lg+ in scenario mode */}
              {isScenarioMode && (
                <style>{`
                  @media (min-width: 1024px) {
                    .scenario-dashboard-header {
                      display: grid !important;
                      grid-template-columns: 1fr 1fr minmax(260px, auto);
                      grid-template-rows: auto auto;
                      gap: 12px;
                      align-items: start;
                    }
                    .scenario-dashboard-header .toolbar-row {
                      grid-column: 1 / -1;
                    }
                  }
                `}</style>
              )}
              {/* Title and Controls Row — grid in scenario mode (lg+), flex otherwise */}
              <div className={isScenarioMode
                ? "scenario-dashboard-header flex flex-col gap-1 sm:gap-2"
                : "flex flex-col lg:flex-row lg:items-start gap-1 sm:gap-2 lg:gap-4 flex-wrap"
              }>
                {/* Dashboard cards — scenario mode only, visible at lg+ */}
                {isScenarioMode && (() => {
                  const dashTheme: DashboardTheme = {
                    cardBg: ahpC.cardBg, borderCls: ahpC.borderCls, borderAt: ahpC.borderAt,
                    text: ahpC.text, textAt: ahpC.textAt, isAhp, isDark,
                  };
                  return (
                    <>
                      <div className="hidden lg:block">
                        <DashboardScenarioCard theme={dashTheme} scenarioMeta={scenarioMeta ?? null} activeScenario={activeScenario} scenarioId={scenarioId!} scenarioDetail={scenarioDetail} onViewVariants={activeFamily.length > 1 ? () => setShowFamilyModal(true) : undefined} familySize={activeFamily.length} />
                      </div>
                      <div className="hidden lg:block">
                        <DashboardProcedureCodes theme={dashTheme} scenarioDetail={scenarioDetail} />
                      </div>
                    </>
                  );
                })()}
                {/* Left: Title — flex-1 so it takes remaining space without pushing others */}
                {/* Hidden at lg+ in scenario mode (replaced by dashboard cards) */}
                {/* Also hidden when isTableFullscreen on desktop — frees vertical space for the table */}
                <div className={`flex-1 min-w-0 overflow-hidden ${isScenarioMode ? 'lg:hidden' : ''} ${isTableFullscreen ? 'lg:hidden' : ''}`}>
                  <div className="space-y-0.5 sm:space-y-1.5">
                    {/* Mobile: Title + Sort/Filter inline; Desktop: Title only */}
                    <div className="flex items-center gap-1 sm:gap-2">
                      <h1
                        className={`text-base sm:text-2xl lg:text-4xl font-bold ${isFinancialMode ? 'min-w-0' : 'whitespace-nowrap shrink-0'} ${isAhp ? '' : 'text-foreground glitch'}`}
                        style={isAhp ? { color: ahpC.text, fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, letterSpacing: '-1px' } : undefined}
                        data-text={isScenarioMode ? 'Scenario Rankings' : (isFinancialMode && financialMeta ? financialMeta.h1 : t('rankings:pageTitle'))}
                      >
                        {/* Financial leaderboards get their dedicated SEO h1 —
                            the FINANCIAL_DIMENSIONS h1 field was previously unused. */}
                        {isScenarioMode ? 'Scenario Rankings' : (isFinancialMode && financialMeta ? financialMeta.h1 : t('rankings:pageTitle'))}
                      </h1>
                      {/* Info tooltip - explains outlier definition */}
                      <ConditionalTooltip
                      isMobile={isMobile}
                      content={
                        <div className="space-y-1">
                          <p className="font-semibold">Outlier Definition</p>
                          <p className="text-sm">Procedures with prices 50%+ above national average are considered outliers.</p>
                        </div>
                      }
                    >
                      <div className="cursor-help shrink-0">
                        <Info className="w-3.5 h-3.5 sm:w-5 sm:h-5 text-orange-400" />
                      </div>
                    </ConditionalTooltip>

                      {/* Mobile inline sort/filter — saves a full row of vertical space */}
                      {isMobile && (
                        <div className="flex items-center gap-1 flex-1 min-w-0 ml-1">
                          <Select value={sortBy} onValueChange={(value: typeof sortBy) => {
                            setSortBy(value);
                            setCurrentPage(1);
                            trackRankingsFilter('sort', value);
                          }}>
                            <SelectTrigger aria-label="Sort rankings by" className="h-7 text-[10px] flex-1 min-w-0 px-1.5">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent align="end" className="z-[500]">
                              {isScenarioMode && <SelectItem value="scenario_price">Price</SelectItem>}
                              <SelectItem value="markup">Markup</SelectItem>
                              <SelectItem value="shame">Outliers</SelectItem>
                              <SelectItem value="google_rating">Google</SelectItem>
                              <SelectItem value="cms_rating">CMS</SelectItem>
                              <SelectItem value="bed_count">Beds</SelectItem>
                            </SelectContent>
                          </Select>
                          <Select value={filterCodeType} onValueChange={(value: 'all' | 'cpt' | 'drg' | 'rev') => {
                            setFilterCodeType(value);
                            setFilterCode("");
                            setCurrentPage(1);
                          }}>
                            <SelectTrigger aria-label="Filter by state" className="h-7 text-[10px] w-[62px] shrink-0 px-1.5">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent align="end" className="z-[500]">
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="cpt">CPT</SelectItem>
                              <SelectItem value="drg">DRG</SelectItem>
                              <SelectItem value="rev">REV</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                    {isScenarioMode && scenarioMeta ? (
                      <>
                        {/* Clamp scenario description — 1 line mobile, 2 lines desktop */}
                        <p className="leading-snug text-[10px] sm:text-xs md:text-sm overflow-hidden" style={{
                          display: '-webkit-box',
                          WebkitLineClamp: isMobile ? 1 : 2,
                          WebkitBoxOrient: 'vertical',
                          ...(isAhp ? { fontFamily: "'Instrument Sans', sans-serif", color: ahpC.textAt(0.65) } : { color: 'rgba(255,255,255,0.7)' }),
                        } as React.CSSProperties}>
                          {scenarioMeta.name}{scenarioMeta.description ? ` — ${scenarioMeta.description}` : ''}
                        </p>
                        <div className="flex items-center gap-2 sm:gap-3 flex-wrap text-[10px] sm:text-xs">
                          <span style={{ color: ahpC.textAt(0.4) }}>
                            Avg: <span className="font-mono font-bold text-green-400">${scenarioMeta.nationalTotal?.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                          </span>
                          <span style={{ color: ahpC.textAt(0.3) }}>·</span>
                          <span className="flex items-center gap-1" style={{ color: ahpC.textAt(0.4) }}>
                            <ClipboardList className="w-3 h-3" />
                            {scenarioMeta.procedureCount || activeScenario?.total_procedures || '?'} procedures
                          </span>
                          <span style={{ color: ahpC.textAt(0.3) }}>·</span>
                          <span style={{ color: ahpC.textAt(0.4) }}>
                            {rankings?.length || 0} hospitals{selectedState !== 'USA' && ` in ${selectedState}`}
                          </span>
                          {activeScenario?.slug && (
                            <Link
                              to={`/scenarios/${scenarioId}/${activeScenario.slug}`}
                              className="flex items-center gap-1 font-medium transition-colors"
                              style={{ color: isAhp ? '#C5A55A' : '#00ff41' }}
                            >
                              View Scenario <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </>
                    ) : isAhp ? (
                      <>
                        <p className="hidden sm:block leading-snug text-xs sm:text-sm lg:text-base" style={{ fontFamily: "'Instrument Sans', sans-serif", color: ahpC.textAt(0.65) }}>
                          {t('rankings:hero.grossChargesSubtitle', { defaultValue: 'Compare hospital gross charges across America — the base sticker prices for every procedure.' })}
                        </p>
                        <p className="text-[10px] sm:text-xs md:text-sm flex items-center gap-1 sm:gap-2 flex-wrap" style={{ color: ahpC.textAt(0.4) }}>
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={
                              <div className="space-y-1.5 max-w-xs">
                                <p className="font-semibold">Ranking criteria</p>
                                <p>A hospital is ranked when it publishes <strong>50+ comparable procedure codes</strong> across CPT, HCPCS, MS-DRG, Revenue, Supply, and NDC.</p>
                                <p>A national benchmark for any code requires <strong>3+ hospitals</strong> publishing that code, so we&apos;re always comparing against real peer pricing.</p>
                                <p className="pt-1"><Link to="/data-methodology" className="underline">Full methodology →</Link></p>
                              </div>
                            }
                          >
                            <span className="cursor-help underline decoration-dotted decoration-1 underline-offset-2"><span className="font-semibold" style={{ color: ahpC.text }}>{(rankings?.length || 0).toLocaleString()}</span> ranked of <span className="font-semibold" style={{ color: ahpC.text }}>9,500+</span> indexed{selectedState !== 'USA' && ` (filtered to ${selectedState})`}</span>
                          </ConditionalTooltip>
                          <Link to="/state-comparison" className="text-[#C5A55A] hover:underline font-medium">States →</Link>
                          <Link to="/data-sources" className="text-[#C5A55A] hover:underline font-medium"><span className="sm:hidden">Sources →</span><span className="hidden sm:inline">All 9,500+ hospitals + chargemaster files →</span></Link>
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="hidden sm:block text-muted-foreground italic leading-snug">
                          <span className="text-xs sm:text-sm lg:text-base">The <span className="font-bold text-orange-400 not-italic">RAW</span> & <span className="font-bold text-orange-400 not-italic">GROSS CHARGE</span> base sticker prices hospitals set for every procedure</span>
                        </p>
                        <CardDescription className="text-white text-[10px] sm:text-xs md:text-sm flex items-center gap-1 sm:gap-2 flex-wrap">
                          <span><span className="font-semibold text-white">{(rankings?.length || 0).toLocaleString()}</span> ranked of <span className="font-semibold text-white">9,500+</span> indexed{selectedState !== 'USA' && ` (filtered to ${selectedState})`}</span>
                          <Link to="/state-comparison" className="text-[#00ff41] hover:underline font-medium">States →</Link>
                          <Link to="/data-sources" className="text-[#00ff41] hover:underline font-medium">All chargemaster files →</Link>
                        </CardDescription>
                      </>
                    )}
                  </div>
                </div>

                {/* Dignity Guide Box — matches map card styling and width.
                    Hidden when isTableFullscreen so the table can claim full vertical space.
                    AHP-only: the box is Dignity by name and portrait, and she is
                    AHP's mascot — it was previously gated on view state alone, so
                    it surfaced on Luigi's and AntiGreed too. */}
                {isAhp && !isScenarioMode && !scenarioBrowserExpanded && !isTableFullscreen && (
                  <div className="hidden lg:block flex-shrink-0">
                    <div
                      className={`backdrop-blur-sm rounded-lg shadow-lg border overflow-hidden ${isAhp ? ahpC.borderCls : 'bg-background/95 border-border'}`}
                      style={isAhp ? { backgroundColor: ahpC.cardBg(0.8), width: '220px' } : { width: '220px' }}
                    >
                      {/* Header — links to /about/dignity/ */}
                      <Link
                        to="/about/dignity"
                        className="flex items-center gap-1.5 px-3 py-1.5 hover:opacity-80 transition-opacity"
                        style={{ borderBottom: `1px solid ${ahpC.borderAt(0.1)}` }}
                      >
                        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: '#C5A55A' }}>
                          Dignity
                        </span>
                        <span className="text-[9px]" style={{ color: ahpC.textAt(0.35) }}>— Your Guide</span>
                        <ExternalLink className="w-2.5 h-2.5 ml-auto" style={{ color: ahpC.textAt(0.25) }} />
                      </Link>
                      {/* PNG-only Dignity portrait — the 3D <model-viewer> alternative
                          was dropping ~30 fps on the rankings page because its internal
                          rAF render loop runs continuously and each frame stalls behind
                          this page's layout/paint work (~350ms long tasks during scroll).
                          Scenarios page still uses the 3D model where it's the main focus. */}
                      <div className="relative" style={{ height: '150px' }}>
                        <img
                          src="/consumer/dignity-transparent.webp"
                          alt="Dignity — Your Healthcare Guide"
                          className="w-full h-full object-contain p-2"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>
                      {/* Audio play bar — scrolls to narration player */}
                      <button
                        type="button"
                        className="flex items-center gap-2 px-2 py-1.5 w-full cursor-pointer hover:opacity-90 transition-opacity"
                        style={{ backgroundColor: isDark ? 'rgba(197,165,90,0.12)' : 'rgba(178,34,52,0.08)', borderTop: `1px solid ${ahpC.borderAt(0.1)}` }}
                        onClick={(e) => {
                          e.preventDefault();
                          // Find and click the narration player's play button
                          const playBtn = document.querySelector('.dignity-play-pulse') as HTMLElement;
                          if (playBtn) { playBtn.scrollIntoView({ behavior: 'smooth', block: 'center' }); setTimeout(() => playBtn.click(), 500); }
                        }}
                      >
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#C5A55A' }}>
                          <div className="w-0 h-0 border-t-[4px] border-t-transparent border-b-[4px] border-b-transparent border-l-[7px] border-l-[#0a1628] ml-0.5" />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-[9px] font-semibold leading-tight" style={{ color: ahpC.text }}>Guide Me</div>
                          <div className="text-[8px] leading-tight" style={{ color: ahpC.textAt(0.4) }}>How to read rankings</div>
                        </div>
                        {/* Waveform decoration */}
                        <div className="flex items-end gap-[2px] h-3 flex-shrink-0">
                          {[3, 6, 4, 8, 5, 7, 3, 5].map((h, i) => (
                            <div key={i} className="w-[2px] rounded-full" style={{ height: `${h}px`, backgroundColor: ahpC.textAt(0.2) }} />
                          ))}
                        </div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Rank By Code Type — shown when no scenario selected.
                    Hidden when isTableFullscreen so the table can claim full vertical space. */}
                {!isScenarioMode && !scenarioBrowserExpanded && !isTableFullscreen && (
                  <div className="hidden lg:flex flex-col gap-1 flex-shrink-0 min-w-[140px]">
                    <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: ahpC.textAt(0.35) }}>
                      Rank by Code Type & Scenarios
                    </span>
                    <div className="flex flex-col gap-0.5">
                      {([
                        { key: 'overall', label: '📊 Overall', color: '#C5A55A', excluded: false, tooltip: 'Weighted average across CPT, HCPCS, MS-DRG, Revenue, Drug/NDC, and Supply. Each code type is weighted by the number of comparable procedures the hospital reports.' },
                        { key: 'cpt', label: '🏥 CPT®', color: '#60a5fa', excluded: false, tooltip: 'CPT® (Current Procedural Terminology) — standardized codes for medical procedures and services. CPT® is a registered trademark of the American Medical Association.' },
                        { key: 'hcpcs', label: '💊 HCPCS', color: '#4ade80', excluded: false, tooltip: 'Healthcare Common Procedure Coding System — codes for supplies, drugs, and services not covered by CPT® (injections, DME, ambulance).' },
                        { key: 'drg', label: '🏨 MS-DRG', color: '#c084fc', excluded: false, tooltip: 'Medicare Severity Diagnosis Related Groups — inpatient episode bundles that group related diagnoses and procedures into a single payment category.' },
                        { key: 'rev', label: '📋 Revenue', color: '#fbbf24', excluded: false, tooltip: 'Revenue codes — department-level billing categories (OR services, ER visits, room & board). Bucketed by setting × unit × dosage so they compare apples-to-apples across hospitals.' },
                        { key: 'ndc', label: '💉 Drug/NDC', color: '#f87171', excluded: true, tooltip: 'National Drug Codes — individual drug products by manufacturer and package. Excluded from overall — per-tablet vs per-vial unit inconsistencies.' },
                        { key: 'supply', label: '🩹 Supply', color: '#fb923c', excluded: true, tooltip: 'Supply codes — medical supplies and devices. Excluded from overall — inconsistent unit pricing across hospitals.' },
                      ] as const).map(({ key, label, color, excluded, tooltip }) => (
                        <Tooltip key={key}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setRankByCodeType(key);
                                if (sortBy !== 'markup') setSortBy('markup');
                                const statePart = selectedState === 'USA' ? 'usa' : selectedState.toLowerCase();
                                if (key === 'overall') {
                                  navigate(`/hospital-rankings/${statePart}`, { replace: true });
                                } else {
                                  navigate(`/hospital-rankings/${statePart}/${key}`, { replace: true });
                                }
                              }}
                              className="flex items-center gap-1.5 px-2 py-1 rounded text-left transition-colors text-xs"
                              style={{
                                backgroundColor: rankByCodeType === key ? `${color}22` : 'transparent',
                                color: rankByCodeType === key ? color : ahpC.textAt(excluded ? 0.35 : 0.55),
                                fontWeight: rankByCodeType === key ? 600 : 400,
                                borderLeft: `3px solid ${rankByCodeType === key ? color : 'transparent'}`,
                                opacity: excluded && rankByCodeType !== key ? 0.6 : 1,
                              }}
                            >
                              <span>{label}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs z-[100]">
                            <p className="text-sm">{tooltip}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                    <div className="text-[8px] mt-1 leading-tight" style={{ color: ahpC.textAt(0.3) }}>
                      Drug/NDC and Supply excluded from Overall
                    </div>
                  </div>
                )}

                {/* Scenario Category Browser — between Rank By and Map */}
                {!isScenarioMode && (() => {
                  // Map DB category names → icon + color
                  const CAT_META: Record<string, { icon: any; color: string }> = {
                    'Emergency & Critical Care': { icon: Flame, color: '#B22234' },
                    'Orthopedic & Joint': { icon: Bone, color: '#2563EB' },
                    'Spine & Back Pain': { icon: Activity, color: '#7C3AED' },
                    'General Surgery': { icon: Scissors, color: '#0891B2' },
                    "Women's Health & OB/GYN": { icon: Baby, color: '#DB2777' },
                    'Heart & Cardiovascular': { icon: Heart, color: '#DC2626' },
                    'Cancer & Oncology': { icon: Ribbon, color: '#9333EA' },
                    'Infectious Disease': { icon: Shield, color: '#059669' },
                    'Diabetes & Endocrinology': { icon: Pill, color: '#D97706' },
                    'Pulmonology': { icon: Wind, color: '#0D9488' },
                    'Transplant': { icon: Replace, color: '#4F46E5' },
                    'Mental Health': { icon: Brain, color: '#6366F1' },
                    'Neurological': { icon: Stethoscope, color: '#8B5CF6' },
                    'Digestive & GI': { icon: Activity, color: '#EA580C' },
                    'Urology': { icon: Microscope, color: '#0284C7' },
                    'Pediatric': { icon: Baby, color: '#EC4899' },
                    'Preventive & Screening': { icon: Eye, color: '#16A34A' },
                    'Other Medical Scenarios': { icon: Stethoscope, color: '#6B7280' },
                  };
                  const allCats = [...Object.keys(familyGroups), ...Object.keys(standaloneByCategory)]
                    .filter((v, i, a) => a.indexOf(v) === i)
                    .sort((a, b) => {
                      const aCount = (familyGroups[a]?.length || 0) + (standaloneByCategory[a]?.length || 0);
                      const bCount = (familyGroups[b]?.length || 0) + (standaloneByCategory[b]?.length || 0);
                      return bCount - aCount;
                    });

                  const selectScenario = (id: string) => {
                    const params = new URLSearchParams(searchParams);
                    params.set('scenario', id);
                    // Preserve browse state so back button can restore it
                    if (browseCategory) params.set('browseCategory', browseCategory);
                    const expandFamily = searchParams.get('expandFamily');
                    if (expandFamily) params.set('expandFamily', expandFamily);
                    navigate(`/hospital-rankings/${selectedState.toLowerCase()}?${params.toString()}`);
                  };

                  // isTableFullscreen (up-arrow collapses the top ribbon for
                  // a full-height table) also hides this scenarios browse
                  // panel — otherwise the category marquee + search float
                  // above the table the user maximized.
                  return (
                  <div className={`${isTableFullscreen ? 'hidden' : 'hidden lg:flex'} flex-col flex-1 min-w-0 ${scenarioBrowserExpanded ? '' : 'max-w-[420px]'}`}>
                    <div
                      className={`backdrop-blur-sm rounded-lg shadow-lg border h-full flex flex-col ${isAhp ? ahpC.borderCls : 'bg-background/95 border-border'}`}
                      style={isAhp ? { backgroundColor: ahpC.cardBg(0.8), overflow: 'visible' } : { overflow: 'visible' }}
                    >
                      <div className="relative px-1.5 py-1" style={{ borderBottom: `1px solid ${ahpC.borderAt(0.1)}` }}>
                        <div className="relative flex items-center">
                          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none" style={{ color: ahpC.textAt(0.3) }} />
                          <input
                            type="text"
                            placeholder="Search scenarios..."
                            value={browseSearch}
                            onChange={(e) => {
                              setBrowseSearch(e.target.value);
                              setShowBrowseDropdown(true);
                              // Also filter the category list via scenarioSearch
                              setScenarioSearch(e.target.value);
                            }}
                            onFocus={() => setShowBrowseDropdown(true)}
                            onBlur={() => setTimeout(() => setShowBrowseDropdown(false), 200)}
                            className="w-full pl-7 pr-12 py-1 text-[10px] rounded outline-none"
                            style={{
                              backgroundColor: ahpC.borderAt(0.05),
                              color: ahpC.textAt(0.7),
                              border: `1px solid ${ahpC.borderAt(0.1)}`,
                            }}
                          />
                          <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
                            {browseSearch && (
                              <button
                                className="p-0.5 rounded hover:opacity-70"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => { setBrowseSearch(''); setScenarioSearch(''); setShowBrowseDropdown(false); }}
                                style={{ color: ahpC.textAt(0.4) }}
                              >
                                <X className="w-2.5 h-2.5" />
                              </button>
                            )}
                            <button
                              className="p-0.5 rounded hover:opacity-70 transition-colors"
                              onClick={() => setScenarioBrowserExpanded(!scenarioBrowserExpanded)}
                              title={scenarioBrowserExpanded ? 'Collapse' : 'Expand scenario browser'}
                              style={{ color: scenarioBrowserExpanded ? '#C5A55A' : ahpC.textAt(0.3) }}
                            >
                              {scenarioBrowserExpanded
                                ? <Columns3 className="w-3 h-3" />
                                : <Maximize2 className="w-3 h-3" />
                              }
                            </button>
                          </div>
                        </div>
                        {/* Typeahead dropdown */}
                        {showBrowseDropdown && browseSearch.length > 0 && (() => {
                          const q = browseSearch.toLowerCase();
                          // Score matches: name-starts-with > name-contains > category-match
                          const matches: { id: string; name: string; category: string; severity_level: number; estimated_cost: number; score: number }[] = [];
                          allScenarios.forEach((s: any) => {
                            const nameLower = s.name.toLowerCase();
                            const catLower = (s.category || '').toLowerCase();
                            let score = 0;
                            if (nameLower.startsWith(q)) score = 3;
                            else if (nameLower.includes(q)) score = 2;
                            else if (catLower.includes(q)) score = 1;
                            if (score > 0) {
                              matches.push({
                                id: s.id, name: s.name, category: s.category || 'Other',
                                severity_level: s.severity_level ?? 0, estimated_cost: s.estimated_cost || 0, score,
                              });
                            }
                          });
                          matches.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
                          const display = matches.slice(0, 80);
                          if (display.length === 0) {
                            return (
                              <div className="absolute left-0 right-0 rounded-lg shadow-2xl overflow-hidden"
                                style={{ top: 'calc(100% + 4px)', zIndex: 9999, backgroundColor: ahpC.dropdownBg, border: `1px solid ${ahpC.borderAt(0.15)}` }}>
                                <p className="text-[10px] text-center py-3" style={{ color: ahpC.textAt(0.3) }}>No matches</p>
                              </div>
                            );
                          }
                          return (
                            <div className="absolute left-0 right-0 rounded-lg shadow-2xl overflow-y-auto"
                              style={{ top: 'calc(100% + 4px)', maxHeight: '50vh', minWidth: 340, zIndex: 9999, backgroundColor: ahpC.dropdownBg, border: `1px solid ${ahpC.borderAt(0.15)}` }}>
                              {/* Result count header */}
                              <div className="px-3 py-1 text-[8px] font-medium sticky top-0" style={{ color: ahpC.textAt(0.3), backgroundColor: ahpC.dropdownBg, borderBottom: `1px solid ${ahpC.borderAt(0.06)}` }}>
                                {matches.length} scenario{matches.length !== 1 ? 's' : ''} found
                              </div>
                              {/* Flat scrollable list */}
                              {display.map(item => {
                                const sev = Math.min(Math.max(Math.round(item.severity_level), 0), 4);
                                return (
                                  <button
                                    key={item.id}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                      selectScenario(item.id);
                                      setBrowseSearch('');
                                      setScenarioSearch('');
                                      setShowBrowseDropdown(false);
                                    }}
                                    className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:opacity-80 transition-opacity"
                                    style={{ borderBottom: `1px solid ${ahpC.borderAt(0.04)}`, color: ahpC.textAt(0.7) }}
                                  >
                                    <span className="shrink-0 w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold text-white"
                                      style={{ backgroundColor: SEVERITY_COLORS[sev] }}>{sev}</span>
                                    <span className="text-[10px] truncate flex-1">{item.name}</span>
                                    <span className="text-[7px] px-1 py-0.5 rounded shrink-0 truncate max-w-[80px]"
                                      style={{ backgroundColor: ahpC.borderAt(0.06), color: ahpC.textAt(0.35) }}>
                                      {item.category.replace(/ & .*/, '')}
                                    </span>
                                    {item.estimated_cost > 0 && (
                                      <span className="text-[9px] font-mono shrink-0" style={{ color: ahpC.textAt(0.3) }}>
                                        ~${item.estimated_cost >= 1000 ? `${(item.estimated_cost/1000).toFixed(0)}k` : item.estimated_cost.toLocaleString()}
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                              {matches.length > display.length && (
                                <div className="px-3 py-2 text-[9px] text-center" style={{ color: ahpC.textAt(0.3) }}>
                                  +{matches.length - display.length} more — type to narrow
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Category marquee — infinite auto-scroll with icons */}
                      <div className="relative overflow-hidden" style={{ borderBottom: `1px solid ${ahpC.borderAt(0.06)}` }}>
                        <style>{`
                          @keyframes catMarquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
                          .cat-marquee { display:flex; animation: catMarquee 40s linear infinite; width: max-content; }
                          .cat-marquee:hover { animation-play-state: paused; }
                        `}</style>
                        <div className="cat-marquee">
                          {[...allCats, ...allCats].map((cat, idx) => {
                            const meta = CAT_META[cat] || { icon: Activity, color: '#6B7280' };
                            const CatIcon = meta.icon;
                            const isActive = browseCategory === cat;
                            return (
                              <button
                                key={`${cat}-${idx}`}
                                onClick={() => setBrowseCategory(isActive ? null : cat)}
                                className="shrink-0 flex items-center gap-1 px-2 py-1.5 transition-colors whitespace-nowrap"
                                style={{
                                  backgroundColor: isActive ? `${meta.color}18` : 'transparent',
                                  color: isActive ? meta.color : ahpC.textAt(0.4),
                                  borderBottom: isActive ? `2px solid ${meta.color}` : '2px solid transparent',
                                }}
                              >
                                <CatIcon className="w-3 h-3" />
                                <span className="text-[9px] font-medium">{cat.replace(/ & .*| &.*/, '')}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Family list — shows when a category is selected */}
                      <div className="overflow-y-auto flex-1" style={{ maxHeight: scenarioBrowserExpanded ? '350px' : '155px' }}>
                        {browseCategory ? (
                          <div className="py-0.5">
                            {(() => {
                              const families = familyGroups[browseCategory] || [];
                              const standalone = (standaloneByCategory[browseCategory] || []) as any[];
                              const allItems = [
                                ...families.map((f: any) => ({
                                  id: f.base?.id,
                                  name: f.base?.name || 'Unknown',
                                  base: f.base,
                                  variants: f.variants || [],
                                  type: 'family' as const,
                                })),
                                ...standalone.map((s: any) => ({
                                  id: s.id,
                                  name: s.name,
                                  base: s,
                                  variants: [],
                                  type: 'standalone' as const,
                                })),
                              ];
                              if (allItems.length === 0) {
                                return <p className="text-[10px] py-4 text-center" style={{ color: ahpC.textAt(0.3) }}>No scenarios in this category</p>;
                              }
                              return allItems.slice(0, 25).map((item) => {
                                const baseCost = item.base?.estimated_cost || 0;
                                const maxCost = item.variants.length > 0
                                  ? Math.max(baseCost, ...item.variants.map((v: any) => v.estimated_cost || 0))
                                  : baseCost;
                                const isExpanded = browseCategory + ':' + item.id === (searchParams.get('expandFamily') || '');
                                return (
                                  <div key={item.id} style={{ borderBottom: `1px solid ${ahpC.borderAt(0.04)}` }}>
                                    {/* Collapsed row: name + price range + variant count */}
                                    <button
                                      className="w-full flex items-center gap-1.5 px-2 py-1.5 hover:opacity-80 transition-opacity text-left"
                                      onClick={() => {
                                        if (item.variants.length === 0) {
                                          selectScenario(item.id);
                                        } else {
                                          // Toggle expand
                                          const params = new URLSearchParams(searchParams);
                                          if (isExpanded) {
                                            params.delete('expandFamily');
                                          } else {
                                            params.set('expandFamily', browseCategory + ':' + item.id);
                                          }
                                          navigate(`?${params.toString()}`, { replace: true });
                                        }
                                      }}
                                    >
                                      <div className="flex-1 min-w-0">
                                        <div className="text-[9px] font-medium truncate" style={{ color: ahpC.textAt(0.75) }}>
                                          {item.name}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-1.5 shrink-0">
                                        {baseCost > 0 && (
                                          <span className="text-[8px] font-mono" style={{ color: ahpC.textAt(0.3) }}>
                                            ${baseCost >= 1000 ? `${(baseCost/1000).toFixed(0)}k` : baseCost.toLocaleString()}
                                            {maxCost > baseCost && `–${maxCost >= 1000 ? `${(maxCost/1000).toFixed(0)}k` : maxCost.toLocaleString()}`}
                                          </span>
                                        )}
                                        {item.variants.length > 0 && (
                                          <span className="text-[8px] px-1 py-0.5 rounded" style={{ backgroundColor: ahpC.borderAt(0.08), color: ahpC.textAt(0.4) }}>
                                            {isExpanded ? '▾' : '▸'} {item.variants.length + 1}
                                          </span>
                                        )}
                                      </div>
                                    </button>

                                    {/* Expanded: mini card (left) + severity variant list (right) */}
                                    {isExpanded && (() => {
                                      const allMembers = [item.base, ...item.variants].filter(Boolean);
                                      const SEV_LABELS: Record<number, string> = {
                                        1: t('common:costQuartile.lowCost'),
                                        2: t('common:costQuartile.midCost'),
                                        3: t('common:costQuartile.highCost'),
                                        4: t('common:costQuartile.highest'),
                                      };
                                      const SEV_COLORS: Record<number, string> = { 0: '#2b5e49', 1: '#3b7a57', 2: '#C5A55A', 3: '#e67e22', 4: '#B22234' };
                                      const sevFilter = browseSevFilter[item.id] ?? null;
                                      const countByLevel: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
                                      allMembers.forEach((s: any) => {
                                        const sl = Math.min(Math.max(Math.round(s.severity_level ?? 0), 0), 4);
                                        if (sl >= 1 && sl <= 4) countByLevel[sl]++;
                                      });
                                      const filtered = sevFilter === null ? allMembers : allMembers.filter((s: any) => {
                                        const sl = Math.min(Math.max(Math.round(s.severity_level ?? 0), 0), 4);
                                        return sl === sevFilter;
                                      });
                                      const catMeta = CAT_META[browseCategory || ''] || { icon: Activity, color: '#6B7280' };
                                      const BaseIcon = catMeta.icon;
                                      const baseSev = Math.min(Math.max(Math.round(item.base?.severity_level ?? 0), 0), 4);
                                      const sevsInFamily = new Set<number>([baseSev]);
                                      allMembers.forEach((s: any) => { sevsInFamily.add(Math.min(Math.max(Math.round(s.severity_level ?? 0), 0), 4)); });

                                      return (
                                        <div className="flex" style={{ backgroundColor: ahpC.borderAt(0.04) }}>
                                          {/* LEFT: Mini scenario card for base (50% width) */}
                                          <button
                                            onClick={() => selectScenario(item.base.id)}
                                            className="flex flex-col shrink-0 p-2.5 hover:opacity-90 transition-opacity text-left"
                                            style={{ width: '50%', borderRight: `1px solid ${ahpC.borderAt(0.08)}` }}
                                          >
                                            <div className="flex items-center gap-2 mb-1.5">
                                              <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${catMeta.color}15` }}>
                                                <BaseIcon className="w-3.5 h-3.5" style={{ color: catMeta.color }} />
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <span className="text-[7px] font-semibold uppercase tracking-wide" style={{ color: SEV_COLORS[baseSev] }}>
                                                  No Complications
                                                </span>
                                              </div>
                                            </div>
                                            <div className="text-[9px] font-medium leading-tight mb-1.5" style={{ color: ahpC.textAt(0.8) }}>
                                              {item.name}
                                            </div>
                                            <div className="flex items-center gap-2 mb-1.5">
                                              {baseCost > 0 && (
                                                <div className="text-[13px] font-bold font-mono" style={{ color: '#B22234' }}>
                                                  ${baseCost >= 1000 ? `${(baseCost/1000).toFixed(1)}k` : baseCost.toLocaleString()}
                                                </div>
                                              )}
                                              <div className="text-[8px]" style={{ color: ahpC.textAt(0.3) }}>
                                                {item.base?.total_procedures || '?'} codes
                                              </div>
                                            </div>
                                            {/* Severity circles — show family range */}
                                            <div className="flex items-center gap-1 mt-auto pt-1.5" style={{ borderTop: `1px solid ${ahpC.borderAt(0.06)}` }}>
                                              {[0, 1, 2, 3, 4].map(lv => {
                                                const inFamily = sevsInFamily.has(lv);
                                                const isCurrent = lv === baseSev;
                                                return (
                                                  <span key={lv} style={{
                                                    width: 16, height: 16, borderRadius: '50%',
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: 9, fontWeight: 700,
                                                    backgroundColor: isCurrent ? SEV_COLORS[lv] : 'transparent',
                                                    color: isCurrent ? '#fff' : inFamily ? SEV_COLORS[lv] : ahpC.textAt(0.15),
                                                    border: `1px solid ${inFamily ? SEV_COLORS[lv] : ahpC.borderAt(0.1)}`,
                                                    opacity: inFamily ? 1 : 0.35,
                                                  }}>
                                                    {lv}
                                                  </span>
                                                );
                                              })}
                                              <span className="text-[7px] ml-1" style={{ color: ahpC.textAt(0.25) }}>
                                                {allMembers.length} variants
                                              </span>
                                            </div>
                                          </button>

                                          {/* RIGHT: Severity tabs + variant list */}
                                          <div className="flex-1 min-w-0 flex flex-col">
                                            {/* Severity filter tabs */}
                                            <div className="flex items-center gap-1 px-2 py-1" style={{ borderBottom: `1px solid ${ahpC.borderAt(0.08)}` }}>
                                              <button
                                                onClick={(e) => { e.stopPropagation(); setBrowseSevFilter(p => ({ ...p, [item.id]: null })); }}
                                                className="px-1.5 py-0.5 rounded text-[7px] font-semibold transition-colors"
                                                style={{
                                                  backgroundColor: sevFilter === null ? '#002147' : 'transparent',
                                                  color: sevFilter === null ? '#fff' : ahpC.textAt(0.4),
                                                  border: sevFilter === null ? 'none' : `1px solid ${ahpC.borderAt(0.15)}`,
                                                }}
                                              >
                                                All ({allMembers.length})
                                              </button>
                                              {[1, 2, 3, 4].map(lvl => (
                                                <button
                                                  key={lvl}
                                                  onClick={(e) => { e.stopPropagation(); setBrowseSevFilter(p => ({ ...p, [item.id]: lvl })); }}
                                                  className="px-1 py-0.5 rounded text-[7px] font-semibold transition-colors"
                                                  style={{
                                                    backgroundColor: sevFilter === lvl ? SEV_COLORS[lvl] : 'transparent',
                                                    color: sevFilter === lvl ? '#fff' : (countByLevel[lvl] > 0 ? SEV_COLORS[lvl] : ahpC.textAt(0.15)),
                                                    border: sevFilter === lvl ? 'none' : `1px solid ${countByLevel[lvl] > 0 ? `${SEV_COLORS[lvl]}30` : ahpC.borderAt(0.1)}`,
                                                    opacity: countByLevel[lvl] > 0 ? 1 : 0.5,
                                                  }}
                                                  disabled={countByLevel[lvl] === 0}
                                                >
                                                  {SEV_LABELS[lvl]} ({countByLevel[lvl]})
                                                </button>
                                              ))}
                                            </div>
                                            {/* Variant rows */}
                                            <div className="overflow-y-auto" style={{ maxHeight: scenarioBrowserExpanded ? '250px' : '120px' }}>
                                              {filtered.map((s: any) => {
                                                const sev = Math.min(Math.max(Math.round(s.severity_level ?? 0), 0), 4);
                                                const cost = s.estimated_cost || 0;
                                                const delta = sev > 0 && baseCost > 0 && cost > 0 ? cost - baseCost : 0;
                                                let variantName = s.family_variant_label || s.name;
                                                const basePrefix = item.name.replace(/\s*\(.*\)$/, '');
                                                if (variantName.startsWith(basePrefix) && variantName.length > basePrefix.length) {
                                                  variantName = variantName.slice(basePrefix.length).replace(/^\s*[—–\-:]\s*/, '').replace(/^\s*with\s+/i, '');
                                                }
                                                if (!variantName) variantName = s.name;
                                                return (
                                                  <button
                                                    key={s.id}
                                                    onClick={() => selectScenario(s.id)}
                                                    className="w-full flex items-center gap-1.5 pl-2 pr-2 py-1 hover:opacity-80 transition-opacity text-left"
                                                    style={{ borderBottom: `1px solid ${ahpC.borderAt(0.04)}` }}
                                                  >
                                                    {sev === 0 ? (
                                                      <span className="text-[7px] font-semibold shrink-0" style={{ color: SEV_COLORS[0] }}>Base</span>
                                                    ) : (
                                                      <div className="flex gap-[2px] shrink-0">
                                                        {[1,2,3,4].map(lvl => (
                                                          <div key={lvl} style={{
                                                            width: 3, height: 6, borderRadius: 1,
                                                            backgroundColor: lvl <= sev ? SEV_COLORS[sev] : ahpC.borderAt(0.08),
                                                          }} />
                                                        ))}
                                                      </div>
                                                    )}
                                                    <span className="text-[8px] truncate flex-1" style={{ color: ahpC.textAt(0.6) }}>
                                                      {variantName}
                                                    </span>
                                                    <span className="text-[7px] font-mono shrink-0" style={{ color: delta > 0 ? '#B22234' : ahpC.textAt(0.3) }}>
                                                      {delta > 0 ? `+$${delta >= 1000 ? `${(delta/1000).toFixed(1)}k` : delta}` : cost > 0 ? `$${cost >= 1000 ? `${(cost/1000).toFixed(0)}k` : cost}` : ''}
                                                    </span>
                                                  </button>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        ) : (
                          <div className="flex items-center justify-center py-8">
                            <p className="text-[10px] italic" style={{ color: ahpC.textAt(0.2) }}>
                              Select a category to browse
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })()}

                {/* Middle: Geographic Filter Map - Hidden on mobile to save space.
                    Also hidden when isTableFullscreen so the table can claim full vertical space. */}
                <div className={`${scenarioBrowserExpanded || isTableFullscreen ? 'hidden' : 'hidden lg:block'} flex-shrink-0`}>
                  <div className={`backdrop-blur-sm rounded-lg p-3 shadow-lg border ${isAhp ? ahpC.borderCls : 'bg-background/95 border-border'}`} style={isAhp ? { backgroundColor: ahpC.cardBg(0.8) } : undefined}>
                    {/* Tab toggle: USA Map / International (scenario mode only) */}
                    {isScenarioMode ? (
                      <div className="flex items-center gap-0 mb-2">
                        <button
                          onClick={() => setMapTab('usa')}
                          className="text-[10px] font-semibold px-2 py-1 rounded-l transition-colors"
                          style={{
                            backgroundColor: mapTab === 'usa' ? (isAhp ? ahpC.borderAt(0.15) : 'rgba(0,0,0,0.1)') : 'transparent',
                            color: mapTab === 'usa' ? ahpC.text : ahpC.textAt(0.4),
                            border: `1px solid ${ahpC.borderAt(0.1)}`,
                            borderRight: 'none',
                          }}
                        >
                          USA Map
                        </button>
                        <button
                          onClick={() => setMapTab('intl')}
                          className="text-[10px] font-semibold px-2 py-1 rounded-r transition-colors"
                          style={{
                            backgroundColor: mapTab === 'intl' ? (isAhp ? ahpC.borderAt(0.15) : 'rgba(0,0,0,0.1)') : 'transparent',
                            color: mapTab === 'intl' ? ahpC.text : ahpC.textAt(0.4),
                            border: `1px solid ${ahpC.borderAt(0.1)}`,
                          }}
                        >
                          International
                        </button>
                      </div>
                    ) : (
                      <h3 className={`text-xs font-semibold mb-2 ${isAhp ? '' : 'text-foreground'}`} style={isAhp ? { color: ahpC.text } : undefined}>Geographic Filter</h3>
                    )}
                    {(!isScenarioMode || mapTab === 'usa') ? (
                      <>
                        <USAMapNavigator
                          selectedState={selectedState}
                          onStateClick={(stateCode) => {
                            if (stateCode === selectedState) return;
                            trackRankingsFilter('state', stateCode);
                            navigate(`/hospital-rankings/${stateCode.toLowerCase()}`, { replace: true });
                          }}
                        />
                        <button
                          onClick={() => {
                            navigate('/hospital-rankings/usa', { replace: true });
                          }}
                          className="w-full text-center px-2 py-1 mt-2 text-xs bg-primary/10 hover:bg-primary/20 rounded transition-colors"
                          style={{ color: ahpC.text }}
                        >
                          View All USA
                        </button>
                      </>
                    ) : (
                      <InternationalOverlay theme={{ cardBg: ahpC.cardBg, borderCls: ahpC.borderCls, borderAt: ahpC.borderAt, text: ahpC.text, textAt: ahpC.textAt, isAhp, isDark }} scenarioId={scenarioId!} usCost={scenarioMeta?.nationalTotal || 0} />
                    )}
                  </div>
                </div>

                {/* ── Desktop Unified Toolbar (lg+) ── severity pills + search + filters in one row */}
                <div
                  className="hidden lg:flex items-center gap-2 toolbar-row w-full rounded-lg border px-3 py-1.5"
                  style={{
                    backgroundColor: isAhp ? (isDark ? 'rgba(10,22,40,0.6)' : 'rgba(245,243,238,0.8)') : 'rgba(0,0,0,0.3)',
                    borderColor: ahpC.borderAt(0.1),
                    overflow: 'visible',
                  }}
                >
                  {/* Severity pills — only in scenario mode with family > 1 */}
                  {isScenarioMode && activeFamily.length > 1 && (() => {
                    // Group family by severity level
                    const byLevel = new Map<number, any[]>();
                    activeFamily.forEach((s: any) => {
                      const sev = Math.min(Math.max(s.severity_level || 0, 0), 4);
                      const arr = byLevel.get(sev) || [];
                      arr.push(s);
                      byLevel.set(sev, arr);
                    });
                    const activeSev = Math.min(Math.max(activeScenario?.severity_level || 0, 0), 4);
                    return (
                    <>
                      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide shrink-0">
                        <span className="text-[10px] uppercase tracking-wider font-semibold shrink-0 mr-0.5" style={{ color: ahpC.textAt(0.35) }}>
                          Sev
                        </span>
                        {[0, 1, 2, 3, 4].map(sev => {
                          const members = byLevel.get(sev) || [];
                          if (members.length === 0) return null;
                          const isActive = sev === activeSev;
                          const baseCost = activeFamily[0]?.estimated_cost || 0;
                          const avgCost = members.reduce((sum: number, s: any) => sum + (s.estimated_cost || 0), 0) / members.length;
                          const delta = sev > 0 && baseCost > 0 && avgCost > 0 ? avgCost - baseCost : 0;
                          return (
                            <button
                              key={sev}
                              onClick={() => {
                                if (members.length === 1) {
                                  // Single scenario at this level — navigate directly
                                  const newParams = new URLSearchParams(searchParams);
                                  newParams.set('scenario', members[0].id);
                                  setSearchParams(newParams);
                                  setCurrentPage(1);
                                } else {
                                  // Multiple scenarios — open family modal
                                  setShowFamilyModal(true);
                                }
                              }}
                              className="flex items-center gap-1 px-2 py-1 rounded-full shrink-0 transition-all"
                              style={{
                                backgroundColor: isActive ? `${SEVERITY_COLORS[sev]}20` : 'transparent',
                                border: `1.5px solid ${isActive ? SEVERITY_COLORS[sev] : ahpC.borderAt(0.12)}`,
                                color: isActive ? SEVERITY_COLORS[sev] : ahpC.textAt(0.5),
                              }}
                            >
                              <span
                                className="w-[14px] h-[14px] rounded-full flex items-center justify-center text-[7px] font-bold text-white shrink-0"
                                style={{ backgroundColor: SEVERITY_COLORS[sev] }}
                              >{sev}</span>
                              <span className="text-[10px] font-medium whitespace-nowrap">
                                {sev === 0 ? 'Base' : SEVERITY_LABELS[sev]}
                              </span>
                              {members.length > 1 && (
                                <span className="text-[8px] font-medium px-1 py-0 rounded-full" style={{ backgroundColor: `${SEVERITY_COLORS[sev]}20`, color: SEVERITY_COLORS[sev] }}>
                                  {members.length}
                                </span>
                              )}
                              {delta > 0 && (
                                <span className="text-[9px] font-mono tabular-nums" style={{ color: '#B22234' }}>
                                  +${delta >= 1000 ? `${(delta / 1000).toFixed(delta >= 10000 ? 0 : 1)}k` : delta.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      {/* Vertical divider */}
                      <div className="w-px h-6 shrink-0" style={{ backgroundColor: ahpC.borderAt(0.15) }} />
                    </>
                    );
                  })()}

                  {/* Hospital search — flex-1, primary action gets remaining space */}
                  <div className="relative flex-1 min-w-[180px]" style={{ overflow: 'visible' }}>
                    <div className="relative rounded-md" style={{
                      border: `1px solid ${searchFocused ? (isAhp ? '#C5A55A' : 'rgba(0,255,65,0.5)') : ahpC.borderAt(0.2)}`,
                      borderRadius: 6,
                      transition: 'border-color 0.3s ease',
                    }}>
                      <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 z-10" style={{ color: ahpC.textAt(0.5) }} />
                      <Input
                        placeholder={t('rankings:filters.searchPlaceholder')}
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                          setSearchOpen(e.target.value.length > 0);
                        }}
                        onFocus={() => {
                          setSearchFocused(true);
                          if (searchTerm.length > 0) setSearchOpen(true);
                        }}
                        onBlur={() => {
                          setSearchFocused(false);
                          setTimeout(() => setSearchOpen(false), 200);
                        }}
                        className="pl-8 h-8 text-xs border-none focus-visible:ring-0 bg-transparent"
                        style={{ color: ahpC.text, backgroundColor: ahpC.searchBg, borderRadius: 6 }}
                      />
                    </div>
                    {/* Autocomplete Dropdown */}
                    {searchOpen && filteredSuggestions.length > 0 && (
                      <div
                        className="absolute left-0 right-0 rounded-lg shadow-2xl overflow-auto"
                        style={{
                          top: 'calc(100% + 4px)',
                          maxHeight: '40vh',
                          zIndex: 9999,
                          backgroundColor: ahpC.dropdownBg,
                          borderWidth: 1, borderStyle: 'solid',
                          borderColor: isAhp ? 'rgba(197,165,90,0.3)' : 'rgba(0,255,65,0.25)',
                        }}
                      >
                        {filteredSuggestions.map((hospital, idx) => {
                          const prevHospital = idx > 0 ? filteredSuggestions[idx - 1] : null;
                          const showOutOfStateDivider = isStateFiltered && hospital.isOutOfState && (!prevHospital || !prevHospital.isOutOfState);
                          return (
                          <div key={hospital.id}>
                            {showOutOfStateDivider && (
                              <div className="flex items-center gap-2 px-3 py-1.5" style={{ borderTop: `1px solid ${ahpC.borderAt(0.15)}`, backgroundColor: ahpC.borderAt(0.04) }}>
                                <MapPin className="w-3 h-3" style={{ color: ahpC.textAt(0.35) }} />
                                <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: ahpC.textAt(0.35) }}>Other states</span>
                              </div>
                            )}
                            <div
                              onClick={() => handleHospitalSelect(hospital.name, hospital.id)}
                              className="flex items-start gap-3 p-3 cursor-pointer"
                              style={{ borderBottom: `1px solid ${ahpC.borderAt(0.05)}`, opacity: hospital.isOutOfState ? 0.7 : 1 }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = isAhp && !isDark ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                              <Building2 className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: ahpC.textAt(0.4) }} />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate" style={{ color: ahpC.textAt(0.9) }}>{hospital.name}</div>
                                {hospital.city && hospital.state && (
                                  <div className="text-xs" style={{ color: ahpC.textAt(0.4) }}>
                                    {hospital.city}, {hospital.state}
                                    {hospital.isOutOfState && <span className="ml-1 text-[10px]" style={{ color: isAhp ? '#C5A55A' : '#f59e0b' }}>(will clear {selectedState} filter)</span>}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-1">
                                  <span className="text-xs font-mono" style={{ color: ahpC.textAt(0.5) }}>{hospital.total_procedures}/{hospital.total_available_procedures} procedures</span>
                                  <span className="text-xs text-orange-400 font-mono">+{hospital.avg_markup?.toFixed(1)}% markup</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Scenario search — fixed width */}
                  <div className="relative w-[220px] shrink-0" style={{ overflow: 'visible' }}>
                    <div className="relative flex items-center">
                      <ClipboardList className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none z-10" style={{ color: isScenarioMode ? (isAhp ? '#C5A55A' : '#00ff41') : ahpC.textAt(0.4) }} />
                      <Input
                        placeholder={activeScenario ? activeScenario.name : "Scenario..."}
                        value={scenarioSearch}
                        onChange={(e) => {
                          setScenarioSearch(e.target.value);
                          setShowScenarioDropdown(true);
                        }}
                        onFocus={() => setShowScenarioDropdown(true)}
                        onBlur={() => setTimeout(() => setShowScenarioDropdown(false), 200)}
                        className="w-full pl-8 pr-7 text-xs h-8 border-0 outline-none focus-visible:ring-1"
                        style={{
                          background: ahpC.searchBg,
                          color: ahpC.searchText,
                          borderRadius: 6,
                          border: `1px solid ${isScenarioMode ? (isAhp ? 'rgba(197,165,90,0.5)' : 'rgba(0,255,65,0.4)') : ahpC.borderAt(0.12)}`,
                        }}
                      />
                      {isScenarioMode && (
                        <button
                          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-white/10 transition-colors"
                          onClick={() => {
                            searchParams.delete('scenario');
                            setSearchParams(searchParams);
                            setSortBy('markup');
                            setScenarioSearch('');
                          }}
                          style={{ color: ahpC.textAt(0.5) }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    {showScenarioDropdown && (
                      <div
                        className="absolute left-0 rounded-lg shadow-2xl overflow-y-auto"
                        style={{
                          top: 'calc(100% + 4px)',
                          minWidth: 400,
                          maxHeight: '55vh',
                          zIndex: 9999,
                          backgroundColor: ahpC.dropdownBg,
                          borderWidth: 1, borderStyle: 'solid',
                          borderColor: isAhp ? 'rgba(197,165,90,0.3)' : 'rgba(0,255,65,0.25)',
                        }}
                      >
                        {allCategories.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-center" style={{ color: ahpC.textAt(0.4) }}>No scenarios found</div>
                        ) : (
                          allCategories.map((category) => {
                            const families = familyGroups[category] || [];
                            const standalones = standaloneByCategory[category] || [];
                            if (families.length === 0 && standalones.length === 0) return null;

                            const selectScenario = (id: string) => {
                              const newParams = new URLSearchParams(searchParams);
                              newParams.set('scenario', id);
                              setSearchParams(newParams);
                              setSortBy('scenario_price');
                              setCurrentPage(1);
                              setScenarioSearch('');
                              setShowScenarioDropdown(false);
                            };

                            const scenarioRowStyle = (id: string) => ({
                              borderBottom: `1px solid ${ahpC.borderAt(0.04)}`,
                              backgroundColor: id === scenarioId ? (isAhp ? 'rgba(197,165,90,0.15)' : 'rgba(0,255,65,0.1)') : 'transparent',
                              color: id === scenarioId ? (isAhp ? '#C5A55A' : '#00ff41') : (isAhp ? (isDark ? '#e2e8f0' : '#1B1B1B') : '#e2e8f0'),
                            });

                            const hoverHandlers = (id: string) => ({
                              onMouseEnter: (e: React.MouseEvent) => {
                                if (id !== scenarioId) (e.currentTarget as HTMLElement).style.backgroundColor = isAhp && !isDark ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
                              },
                              onMouseLeave: (e: React.MouseEvent) => {
                                if (id !== scenarioId) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                              },
                            });

                            return (
                            <div key={category}>
                              <div
                                className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest sticky top-0"
                                style={{
                                  color: isAhp ? '#C5A55A' : '#00ff41',
                                  backgroundColor: ahpC.dropdownBg,
                                  borderBottom: `1px solid ${isAhp ? 'rgba(197,165,90,0.12)' : 'rgba(0,255,65,0.1)'}`,
                                }}
                              >
                                {category}
                              </div>
                              {families.map((family: { base: any; variants: any[] }) => {
                                const baseCost = family.base.estimated_cost || 0;
                                return (
                                <div key={family.base.id}>
                                  <button
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectScenario(family.base.id)}
                                    className="w-full text-left px-4 py-2.5 flex items-center gap-2"
                                    style={scenarioRowStyle(family.base.id)}
                                    {...hoverHandlers(family.base.id)}
                                  >
                                    <span className="shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: SEVERITY_COLORS[0] }}>0</span>
                                    <span className="text-sm truncate flex-1">{family.base.name}</span>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {family.variants.length > 0 && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: ahpC.textAt(0.4), backgroundColor: ahpC.borderAt(0.08) }}>
                                          +{family.variants.length} variant{family.variants.length !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                      {baseCost > 0 && (
                                        <span className="text-xs font-mono tabular-nums" style={{ color: ahpC.textAt(0.35) }}>~${baseCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                      )}
                                    </div>
                                  </button>
                                  {family.variants.map((v: any) => {
                                    const sevLevel = Math.min(Math.max(v.severity_level || 1, 0), 4);
                                    const vCost = v.estimated_cost || 0;
                                    const delta = baseCost > 0 && vCost > 0 ? vCost - baseCost : 0;
                                    const variantLabel = v.family_variant_label || v.name;
                                    return (
                                      <button
                                        key={v.id}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => selectScenario(v.id)}
                                        className="w-full text-left pl-8 pr-4 py-2 flex items-center gap-2"
                                        style={{ ...scenarioRowStyle(v.id), borderLeft: `2px solid ${SEVERITY_COLORS[sevLevel]}22` }}
                                        {...hoverHandlers(v.id)}
                                      >
                                        <span className="shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: SEVERITY_COLORS[sevLevel] }}>{sevLevel}</span>
                                        <span className="text-xs truncate flex-1" style={{ color: ahpC.textAt(0.55) }}>{variantLabel}</span>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                          <span className="text-[10px] font-medium" style={{ color: SEVERITY_COLORS[sevLevel] }}>{SEVERITY_LABELS[sevLevel]}</span>
                                          {delta > 0 && <span className="text-[10px] font-mono tabular-nums" style={{ color: '#B22234' }}>+${delta >= 1000 ? `${(delta / 1000).toFixed(0)}k` : delta.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                          {delta < 0 && <span className="text-[10px] font-mono tabular-nums" style={{ color: '#2b5e49' }}>-${Math.abs(delta) >= 1000 ? `${(Math.abs(delta) / 1000).toFixed(0)}k` : Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                                );
                              })}
                              {standalones.map((scenario: any) => (
                                <button
                                  key={scenario.id}
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectScenario(scenario.id)}
                                  className="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3"
                                  style={scenarioRowStyle(scenario.id)}
                                  {...hoverHandlers(scenario.id)}
                                >
                                  <span className="text-sm truncate">{scenario.name}</span>
                                  {scenario.estimated_cost > 0 && (
                                    <span className="text-xs shrink-0 font-mono tabular-nums" style={{ color: ahpC.textAt(0.35) }}>~${scenario.estimated_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                                  )}
                                </button>
                              ))}
                            </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  {/* Sort dropdown — compact */}
                  <Select value={sortBy} onValueChange={(value: typeof sortBy) => {
                    setSortBy(value);
                    setCurrentPage(1);
                    trackRankingsFilter('sort', value);
                  }}>
                    <SelectTrigger className="h-8 text-xs shrink-0 w-[110px]" style={{ borderColor: ahpC.borderAt(0.15), backgroundColor: ahpC.searchBg, color: ahpC.text, borderRadius: 6 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent align="end" className="z-[9999]">
                      {isScenarioMode && <SelectItem value="scenario_price">Price</SelectItem>}
                      <SelectItem value="markup">Markup</SelectItem>
                      <SelectItem value="shame">Outliers</SelectItem>
                      <SelectItem value="google_rating">Google</SelectItem>
                      <SelectItem value="cms_rating">CMS</SelectItem>
                      <SelectItem value="bed_count">Beds</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Code type filter — compact */}
                  <Select value={filterCodeType} onValueChange={(value: 'all' | 'cpt' | 'drg' | 'rev') => {
                    setFilterCodeType(value);
                    setFilterCode("");
                    setCurrentPage(1);
                  }}>
                    <SelectTrigger className="h-8 text-xs shrink-0 w-[90px]" style={{ borderColor: ahpC.borderAt(0.15), backgroundColor: ahpC.searchBg, color: ahpC.text, borderRadius: 6 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent align="end" className="z-[9999]">
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="cpt">CPT</SelectItem>
                      <SelectItem value="drg">DRG</SelectItem>
                      <SelectItem value="rev">REV</SelectItem>
                    </SelectContent>
                  </Select>

                  {/* Specific code input — conditional */}
                  {filterCodeType !== 'all' && (
                    <Input
                      placeholder={`${filterCodeType.toUpperCase()}...`}
                      value={filterCode}
                      onChange={(e) => { setFilterCode(e.target.value); setCurrentPage(1); }}
                      className="w-[100px] shrink-0 h-8 text-xs border-0 focus-visible:ring-1"
                      style={{ color: ahpC.text, backgroundColor: ahpC.searchBg, borderRadius: 6, border: `1px solid ${ahpC.borderAt(0.15)}` }}
                    />
                  )}
                </div>

                {/* ── Mobile/Tablet Search & Filters (< lg) ── stacked layout unchanged */}
                <div className="flex-shrink-0 w-full lg:hidden space-y-1.5 sm:space-y-2">
                  {/* Search with Autocomplete */}
                  <div className="relative">
                    <div className="relative rounded-md" style={isAhp ? {
                      border: `1px solid ${ahpC.borderAt(0.2)}`,
                      borderRadius: 8,
                      transition: 'border-color 0.3s ease',
                      ...(searchFocused ? { borderColor: '#C5A55A' } : {}),
                    } : {
                      background: 'linear-gradient(90deg, #00ff41, #ff0000, #00ff41, #ff0000)',
                      backgroundSize: '400% 100%',
                      animation: 'electricBorder 3s linear infinite',
                      padding: '2px',
                      opacity: searchFocused ? 0.8 : 0.2,
                      transition: 'opacity 0.3s ease',
                    }}>
                      <div className={`relative rounded-md ${isAhp ? '' : 'bg-background'}`} style={isAhp ? { backgroundColor: ahpC.searchBg } : undefined}>
                        <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 z-10 ${isAhp ? '' : 'text-white'}`} style={isAhp ? { color: ahpC.textAt(0.5) } : undefined} />
                        <Input
                          placeholder={t('rankings:filters.searchPlaceholder')}
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                            setSearchOpen(e.target.value.length > 0);
                          }}
                          onFocus={() => {
                            setSearchFocused(true);
                            if (searchTerm.length > 0) setSearchOpen(true);
                          }}
                          onBlur={() => {
                            setSearchFocused(false);
                            setTimeout(() => setSearchOpen(false), 200);
                          }}
                          className={`pl-9 h-8 sm:h-9 text-xs sm:text-sm border-none focus-visible:ring-0 ${isAhp ? 'bg-transparent' : 'text-white placeholder:text-white/60'}`}
                          style={isAhp ? { color: ahpC.text } : undefined}
                        />
                      </div>
                    </div>

                    <style>{`
                      @keyframes electricBorder {
                        0% { background-position: 0% 50%; filter: brightness(1) drop-shadow(0 0 8px #00ff41); }
                        25% { filter: brightness(1.2) drop-shadow(0 0 12px #00ff41); }
                        50% { background-position: 100% 50%; filter: brightness(1) drop-shadow(0 0 8px #ff0000); }
                        75% { filter: brightness(1.2) drop-shadow(0 0 12px #ff0000); }
                        100% { background-position: 0% 50%; filter: brightness(1) drop-shadow(0 0 8px #00ff41); }
                      }
                      .hospital-rankings-scroll::-webkit-scrollbar { display: none; }
                      .hospital-rankings-scroll:not([data-scroll-x="0"]) ~ .scroll-hint-fade,
                      .hospital-rankings-scroll.scrolled-x + .scroll-hint-fade { opacity: 0 !important; transition: opacity 0.3s; }
                      .hospital-rankings-scroll table { transform: translateZ(0); backface-visibility: hidden; }
                      .hospital-rankings-scroll tr:hover { transform: translateZ(0); }
                      .bg-black\/80 { transform: translateZ(0); contain: paint; }
                      .markup-box { display: inline-flex; align-items: center; justify-content: center; gap: 0.25rem; padding: 0.125rem 0.25rem; border-radius: 0.25rem; border-width: 1px; border-style: solid; white-space: nowrap; }
                      @media (min-width: 640px) { .markup-box { padding: 0.5rem 0.75rem; border-radius: 0.375rem; } }
                      .markup-extreme { color: #fca5a5; border-color: #dc2626; background-color: rgba(220, 38, 38, 0.15); }
                      .markup-high { color: #fdba74; border-color: #ea580c; background-color: rgba(234, 88, 12, 0.15); }
                      .markup-moderate { color: #fde68a; border-color: #f59e0b; background-color: rgba(245, 158, 11, 0.12); }
                      .markup-low { color: #fef08a; border-color: #eab308; background-color: rgba(234, 179, 8, 0.10); }
                      .markup-minimal { color: #d9f99d; border-color: #84cc16; background-color: rgba(132, 204, 22, 0.10); }
                      .markup-negative { color: #bbf7d0; border-color: #16a34a; background-color: rgba(22, 163, 74, 0.12); }
                      .markup-neutral { color: #d1d5db; border-color: #6b7280; background-color: rgba(107, 114, 128, 0.10); }
                      .markup-text-sm { padding: 0.25rem 0.5rem; font-size: 0.75rem; line-height: 1rem; }
                      @keyframes matrixRain { 0% { transform: translateY(-100px); opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { transform: translateY(100vh); opacity: 0; } }
                      @keyframes neonPulse { 0%, 100% { box-shadow: 0 0 20px rgba(0,255,65,0.8), 0 0 40px rgba(0,255,65,0.4), inset 0 0 10px rgba(0,255,65,0.3); } 50% { box-shadow: 0 0 30px rgba(0,255,65,1), 0 0 60px rgba(0,255,65,0.6), inset 0 0 15px rgba(0,255,65,0.5); } }
                    `}</style>

                    {searchOpen && filteredSuggestions.length > 0 && (
                      <div
                        className="absolute left-0 right-0 rounded-lg shadow-2xl overflow-auto"
                        style={{ top: 'calc(100% + 4px)', maxHeight: '40vh', zIndex: 9999, backgroundColor: ahpC.dropdownBg, borderWidth: 1, borderStyle: 'solid', borderColor: isAhp ? 'rgba(197,165,90,0.3)' : 'rgba(0,255,65,0.25)' }}
                      >
                        {filteredSuggestions.map((hospital, idx) => {
                          const prevHospital = idx > 0 ? filteredSuggestions[idx - 1] : null;
                          const showOutOfStateDivider = isStateFiltered && hospital.isOutOfState && (!prevHospital || !prevHospital.isOutOfState);
                          return (
                          <div key={hospital.id}>
                            {showOutOfStateDivider && (
                              <div className="flex items-center gap-2 px-3 py-1.5" style={{ borderTop: `1px solid ${ahpC.borderAt(0.15)}`, backgroundColor: ahpC.borderAt(0.04) }}>
                                <MapPin className="w-3 h-3" style={{ color: ahpC.textAt(0.35) }} />
                                <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: ahpC.textAt(0.35) }}>Other states</span>
                              </div>
                            )}
                            <div
                              onClick={() => handleHospitalSelect(hospital.name, hospital.id)}
                              className="flex items-start gap-3 p-3 cursor-pointer"
                              style={{ borderBottom: `1px solid ${ahpC.borderAt(0.05)}`, opacity: hospital.isOutOfState ? 0.7 : 1 }}
                              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = isAhp && !isDark ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)'; }}
                              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                              <Building2 className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: ahpC.textAt(0.4) }} />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm truncate" style={{ color: ahpC.textAt(0.9) }}>{hospital.name}</div>
                                {hospital.city && hospital.state && (
                                  <div className="text-xs" style={{ color: ahpC.textAt(0.4) }}>
                                    {hospital.city}, {hospital.state}
                                    {hospital.isOutOfState && <span className="ml-1 text-[10px]" style={{ color: isAhp ? '#C5A55A' : '#f59e0b' }}>(will clear {selectedState} filter)</span>}
                                  </div>
                                )}
                                <div className="flex flex-wrap gap-2 mt-1">
                                  <span className="text-xs font-mono" style={{ color: ahpC.textAt(0.5) }}>{hospital.total_procedures}/{hospital.total_available_procedures} procedures</span>
                                  <span className="text-xs text-orange-400 font-mono">+{hospital.avg_markup?.toFixed(1)}% markup</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Scenario Selector Dropdown — mobile */}
                  <div className="relative w-full">
                    <div className="relative flex items-center">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none z-10" style={{ color: isScenarioMode ? (isAhp ? '#C5A55A' : '#00ff41') : ahpC.textAt(0.4) }} />
                      <Input
                        placeholder={activeScenario ? activeScenario.name : "Filter by scenario..."}
                        value={scenarioSearch}
                        onChange={(e) => { setScenarioSearch(e.target.value); setShowScenarioDropdown(true); }}
                        onFocus={() => setShowScenarioDropdown(true)}
                        onBlur={() => setTimeout(() => setShowScenarioDropdown(false), 200)}
                        className="w-full pl-9 pr-8 text-xs sm:text-sm h-8 sm:h-9 border-0 outline-none focus-visible:ring-1"
                        style={{
                          background: ahpC.searchBg, color: ahpC.searchText, borderRadius: 8,
                          border: `1px solid ${isScenarioMode ? (isAhp ? 'rgba(197,165,90,0.5)' : 'rgba(0,255,65,0.4)') : ahpC.borderAt(0.12)}`,
                        }}
                      />
                      {isScenarioMode && (
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/10 transition-colors"
                          onClick={() => { searchParams.delete('scenario'); setSearchParams(searchParams); setSortBy('markup'); setScenarioSearch(''); }}
                          style={{ color: ahpC.textAt(0.5) }}
                        ><X className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                    {showScenarioDropdown && (
                      <div
                        className="absolute left-0 right-0 rounded-lg shadow-2xl overflow-y-auto"
                        style={{ top: 'calc(100% + 4px)', maxHeight: '55vh', zIndex: 9999, backgroundColor: ahpC.dropdownBg, borderWidth: 1, borderStyle: 'solid', borderColor: isAhp ? 'rgba(197,165,90,0.3)' : 'rgba(0,255,65,0.25)' }}
                      >
                        {allCategories.length === 0 ? (
                          <div className="px-4 py-6 text-sm text-center" style={{ color: ahpC.textAt(0.4) }}>No scenarios found</div>
                        ) : (
                          allCategories.map((category) => {
                            const families = familyGroups[category] || [];
                            const standalones = standaloneByCategory[category] || [];
                            if (families.length === 0 && standalones.length === 0) return null;
                            const selectScenario = (id: string) => {
                              const newParams = new URLSearchParams(searchParams);
                              newParams.set('scenario', id); setSearchParams(newParams);
                              setSortBy('scenario_price'); setCurrentPage(1); setScenarioSearch(''); setShowScenarioDropdown(false);
                            };
                            const scenarioRowStyle = (id: string) => ({
                              borderBottom: `1px solid ${ahpC.borderAt(0.04)}`,
                              backgroundColor: id === scenarioId ? (isAhp ? 'rgba(197,165,90,0.15)' : 'rgba(0,255,65,0.1)') : 'transparent',
                              color: id === scenarioId ? (isAhp ? '#C5A55A' : '#00ff41') : (isAhp ? (isDark ? '#e2e8f0' : '#1B1B1B') : '#e2e8f0'),
                            });
                            const hoverHandlers = (id: string) => ({
                              onMouseEnter: (e: React.MouseEvent) => { if (id !== scenarioId) (e.currentTarget as HTMLElement).style.backgroundColor = isAhp && !isDark ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)'; },
                              onMouseLeave: (e: React.MouseEvent) => { if (id !== scenarioId) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; },
                            });
                            return (
                            <div key={category}>
                              <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-widest sticky top-0"
                                style={{ color: isAhp ? '#C5A55A' : '#00ff41', backgroundColor: ahpC.dropdownBg, borderBottom: `1px solid ${isAhp ? 'rgba(197,165,90,0.12)' : 'rgba(0,255,65,0.1)'}` }}>
                                {category}
                              </div>
                              {families.map((family: { base: any; variants: any[] }) => {
                                const baseCost = family.base.estimated_cost || 0;
                                return (
                                <div key={family.base.id}>
                                  <button onMouseDown={(e) => e.preventDefault()} onClick={() => selectScenario(family.base.id)}
                                    className="w-full text-left px-4 py-2.5 flex items-center gap-2" style={scenarioRowStyle(family.base.id)} {...hoverHandlers(family.base.id)}>
                                    <span className="shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: SEVERITY_COLORS[0] }}>0</span>
                                    <span className="text-sm truncate flex-1">{family.base.name}</span>
                                    <div className="flex items-center gap-2 shrink-0">
                                      {family.variants.length > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ color: ahpC.textAt(0.4), backgroundColor: ahpC.borderAt(0.08) }}>+{family.variants.length} variant{family.variants.length !== 1 ? 's' : ''}</span>}
                                      {baseCost > 0 && <span className="text-xs font-mono tabular-nums" style={{ color: ahpC.textAt(0.35) }}>~${baseCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                    </div>
                                  </button>
                                  {family.variants.map((v: any) => {
                                    const sevLevel = Math.min(Math.max(v.severity_level || 1, 0), 4);
                                    const vCost = v.estimated_cost || 0;
                                    const delta = baseCost > 0 && vCost > 0 ? vCost - baseCost : 0;
                                    return (
                                      <button key={v.id} onMouseDown={(e) => e.preventDefault()} onClick={() => selectScenario(v.id)}
                                        className="w-full text-left pl-8 pr-4 py-2 flex items-center gap-2"
                                        style={{ ...scenarioRowStyle(v.id), borderLeft: `2px solid ${SEVERITY_COLORS[sevLevel]}22` }} {...hoverHandlers(v.id)}>
                                        <span className="shrink-0 w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: SEVERITY_COLORS[sevLevel] }}>{sevLevel}</span>
                                        <span className="text-xs truncate flex-1" style={{ color: ahpC.textAt(0.55) }}>{v.family_variant_label || v.name}</span>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                          <span className="text-[10px] font-medium" style={{ color: SEVERITY_COLORS[sevLevel] }}>{SEVERITY_LABELS[sevLevel]}</span>
                                          {delta > 0 && <span className="text-[10px] font-mono tabular-nums" style={{ color: '#B22234' }}>+${delta >= 1000 ? `${(delta / 1000).toFixed(0)}k` : delta.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                          {delta < 0 && <span className="text-[10px] font-mono tabular-nums" style={{ color: '#2b5e49' }}>-${Math.abs(delta) >= 1000 ? `${(Math.abs(delta) / 1000).toFixed(0)}k` : Math.abs(delta).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                        </div>
                                      </button>
                                    );
                                  })}
                                </div>
                                );
                              })}
                              {standalones.map((scenario: any) => (
                                <button key={scenario.id} onMouseDown={(e) => e.preventDefault()} onClick={() => selectScenario(scenario.id)}
                                  className="w-full text-left px-4 py-2.5 flex items-center justify-between gap-3" style={scenarioRowStyle(scenario.id)} {...hoverHandlers(scenario.id)}>
                                  <span className="text-sm truncate">{scenario.name}</span>
                                  {scenario.estimated_cost > 0 && <span className="text-xs shrink-0 font-mono tabular-nums" style={{ color: ahpC.textAt(0.35) }}>~${scenario.estimated_cost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>}
                                </button>
                              ))}
                            </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>

                  {/* Sort & Filter Controls — tablet only (mobile has inline controls) */}
                  <div className="hidden sm:flex gap-2 items-center w-full">
                    <div className="flex items-center gap-1.5 flex-1 min-w-0">
                      <ArrowUpDown className="w-4 h-4 shrink-0" style={{ color: ahpC.textAt(0.6) }} />
                      <Select value={sortBy} onValueChange={(value: typeof sortBy) => { setSortBy(value); setCurrentPage(1); trackRankingsFilter('sort', value); }}>
                        <SelectTrigger className="flex-1 min-w-0 h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent align="end" className="z-[500]">
                          {isScenarioMode && <SelectItem value="scenario_price">{t('rankings:filters.sortBy')}: {t('rankings:scenarioMode.totalPrice')}</SelectItem>}
                          <SelectItem value="markup">{t('rankings:filters.sortBy')}: {t('rankings:filters.sortOptions.markup')}</SelectItem>
                          <SelectItem value="shame">{t('rankings:filters.sortBy')}: {t('rankings:filters.sortOptions.outliers')}</SelectItem>
                          <SelectItem value="google_rating">{t('rankings:filters.sortBy')}: {t('rankings:columns.googleRating')}</SelectItem>
                          <SelectItem value="cms_rating">{t('rankings:filters.sortBy')}: {t('rankings:columns.cmsRating')}</SelectItem>
                          <SelectItem value="bed_count">{t('rankings:filters.sortBy')}: {t('rankings:columns.bedCount')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Filter className="w-4 h-4 shrink-0" style={{ color: ahpC.textAt(0.6) }} />
                      <Select value={filterCodeType} onValueChange={(value: 'all' | 'cpt' | 'drg' | 'rev') => { setFilterCodeType(value); setFilterCode(""); setCurrentPage(1); }}>
                        <SelectTrigger className="w-[120px] h-9 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent align="end" className="z-[500]">
                          <SelectItem value="all">All Procedures</SelectItem>
                          <SelectItem value="cpt">CPT Codes</SelectItem>
                          <SelectItem value="drg">DRG Codes</SelectItem>
                          <SelectItem value="rev">REV Codes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {filterCodeType !== 'all' && (
                      <Input placeholder={`${filterCodeType.toUpperCase()} code...`} value={filterCode}
                        onChange={(e) => { setFilterCode(e.target.value); setCurrentPage(1); }}
                        className="w-40 h-9 text-sm" style={{ color: ahpC.text }} />
                    )}
                  </div>
                </div>
              </div>
            </CardHeader>

            {/* Severity Family Navigator — mobile only (desktop version is in the toolbar) */}
            {isScenarioMode && activeFamily.length > 1 && (() => {
              const mByLevel = new Map<number, any[]>();
              activeFamily.forEach((s: any) => {
                const sev = Math.min(Math.max(s.severity_level || 0, 0), 4);
                const arr = mByLevel.get(sev) || [];
                arr.push(s);
                mByLevel.set(sev, arr);
              });
              const mActiveSev = Math.min(Math.max(activeScenario?.severity_level || 0, 0), 4);
              return (
              <div
                className="flex items-center gap-1.5 px-2 sm:px-4 lg:px-6 py-2 overflow-x-auto scrollbar-hide lg:hidden"
                style={{
                  borderTop: `1px solid ${ahpC.borderAt(0.08)}`,
                  borderBottom: `1px solid ${ahpC.borderAt(0.08)}`,
                  backgroundColor: isAhp ? (isDark ? 'rgba(10,22,40,0.4)' : 'rgba(245,243,238,0.6)') : 'rgba(0,0,0,0.2)',
                }}
              >
                <span className="text-[10px] uppercase tracking-wider font-semibold shrink-0 mr-1" style={{ color: ahpC.textAt(0.35) }}>
                  Severity
                </span>
                {[0, 1, 2, 3, 4].map(sev => {
                  const members = mByLevel.get(sev) || [];
                  if (members.length === 0) return null;
                  const isActive = sev === mActiveSev;
                  const baseCost = activeFamily[0]?.estimated_cost || 0;
                  const avgCost = members.reduce((sum: number, s: any) => sum + (s.estimated_cost || 0), 0) / members.length;
                  const delta = sev > 0 && baseCost > 0 && avgCost > 0 ? avgCost - baseCost : 0;
                  return (
                    <button
                      key={sev}
                      onClick={() => {
                        if (members.length === 1) {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.set('scenario', members[0].id);
                          setSearchParams(newParams);
                          setCurrentPage(1);
                        } else {
                          setShowFamilyModal(true);
                        }
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shrink-0 transition-all"
                      style={{
                        backgroundColor: isActive ? `${SEVERITY_COLORS[sev]}20` : 'transparent',
                        border: `1.5px solid ${isActive ? SEVERITY_COLORS[sev] : ahpC.borderAt(0.12)}`,
                        color: isActive ? SEVERITY_COLORS[sev] : ahpC.textAt(0.5),
                      }}
                    >
                      <span
                        className="w-[16px] h-[16px] rounded-full flex items-center justify-center text-[8px] font-bold text-white shrink-0"
                        style={{ backgroundColor: SEVERITY_COLORS[sev] }}
                      >{sev}</span>
                      <span className="text-[11px] font-medium whitespace-nowrap max-w-[120px] truncate">
                        {sev === 0 ? 'Base' : SEVERITY_LABELS[sev]}
                      </span>
                      {members.length > 1 && (
                        <span className="text-[8px] font-medium px-1 py-0 rounded-full" style={{ backgroundColor: `${SEVERITY_COLORS[sev]}20`, color: SEVERITY_COLORS[sev] }}>
                          {members.length}
                        </span>
                      )}
                      {delta > 0 && (
                        <span className="text-[10px] font-mono tabular-nums" style={{ color: '#B22234' }}>
                          +${delta >= 1000 ? `${(delta / 1000).toFixed(delta >= 10000 ? 0 : 1)}k` : delta.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              );
            })()}

            <CardContent className="p-0 relative">

              {/* isFetching overlay: subtle dark shimmer when switching scenarios (no full skeleton flash) */}
              {isFetching && !isLoading && (
                <div
                  className="absolute inset-0 pointer-events-none z-[50]"
                  style={{
                    background: isAhp
                      ? (isDark ? 'rgba(10,22,40,0.45)' : 'rgba(250,249,246,0.55)')
                      : 'rgba(0,0,0,0.45)',
                    backdropFilter: 'blur(1px)',
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center gap-3">
                      <div className="flex gap-2">
                        {[0, 150, 300].map((delay) => (
                          <div key={delay} className="w-2 h-2 rounded-full animate-bounce"
                            style={{ background: isAhp ? '#C5A55A' : '#00ff41', animationDelay: `${delay}ms` }} />
                        ))}
                      </div>
                      <p className="font-mono text-xs" style={{ color: isAhp ? 'rgba(197,165,90,0.8)' : 'rgba(0,255,65,0.7)' }}>
                        {isScenarioMode ? 'Loading scenario data...' : 'Refreshing...'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Scrollable Table Container - GPU Accelerated with Virtual Scrolling */}
              <div style={{ position: 'relative' }}>
                {/* Horizontal scroll hint — right fade + arrow (mobile only) */}
                {isMobile && (
                  <div className="scroll-hint-fade" style={{
                    position: 'absolute', top: 0, right: 0, bottom: 0, width: 28, zIndex: 15,
                    background: `linear-gradient(90deg, transparent, ${isAhp ? (isDark ? 'rgba(10,22,40,0.9)' : 'rgba(250,249,246,0.9)') : 'rgba(0,0,0,0.6)'})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none',
                  }}>
                    <ChevronRight className="w-4 h-4 animate-pulse" style={{ color: isAhp ? '#C5A55A' : '#00ff41', opacity: 0.8 }} />
                  </div>
                )}
              <div ref={parentRef} className="overflow-y-auto overflow-x-auto hospital-rankings-scroll" style={{
                // Mobile: 80vh for table (controls moved inline). Desktop normal: leave room for
                // page header + dignity guide. Desktop fullscreen: claim all but ~50px (top bar
                // for filter row inside the card) so the table uses 100% of vertical space.
                maxHeight: isMobile
                  ? '80vh'
                  : isTableFullscreen
                    ? `calc(100vh - ${ahpHeaderOffset + 110}px)`
                    : `calc(100vh - ${isAhp ? 230 + ahpHeaderOffset : 230}px)`,
                paddingBottom: '120px',
                WebkitOverflowScrolling: 'touch',
                transform: 'translate3d(0, 0, 0)', // Force GPU layer
                willChange: 'scroll-position',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                contain: 'layout style paint', // Isolate rendering
                backfaceVisibility: 'hidden' // Reduce flickering
              }}>
                {/* Table Header - Sticky with Compact Legend - GPU Accelerated */}
                <div className={`sticky top-0 z-20 border-b pt-1 lg:pt-4 ${isAhp ? ahpC.borderCls : 'bg-background border-border'}`} style={{
                  transform: 'translate3d(0, 0, 0)', // Force GPU layer
                  willChange: 'transform', // Optimize sticky behavior
                  backfaceVisibility: 'hidden',
                  ...(isAhp ? { backgroundColor: ahpC.navyBg } : {}),
                }}>
                  {/* Compact Legend Row */}
                  <div className={`px-2 lg:px-6 py-1 lg:py-3 border-b ${isAhp ? ahpC.borderCls : 'bg-background border-border/50'}`} style={isAhp ? { backgroundColor: ahpC.navyBg } : undefined}>
                    <div className="flex items-center justify-between text-[10px] lg:text-xs">
                      <div className="flex items-center gap-1 lg:gap-4">
                        <span className={`hidden lg:inline font-medium ${isAhp ? '' : 'text-foreground'}`} style={isAhp ? { color: ahpC.text } : undefined}>Color Guide:</span>
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 lg:w-3 lg:h-3 rounded bg-red-600"></div>
                          <span className="text-red-400 ml-0.5">High</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 lg:w-3 lg:h-3 rounded bg-gray-600"></div>
                          <span className={`ml-0.5 ${isAhp ? '' : 'text-foreground'}`} style={isAhp ? { color: ahpC.textAt(0.7) } : undefined}>Neutral</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <div className="w-2 h-2 lg:w-3 lg:h-3 rounded bg-green-600"></div>
                          <span className="text-green-400 ml-0.5">Low</span>
                        </div>
                      </div>
                      {/* Fullscreen toggle — center vertically between Color Guide and Shame legend.
                          Hidden on mobile (mobile already uses near-full vertical space). */}
                      <ConditionalTooltip
                        isMobile={isMobile}
                        content={isTableFullscreen ? "Exit fullscreen — restore page header and footer" : "Maximize table — hide page header/footer for more vertical space"}
                      >
                        <button
                          type="button"
                          onClick={() => setIsTableFullscreen(v => !v)}
                          aria-label={isTableFullscreen ? "Exit fullscreen table view" : "Enter fullscreen table view"}
                          className={`hidden lg:flex items-center justify-center rounded border w-7 h-7 mx-2 transition-colors ${isAhp ? 'hover:bg-white/10' : 'hover:bg-foreground/10'}`}
                          style={isAhp ? { borderColor: ahpC.borderAt(0.3), color: ahpC.text } : undefined}
                        >
                          {isTableFullscreen
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronUp className="w-4 h-4" />}
                        </button>
                      </ConditionalTooltip>
                      {/* Shame badges - hidden on mobile to save space */}
                      <div className="hidden lg:flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <Badge className="bg-red-600 text-white text-xs px-1 py-0">{t('rankings:badges.extreme')}</Badge>
                          <span className={isAhp ? '' : 'text-foreground'} style={isAhp ? { color: ahpC.textAt(0.7) } : undefined}>≥40%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className="bg-orange-500 text-white text-xs px-1 py-0">{t('rankings:badges.severe')}</Badge>
                          <span className={isAhp ? '' : 'text-foreground'} style={isAhp ? { color: ahpC.textAt(0.7) } : undefined}>25-39%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className="bg-yellow-500 text-white text-xs px-1 py-0">{t('rankings:badges.high')}</Badge>
                          <span className={isAhp ? '' : 'text-foreground'} style={isAhp ? { color: ahpC.textAt(0.7) } : undefined}>10-24%</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Badge className="bg-gray-500 text-white text-xs px-1 py-0">MOD</Badge>
                          <span className={isAhp ? '' : 'text-foreground'} style={isAhp ? { color: ahpC.textAt(0.7) } : undefined}>&lt;10%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Quick sort/filter toolbar — pill toggles for the most common
                      ranking dimensions, code-type rank scopes, and section-group
                      expand toggles. Lives OUTSIDE the <table> on purpose: when it
                      sat inside <thead> as a tr/th colSpan=50, it interfered with
                      the fixed-table-layout column-width algorithm and the header
                      cells locked to literal colgroup widths while the body cells
                      proportionally filled the parent — producing visible column
                      misalignment between headers and rows. As a sibling div with
                      its own width: 100%, it has zero effect on table layout. */}
                  <div className="px-2 py-1 bg-slate-900/70 border-b border-white/10">
                    {isMobile && isAhp ? (
                      <div className="text-[11px] text-white">
                        {/* SORT — one compact horizontal-scroll row */}
                        <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
                          <span className="opacity-50 font-medium uppercase tracking-wide pr-0.5 flex-shrink-0 text-[10px]">Sort</span>
                          {[
                            { v: 'markup', l: isStateFiltered ? 'US Markup' : 'Markup' },
                            { v: 'shame', l: 'Outliers' },
                            { v: 'google_rating', l: 'Google ★' },
                            { v: 'cms_rating', l: 'CMS ★' },
                            { v: 'bed_count', l: 'Beds' },
                            { v: 'exec_comp_per_bed', l: 'Exec/Bed' },
                          ].map(({ v, l }) => (
                            <button key={v} type="button" onClick={() => { setSortBy(v as typeof sortBy); setCurrentPage(1); }}
                              className={`flex-shrink-0 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${sortBy === v ? 'bg-amber-400 text-slate-900 font-semibold' : 'bg-white/10 text-white/80'}`}>
                              {l}
                            </button>
                          ))}
                        </div>
                        {/* Code-type breakdown — collapsed by default (consumers don't need CPT/REV/etc.) */}
                        <button type="button" onClick={() => setMobileCodeOpen(o => !o)} className="mt-1.5 flex items-center gap-1 text-[10px] text-white/55">
                          <span>{mobileCodeOpen ? '▾' : '▸'}</span> Break down by code type{rankByCodeType !== 'overall' ? `: ${rankByCodeType.toUpperCase()}` : ''}
                        </button>
                        {mobileCodeOpen && (
                          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar mt-1 pb-0.5">
                            {[
                              { v: 'overall', l: 'Overall' }, { v: 'cpt', l: 'CPT' }, { v: 'hcpcs', l: 'HCPCS' },
                              { v: 'drg', l: 'DRG' }, { v: 'rev', l: 'REV' }, { v: 'ndc', l: 'NDC' }, { v: 'supply', l: 'Supply' },
                            ].map(({ v, l }) => (
                              <button key={v} type="button" onClick={() => { setRankByCodeType(v as typeof rankByCodeType); setCurrentPage(1); }}
                                className={`flex-shrink-0 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap ${rankByCodeType === v ? 'bg-amber-400 text-slate-900 font-semibold' : 'bg-white/10 text-white/80'}`}>
                                {l}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                    <div className="flex items-center gap-1 flex-wrap text-[10px] leading-none text-white">
                      <span className="opacity-60 font-medium uppercase tracking-wide pr-0.5">Sort:</span>
                      {[
                        { v: 'markup', l: isStateFiltered ? 'US Markup' : 'Markup' },
                        { v: 'shame', l: 'Outliers' },
                        { v: 'google_rating', l: 'Google ★' },
                        { v: 'cms_rating', l: 'CMS ★' },
                        { v: 'bed_count', l: 'Beds' },
                        { v: 'exec_comp_per_bed', l: 'Exec / Bed' },
                      ].map(({ v, l }) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => { setSortBy(v as typeof sortBy); setCurrentPage(1); }}
                          className={`${isMobile ? 'px-2.5 py-1.5' : 'px-1.5 py-0.5'} rounded transition-colors whitespace-nowrap ${
                            sortBy === v
                              ? 'bg-amber-400 text-slate-900 font-semibold'
                              : 'text-white/70 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          {l}{sortBy === v ? ' ▾' : ''}
                        </button>
                      ))}
                      <span className="mx-1 sm:mx-2 opacity-30 select-none">|</span>
                      <span className="opacity-60 font-medium uppercase tracking-wide pr-0.5">Rank by:</span>
                      {[
                        { v: 'overall', l: 'Overall' },
                        { v: 'cpt', l: 'CPT' },
                        { v: 'hcpcs', l: 'HCPCS' },
                        { v: 'drg', l: 'DRG' },
                        { v: 'rev', l: 'REV' },
                        { v: 'ndc', l: 'NDC' },
                        { v: 'supply', l: 'Supply' },
                      ].map(({ v, l }) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => { setRankByCodeType(v as typeof rankByCodeType); setCurrentPage(1); }}
                          className={`${isMobile ? 'px-2.5 py-1.5' : 'px-1.5 py-0.5'} rounded transition-colors whitespace-nowrap ${
                            rankByCodeType === v
                              ? 'bg-amber-400 text-slate-900 font-semibold'
                              : 'text-white/70 hover:text-white hover:bg-white/10'
                          }`}
                        >
                          {l}
                        </button>
                      ))}
                      {/* Section toggles control TABLE COLUMNS — hidden on mobile
                          where the ranking renders as cards (no columns to toggle). */}
                      {!(isMobile && isAhp) && (
                        <>
                          <span className="mx-1 sm:mx-2 opacity-30 select-none">|</span>
                          <span className="opacity-60 font-medium uppercase tracking-wide pr-0.5">Sections:</span>
                          {[
                            { open: !profitsColsHidden, set: setProfitsColumnsExpanded, l: 'Financial' },
                            { open: !reviewsColsHidden, set: setReviewsColumnsExpanded, l: 'Quality' },
                            { open: !profileColsHidden, set: setProfileColumnsExpanded, l: 'About' },
                            { open: !codeColsHidden, set: setCodeColumnsExpanded, l: 'Pricing' },
                          ].map(({ open, set, l }) => (
                            <button
                              key={l}
                              type="button"
                              onClick={() => set(!open)}
                              className={`${isMobile ? 'px-2.5 py-1.5' : 'px-1.5 py-0.5'} rounded transition-colors whitespace-nowrap ${
                                open
                                  ? 'bg-emerald-500/80 text-white font-semibold'
                                  : 'text-white/70 hover:text-white hover:bg-white/10'
                              }`}
                            >
                              {l} {open ? '▾' : '▸'}
                            </button>
                          ))}
                        </>
                      )}
                    </div>
                    )}
                  </div>
                  <table ref={headerTableRef} className="w-full caption-bottom text-sm" style={{ tableLayout: 'fixed', display: (isMobile && isAhp) ? 'none' : 'table' }}>
                    {renderColgroup(false)}
                    {renderColgroup(true)}
                    <thead className={isAhp ? 'ahp-navy-header' : ''}>
                      <tr>
                        <th className="h-8 sm:h-12 px-1 sm:px-4 text-left align-middle font-medium text-center text-white text-[9px] sm:text-sm">
                          <div className="flex flex-col items-center leading-tight">
                            <span>{isScenarioMode && sortBy === 'scenario_price' ? 'Price Rank ↓' : `${isStateFiltered ? 'US Markup' : 'Markup'} Rank ↓`}</span>
                            <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">{isScenarioMode && sortBy === 'scenario_price' ? 'lowest first' : 'highest first'}</span>
                          </div>
                        </th>
                        {isStateFiltered && (
                          <th className="h-8 sm:h-12 px-1 sm:px-2 text-left align-middle font-medium text-center text-white">
                            <span className="text-[9px] sm:text-sm">{selectedState} Rank</span>
                          </th>
                        )}
                        <th className="h-8 sm:h-12 px-1 sm:px-4 text-left align-middle font-medium text-white text-[9px] sm:text-sm">{t('rankings:columns.hospital')}</th>
                        <th className="h-8 sm:h-12 px-1 sm:px-4 text-left align-middle font-medium text-white text-[9px] sm:text-sm" style={isMobile ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}>{!isMobile && t('rankings:columns.location')}</th>
                        <th className="h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white text-[9px] sm:text-sm">
                          {isScenarioMode ? 'Scenario Markup' : (sortBy === 'markup' ? (rankByCodeType !== 'overall' ? `${rankByCodeType.toUpperCase()} Markup` : t('rankings:columns.markupVsMedian', { defaultValue: 'Markup vs Median' })) : t('rankings:columns.shameScore') + ' %')}
                        </th>
                        {/* Scenario: Est. Total header */}
                        {isScenarioMode && (
                          <th className="h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white text-[9px] sm:text-sm">{t('rankings:scenarioMode.totalPrice')}</th>
                        )}
                        {/* Total Procedures header MOVED — now sits inside the Chargemaster section, see below */}
                        {/* Financial summary header (collapsed) — click to expand financial breakdown */}
                        <th
                          className="h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell"
                          style={profitsColsHidden ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
                        >
                          {profitsColsHidden && (
                            <ConditionalTooltip
                              isMobile={isMobile}
                              content={<div className="space-y-1 max-w-xs"><p className="font-semibold">Financial — money in / money out</p><p className="text-xs">Headline: <strong>Annual Revenue</strong> (CMS HCRIS net patient revenue or, where researched, IRS&nbsp;990 / SEC&nbsp;10-K). Expand for <strong>Total Medicare $ received</strong>, <strong>Operating Margin</strong>, <strong>Charity Care</strong>, and (for non-profits) <strong>CEO compensation</strong>. <span className="opacity-70">Source labels appear in each cell&apos;s tooltip.</span></p></div>}
                            >
                              <div
                                className="flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setProfitsColumnsExpanded(true)}
                              >
                                <div className="flex items-center gap-0.5 sm:gap-1">
                                  <Banknote className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-500" />
                                  <span className="text-[8px] sm:text-xs font-semibold">Financial</span>
                                  <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                </div>
                                <span className="text-[7px] sm:text-[9px] opacity-50 font-normal leading-none">revenue · margin</span>
                              </div>
                            </ConditionalTooltip>
                          )}
                        </th>
                        {/* Financial: Annual Revenue header (FIRST detail col — also doubles as Financial group label + collapse trigger when expanded) */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
                          <div className="flex flex-col items-center leading-tight">
                            <div
                              className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setProfitsColumnsExpanded(false)}
                              title="Collapse Financial group"
                            >
                              <Banknote className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-emerald-500" />
                              <span className="text-[8px] sm:text-xs font-semibold">Annual Revenue</span>
                              <ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            </div>
                            <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">HCRIS net patient rev</span>
                          </div>
                        </th>
                        {/* Profits: Total Medicare $ header */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">Total Medicare payments received by this hospital (HCRIS Worksheet E Part A — inpatient PPS only). Outpatient OPPS payments require separate extraction.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">Medicare $</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">paid to hospital</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">Operating margin % from CMS HCRIS — net income divided by total revenue. Higher = more profitable.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">Op Margin</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">% of revenue</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">Charity care provided (HCRIS Worksheet S-10). For non-profits this is a major obligation; low values + high margins = red flag.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">Charity</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">care given</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">CEO / top-exec compensation, sourced from IRS Form 990 (non-profits only). Compare against charity care + operating margin to see where the money goes.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">CEO Comp</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">non-profits</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* Exec/Bed — clickable header sorts the table by CEO comp ÷ bed count.
                            Same dimension as the dedicated /executive-comp/ leaderboard route. */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white profits-cell ${profitsColClass}`} style={profitsHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">CEO comp ÷ staffed bed count. Wealth-density proxy: $20K+/bed at a community hospital is egregious. Click to sort by this metric.</div>}>
                            <button
                              type="button"
                              onClick={() => setSortBy(sortBy === 'exec_comp_per_bed' ? 'markup' : 'exec_comp_per_bed')}
                              className="flex flex-col items-center leading-tight cursor-pointer hover:text-amber-300 transition-colors"
                            >
                              <span className="text-[8px] sm:text-xs">Exec/Bed{sortBy === 'exec_comp_per_bed' && ' ▾'}</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">$ per staffed bed</span>
                            </button>
                          </ConditionalTooltip>
                        </th>
                        {/* Reviews summary header (collapsed) — at start of Reviews block */}
                        <th
                          className="h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell"
                          style={reviewsColsHidden ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
                        >
                          {reviewsColsHidden && (
                            <ConditionalTooltip
                              isMobile={isMobile}
                              content={<div className="space-y-1 max-w-xs"><p className="font-semibold">Quality — care &amp; outcomes</p><p className="text-xs">Combines <strong>Google Reviews</strong>, <strong>CMS Overall Rating</strong>, and (coming soon) HCAHPS patient experience, 30-day mortality, hospital-acquired infections, readmission rates, and ER wait time. Click to expand the breakdown.</p></div>}
                            >
                              <div
                                className="flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setReviewsColumnsExpanded(true)}
                              >
                                <div className="flex items-center gap-0.5 sm:gap-1">
                                  <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-[#facc15]" fill="#facc15" />
                                  <span className="text-[8px] sm:text-xs font-semibold">Quality</span>
                                  <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                </div>
                                <span className="text-[7px] sm:text-[9px] opacity-50 font-normal leading-none">Google + CMS</span>
                              </div>
                            </ConditionalTooltip>
                          )}
                        </th>
                        {/* Reviews: Google Rating header (also doubles as Reviews group label + collapse trigger) */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <div className="flex flex-col items-center leading-tight">
                            <div
                              className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setReviewsColumnsExpanded(false)}
                              title="Collapse Reviews group"
                            >
                              <Star className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#facc15]" fill="#facc15" />
                              <span className="text-[8px] sm:text-xs font-semibold">Reviews</span>
                              <ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#facc15]" />
                            </div>
                            <span className="text-[7px] sm:text-[9px] opacity-50 font-normal mt-0.5">{t('rankings:columns.googleRating')}</span>
                          </div>
                        </th>
                        {/* Reviews: CMS Rating header */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <span className="text-[8px] sm:text-xs">{t('rankings:columns.cmsRating')}</span>
                        </th>
                        {/* Reviews: Mortality rate (Hybrid_HWM) — links to aggregate measure page */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">Hospital-wide mortality rate</p><p>30-day risk-adjusted death rate after admission. Source: CMS Hybrid_HWM measure (~5,200 hospitals report it). Lower is better.</p><p className="opacity-60">Click to see the leaderboard across all U.S. hospitals.</p></div>}
                          >
                            <Link to="/quality/measure/Hybrid_HWM" className="block hover:opacity-80 transition-opacity">
                              <div className="flex flex-col items-center leading-tight">
                                <span className="text-[8px] sm:text-xs">Mortality</span>
                                <span className="text-[7px] sm:text-[9px] opacity-50">30-day · lower is better</span>
                              </div>
                            </Link>
                          </ConditionalTooltip>
                        </th>
                        {/* Reviews: Readmission rate (Hybrid_HWR) */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">Hospital-wide readmission rate</p><p>30-day all-cause unplanned-readmission rate. Source: CMS Hybrid_HWR measure. Lower is better — readmissions often signal incomplete care or premature discharge.</p><p className="opacity-60">Click to see the leaderboard across all U.S. hospitals.</p></div>}
                          >
                            <Link to="/quality/measure/Hybrid_HWR" className="block hover:opacity-80 transition-opacity">
                              <div className="flex flex-col items-center leading-tight">
                                <span className="text-[8px] sm:text-xs">Readmit.</span>
                                <span className="text-[7px] sm:text-[9px] opacity-50">30-day · lower is better</span>
                              </div>
                            </Link>
                          </ConditionalTooltip>
                        </th>
                        {/* Reviews: HCAHPS (would definitely recommend %) — H_RECMND_DY */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">Patient Experience — Would recommend</p><p>% of HCAHPS survey respondents who answered they would <em>definitely recommend</em> this hospital. Source: CMS HCAHPS measure <code>H_RECMND_DY</code> (~5,800 hospitals report it). Higher is better.</p><p className="opacity-60">Loaded on demand the first time you sort by this column.</p></div>}
                          >
                            <div className="flex flex-col items-center leading-tight">
                              <span className="text-[8px] sm:text-xs">Recommend</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50">HCAHPS · higher better</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* Reviews: HAI Risk (Standardized Infection Ratio) — HAI_1_SIR */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">Hospital-Acquired Infection (HAI) Risk</p><p>Standardized Infection Ratio for central-line bloodstream infections (CLABSI). 1.0 = national average; below 1.0 = safer than average. Source: CMS HAI measure <code>HAI_1_SIR</code>.</p><p className="opacity-60">Loaded on demand the first time you sort by this column.</p></div>}
                          >
                            <div className="flex flex-col items-center leading-tight">
                              <span className="text-[8px] sm:text-xs">HAI</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50">SIR · lower safer</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* Reviews: ER Wait Time (median minutes from arrival to leaving) — OP_18b */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white quality-cell ${reviewsColClass}`}
                          style={reviewsHiddenStyle}
                        >
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={<div className="space-y-1 max-w-xs text-xs"><p className="font-semibold">ER Wait Time</p><p>Median minutes patients spent in the emergency department before leaving the visit, excluding transfers and psychiatric/mental-health patients. Source: CMS Timely &amp; Effective measure <code>OP_18b</code>. Lower is better.</p><p className="opacity-60">Loaded on demand the first time you sort by this column.</p></div>}
                          >
                            <div className="flex flex-col items-center leading-tight">
                              <span className="text-[8px] sm:text-xs">ER Wait</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50">min · lower better</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* Profile summary header (collapsed) — at start of Profile block */}
                        <th
                          className="h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white about-cell"
                          style={profileColsHidden ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
                        >
                          {profileColsHidden && (
                            <ConditionalTooltip
                              isMobile={isMobile}
                              content={<div className="space-y-1 max-w-xs"><p className="font-semibold">About — hospital metadata &amp; finances</p><p className="text-xs">Hospital type, parent system, ownership, year founded, bed count, and last reported annual revenue. Hover any cell for the full breakdown. <span className="opacity-70">Money group (operating margin, charity care, Medicare $, CEO comp) coming next.</span></p></div>}
                            >
                              <div
                                className="flex flex-col items-center justify-center cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setProfileColumnsExpanded(true)}
                              >
                                <div className="flex items-center gap-0.5 sm:gap-1">
                                  <Building2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white/70" />
                                  <span className="text-[8px] sm:text-xs font-semibold">About</span>
                                  <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                </div>
                                <span className="text-[7px] sm:text-[9px] opacity-50 font-normal leading-none">type · beds · revenue</span>
                              </div>
                            </ConditionalTooltip>
                          )}
                        </th>
                        {/* Profile: Beds header (also doubles as Profile group label + collapse trigger) */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white about-cell ${profileColClass}`}
                          style={profileHiddenStyle}
                        >
                          <div className="flex flex-col items-center leading-tight">
                            <div
                              className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setProfileColumnsExpanded(false)}
                              title="Collapse Profile group"
                            >
                              <Building2 className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/70" />
                              <span className="text-[8px] sm:text-xs font-semibold">About</span>
                              <ChevronDown className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-white/50" />
                            </div>
                            <span className="text-[7px] sm:text-[9px] opacity-50 font-normal mt-0.5">{t('rankings:columns.bedCount')}</span>
                          </div>
                        </th>
                        {/* Profile: Type header */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white about-cell ${profileColClass}`}
                          style={profileHiddenStyle}
                        >
                          <ConditionalTooltip
                            isMobile={isMobile}
                            content={<div className="text-xs max-w-xs">Hospital type (Acute / Critical Access / Psychiatric / Children&apos;s) and ownership (For-profit / Non-profit / Government / Religious-affiliated). Hover a cell for parent system + ownership detail.</div>}
                          >
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">Type</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">+ ownership</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* About: NPI header — NPPES National Provider Identifier */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white about-cell ${profileColClass}`} style={profileHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">National Provider Identifier (NPI) — 10-digit ID issued by CMS via the NPPES registry. Click to open the provider's NPPES record.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">NPI</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">NPPES</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* About: CCN header — CMS Certification Number */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white about-cell ${profileColClass}`} style={profileHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">CMS Certification Number — 6-digit ID used by Medicare for HCRIS cost reports + Hospital Compare quality data. Click to open Care Compare.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">CCN</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">CMS</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* About: EIN header — IRS Employer ID for Form 990 lookup */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white about-cell ${profileColClass}`} style={profileHiddenStyle}>
                          <ConditionalTooltip isMobile={isMobile} content={<div className="text-xs max-w-xs">IRS Employer ID — required for non-profit Form 990 lookups (CEO comp, charitable mission, executive perks). Click to open ProPublica Nonprofit Explorer. Most rows still missing until 990 ingest completes.</div>}>
                            <div className="flex flex-col items-center leading-tight cursor-help">
                              <span className="text-[8px] sm:text-xs">EIN</span>
                              <span className="text-[7px] sm:text-[9px] opacity-50 font-normal">IRS 990</span>
                            </div>
                          </ConditionalTooltip>
                        </th>
                        {/* Total Procedures — first column of Chargemaster section */}
                        <th className={`h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white text-[9px] sm:text-sm chargemaster-cell ${codeColClass}`}
                          style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
                        >
                          {isScenarioMode ? t('rankings:scenarioMode.coverage') : t('rankings:columns.totalProcedures')}
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white chargemaster-cell ${codeColClass}`}
                          style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-help text-[9px] sm:text-sm">
                                {isAhp && codeColumnsExpanded && (
                                  <ChevronDown
                                    className="w-2.5 h-2.5 sm:w-3 sm:h-3 cursor-pointer text-[#C5A55A] hover:text-white transition-colors"
                                    onClick={() => setCodeColumnsExpanded(false)}
                                    title="Collapse chargemaster code columns"
                                  />
                                )}
                                CPT
                                <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs z-[100]">
                              <div className="space-y-1">
                                <p className="font-semibold">CPT Codes</p>
                                <p className="text-sm">Current Procedural Terminology codes describe medical, surgical, and diagnostic services performed by healthcare providers.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white chargemaster-cell ${codeColClass}`}
                          style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-help text-[9px] sm:text-sm">
                                MS-DRG
                                <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs z-[100]">
                              <div className="space-y-1">
                                <p className="font-semibold">MS-DRG Codes</p>
                                <p className="text-sm">Medicare Severity Diagnosis Related Groups classify hospital cases for Medicare payment purposes. Used primarily for inpatient services.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white chargemaster-cell ${codeColClass}`}
                          style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-help text-[9px] sm:text-sm">
                                HCPCS
                                <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs z-[100]">
                              <div className="space-y-1">
                                <p className="font-semibold">HCPCS Codes</p>
                                <p className="text-sm">Healthcare Common Procedure Coding System codes for medical services, supplies, and equipment not covered by CPT codes.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white chargemaster-cell ${codeColClass}`}
                          style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-help text-[9px] sm:text-sm">
                                REV
                                <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs z-[100]">
                              <div className="space-y-1">
                                <p className="font-semibold">Revenue Codes</p>
                                <p className="text-sm">Four-digit codes that describe hospital departments or services where care was provided (e.g., emergency room, pharmacy, laboratory).</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </th>
                        <th className={`h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white chargemaster-cell ${codeColClass}`}
                          style={codeColsHidden ? { width: 0, padding: 0, overflow: 'hidden', border: 'none' } : undefined}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-help text-[9px] sm:text-sm">
                                NDC
                                <Info className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs z-[100]">
                              <div className="space-y-1">
                                <p className="font-semibold">National Drug Codes (NDC)</p>
                                <p className="text-sm">NDC codes identify pharmaceutical products including prescription drugs, over-the-counter medications, and certain medical devices. These are standardized national codes, not hospital-specific internal drug codes.</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </th>
                        {/* Code Breakdown summary header (AHP collapsed) — hidden in scenario mode */}
                        <th
                          className="h-8 sm:h-12 px-1 sm:px-2 align-middle font-medium text-center text-white"
                          style={(codeColsHidden && !isScenarioMode) ? undefined : { width: 0, padding: 0, overflow: 'hidden', border: 'none' }}
                        >
                          {codeColsHidden && !isScenarioMode && (
                            <div
                              className="flex items-center justify-center gap-0.5 sm:gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => setCodeColumnsExpanded(true)}
                            >
                              <Columns3 className="w-3 h-3 sm:w-4 sm:h-4 text-[#C5A55A]" />
                              <span className="text-[8px] sm:text-xs">Chargemaster Codes</span>
                              <ChevronRight className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            </div>
                          )}
                        </th>
                        {!isScenarioMode && <th className="h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white text-[9px] sm:text-sm outer-cell">Price Outliers <span className="text-[7px] sm:text-xs text-muted-foreground">(% outliers)</span></th>}
                        {!isScenarioMode && !isAhp && <th className="h-8 sm:h-12 px-1 sm:px-4 align-middle font-medium text-center text-white text-[9px] sm:text-sm">Shame</th>}
                      </tr>
                    </thead>
                  </table>
                </div>

                <div style={{ position: 'relative', width: '100%', minHeight: `${rowVirtualizer.getTotalSize()}px` }}>
                  {/* Visible virtual rows */}
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const hospital = rankings?.[virtualRow.index];
                    if (!hospital) return null;

                    // Mobile (ahp): a tappable card instead of a cramped table row.
                    // Same virtualized list + same `rankings` array; desktop keeps
                    // the full table untouched.
                    if (isMobile && isAhp) {
                      const rank = (isScenarioMode || isStateFiltered) ? virtualRow.index + 1 : hospital.overall_rank_gross;
                      const scenarioPrice = isScenarioMode && hospital.scenario_blended_price != null
                        ? `$${Math.round(hospital.scenario_blended_price).toLocaleString()}`
                        : null;
                      const H = hospital as any;
                      const hid = hospital.hospital_id;
                      const isExp = expandedRankId === hid;
                      const sub = isDark ? 'rgba(240,235,227,0.6)' : 'rgba(27,27,27,0.6)';
                      const fmtCount = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`;
                      const fmtUsd = (n: number) => n >= 1e9 ? `$${(n / 1e9).toFixed(1)}B` : n >= 1e6 ? `$${(n / 1e6).toFixed(0)}M` : `$${Math.round(n).toLocaleString()}`;
                      const dimRow = (label: React.ReactNode, value: React.ReactNode, onClick?: () => void, key?: string) => (
                        <div key={key} onClick={onClick} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)'}`, cursor: onClick ? 'pointer' : 'default', fontSize: 13 }}>
                          <span style={{ color: sub }}>{label}</span>
                          <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>{value}</span>
                        </div>
                      );
                      return (
                        <div
                          key={virtualRow.key}
                          data-index={virtualRow.index}
                          ref={rowVirtualizer.measureElement}
                          style={{ position: 'absolute', top: 0, left: 0, width: '100%', transform: `translateY(${virtualRow.start}px)`, padding: '4px 8px' }}
                        >
                          <div style={{ borderRadius: 12, backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff', border: `1px solid ${isDark ? 'rgba(197,165,90,0.18)' : 'rgba(0,40,104,0.10)'}`, overflow: 'hidden' }}>
                            <button
                              onClick={() => {
                                hapticLight();
                                if (isExp) { setExpandedRankId(null); return; }
                                setExpandedRankId(hid);
                                const q = ['google_rating', 'cms_rating', 'mortality', 'readmission', 'hcahps', 'hai_risk', 'er_wait_time'];
                                const f = ['operating_margin', 'exec_comp_per_bed', 'charity_care', 'net_patient_revenue'];
                                setExpandTab(q.includes(sortBy) ? 'quality' : f.includes(sortBy) ? 'financial' : sortBy === 'bed_count' ? 'misc' : 'markup');
                              }}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', color: isDark ? '#f0ebe3' : '#1B1B1B', textAlign: 'left' }}
                            >
                              <div style={{ flexShrink: 0, width: 46, textAlign: 'center' }}>
                                <div style={{ fontSize: 16, fontWeight: 800, lineHeight: 1, fontFamily: 'monospace', color: isDark ? '#C5A55A' : '#002868' }}>#{rank}</div>
                                <div style={{ fontSize: 8, opacity: 0.5, marginTop: 2 }}>{(isScenarioMode || isStateFiltered) ? `of ${rankings?.length}` : 'overall'}</div>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{titleCaseHospital(hospital.hospital_name)}</div>
                                <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hospital.hospital_city}, {hospital.hospital_state}</div>
                              </div>
                              <div style={{ flexShrink: 0, textAlign: 'right' }}>
                                {scenarioPrice ? (
                                  <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 15 }}>{scenarioPrice}</div>
                                ) : (
                                  <div className={`markup-box ${getMarkupColorClass(hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage ?? 0)}`} style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, padding: '3px 7px', borderRadius: 6 }}>
                                    {formatMarkupPercentage(hospital.median_gross_markup_percentage ?? hospital.avg_gross_markup_percentage ?? 0)}
                                  </div>
                                )}
                                {hospital.google_rating != null && (
                                  <div style={{ fontSize: 10, opacity: 0.7, marginTop: 3 }}>★ {hospital.google_rating.toFixed(1)}</div>
                                )}
                              </div>
                              <ChevronDown style={{ width: 16, height: 16, flexShrink: 0, opacity: 0.5, transform: isExp ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                            </button>
                            {isExp && (
                              <div style={{ padding: '0 12px 12px' }}>
                                <div style={{ display: 'flex', gap: 6, padding: '2px 0 10px', overflowX: 'auto' }} className="no-scrollbar">
                                  {([['markup', 'Markup'], ['quality', 'Quality'], ['financial', 'Financial'], ['misc', 'Misc']] as const).map(([tb, lbl]) => (
                                    <button key={tb} onClick={() => setExpandTab(tb)} style={{ flexShrink: 0, padding: '5px 13px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', background: expandTab === tb ? (isDark ? '#C5A55A' : '#002868') : (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,40,104,0.06)'), color: expandTab === tb ? '#fff' : (isDark ? '#f0ebe3' : '#002868') }}>{lbl}</button>
                                  ))}
                                </div>
                                {expandTab === 'markup' && (
                                  <div>
                                    {[{ k: 'cpt', l: 'CPT' }, { k: 'hcpcs', l: 'HCPCS' }, { k: 'drg', l: 'MS-DRG' }, { k: 'rev', l: 'Revenue' }, { k: 'ndc', l: 'Drug/NDC' }, { k: 'supply', l: 'Supply' }].map(({ k, l }) => {
                                      const v = H[`${k}_avg_markup_percentage`];
                                      if (v == null) return null;
                                      const c = H[`${k}_procedure_count`] ?? H[`${k}_count`];
                                      return dimRow(<span>{l}{c ? ` · ${fmtCount(c)} codes` : ''}</span>, <span style={{ color: v >= 0 ? '#e2576a' : '#6bc9a0' }}>{formatMarkupPercentage(v)} ›</span>, () => handleMarkupClick(hid, hospital.hospital_name, k, v), k);
                                    })}
                                    <div style={{ fontSize: 10, color: sub, marginTop: 6 }}>Tap a code type to see the procedures behind it.</div>
                                  </div>
                                )}
                                {expandTab === 'quality' && (
                                  <div>
                                    {H.google_rating != null && dimRow('Google rating', `★ ${H.google_rating.toFixed(1)}`, undefined, 'g')}
                                    {H.cms_rating != null && dimRow('CMS star rating', `★ ${H.cms_rating}`, undefined, 'c')}
                                    {H.hcahps_recommend_pct != null && dimRow('Patients who recommend', `${Math.round(H.hcahps_recommend_pct)}%`, undefined, 'h')}
                                    {H.mortality_score != null && dimRow('Mortality measures', H.mortality_score, undefined, 'm')}
                                    {H.readmission_score != null && dimRow('Readmission measures', H.readmission_score, undefined, 'r')}
                                    {H.hai_risk != null && dimRow('Infection risk', H.hai_risk, undefined, 'i')}
                                    {(H.er_wait_minutes ?? H.er_wait_time) != null && dimRow('ER wait', `${Math.round(H.er_wait_minutes ?? H.er_wait_time)} min`, undefined, 'e')}
                                  </div>
                                )}
                                {expandTab === 'financial' && (
                                  <div>
                                    {(H.annual_revenue_usd ?? H.hcris_net_patient_revenue_usd) != null && dimRow('Net patient revenue', fmtUsd(H.annual_revenue_usd ?? H.hcris_net_patient_revenue_usd), undefined, 'rev')}
                                    {H.operating_margin != null && dimRow('Operating margin', `${Number(H.operating_margin).toFixed(1)}%`, undefined, 'om')}
                                    {H.exec_comp_per_bed != null && dimRow('Exec comp / bed', fmtUsd(H.exec_comp_per_bed), undefined, 'ec')}
                                    {(H.charity_care_usd ?? H.charity_care) != null && dimRow('Charity care', fmtUsd(H.charity_care_usd ?? H.charity_care), undefined, 'ch')}
                                  </div>
                                )}
                                {expandTab === 'misc' && (
                                  <div>
                                    {H.bed_count != null && dimRow('Beds', Number(H.bed_count).toLocaleString(), undefined, 'b')}
                                    {H.hospital_type && dimRow('Type', H.hospital_type, undefined, 't')}
                                    {dimRow('Overall US rank', `#${hospital.overall_rank_gross}`, undefined, 'ur')}
                                  </div>
                                )}
                                <Link to={`/hospital/${hid}`} style={{ display: 'block', marginTop: 10, textAlign: 'center', padding: '9px', borderRadius: 8, background: isDark ? 'rgba(197,165,90,0.15)' : 'rgba(178,34,52,0.08)', color: isDark ? '#C5A55A' : '#B22234', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>View full hospital profile →</Link>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <table
                        key={virtualRow.key}
                        data-index={virtualRow.index}
                        ref={rowVirtualizer.measureElement}
                        style={{
                          width: '100%',
                          tableLayout: 'fixed',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        {renderColgroup(false)}
                        {renderColgroup(true)}
                        <tbody>
                          {renderTableRow(hospital, virtualRow.index)}
                        </tbody>
                      </table>
                    );
                  })}
                </div>

                {/* Error state for scenario mode */}
                {isScenarioMode && rankingsError && !rankings?.length && (
                  <div className="p-8 text-center space-y-4">
                    <AlertTriangle className="w-8 h-8 mx-auto text-orange-400" />
                    <p className="text-sm" style={{ color: ahpC.textAt(0.6) }}>
                      Could not load hospital pricing data for this scenario. Try refreshing the page.
                    </p>
                    <Button onClick={() => window.location.reload()} variant="outline" size="sm" className="gap-2">
                      <RefreshCw className="w-3 h-3" /> Refresh
                    </Button>
                  </div>
                )}

                {/* Footer: count + data attribution + methodology + data-sources links */}
                <div className="p-6 border-t border-border space-y-3">
                  <div className="flex items-center justify-center">
                    <div className="text-sm" style={{ color: ahpC.text }}>
                      Showing <span className="font-semibold">{(rankings?.length || totalCount).toLocaleString()}</span> ranked hospitals out of <span className="font-semibold">9,500+</span> indexed (only hospitals with 50+ comparable procedures are ranked).
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs" style={{ color: ahpC.textAt(0.6) }}>
                    <span>
                      Sourced from each hospital&apos;s federally-mandated CMS Hospital Price Transparency disclosure
                    </span>
                    <span className="hidden sm:inline">·</span>
                    <Link to="/data-methodology" className="underline hover:no-underline" style={{ color: ahpC.text }}>
                      How we calculate rankings →
                    </Link>
                    <span className="hidden sm:inline">·</span>
                    <Link to="/data-sources" className="underline hover:no-underline" style={{ color: ahpC.text }}>
                      Browse all 9,500+ hospitals + their chargemaster files →
                    </Link>
                  </div>
                </div>
              </div>
              </div>{/* close scroll-hint wrapper */}
            </CardContent>
          </Card>

          {/* Care Resources — shown in scenario mode */}
          {isScenarioMode && activeScenario && (
            <div id="care-resources-section">
              <ScenarioCareResources
                scenarioTags={activeScenario.tags || [activeScenario.category?.toLowerCase().replace(/[^a-z]+/g, '-') || 'general']}
                scenarioName={activeScenario.name || ''}
              />
            </div>
          )}

        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <Button
          onClick={scrollToTop}
          className="fixed right-6 z-[10010] h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 bg-primary hover:bg-primary/90"
          style={{ bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))' }}
          size="icon"
          aria-label="Back to top"
        >
          <ChevronUp className="w-5 h-5" />
        </Button>
      )}

      {/* Elegant Custom Scrollbar - Right Side.
          Top is pinned to ahpHeaderOffset (banner + header + flag stripe, incl.
          the iOS safe-area inset) so the red top-arrow + initial thumb clear the
          fixed header — otherwise on mobile the handle sat behind the header and
          couldn't be grabbed. Track height fills down to just above the tab bar. */}
      <div className="flex fixed right-0 sm:right-2 z-40 flex-col items-center pointer-events-auto" style={{ top: `calc(${ahpHeaderOffset}px + 6px)` }}>
        {/* Top Arrow - Red in normal mode (worst hospitals), Green in scenario mode (cheapest/best at top) */}
        <button
          onClick={scrollToTop}
          className={`flex items-center justify-center w-4 h-4 sm:w-8 sm:h-8 shadow-md transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            isScenarioMode
              ? 'bg-gradient-to-b from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 border border-green-400/50'
              : 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border border-red-400/50'
          }`}
          style={{ borderRadius: '8px 8px 0 0' }}
          aria-label="Scroll to top"
        >
          <ChevronUp className="w-3 h-3 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
        </button>

        {/* Scrollbar Track — inverted in scenario mode (green=cheap at top, red=expensive at bottom) */}
        <div
          className="scrollbar-track relative w-4 sm:w-8 cursor-pointer overflow-hidden"
          style={{
            // Fill from just below the header down to above the mobile tab bar
            // (~84px + safe-area) / desktop footer, minus the two arrow buttons.
            height: `calc(100dvh - ${ahpHeaderOffset}px - 150px - env(safe-area-inset-bottom, 0px))`,
            background: isScenarioMode
              ? 'linear-gradient(180deg, #22c55e 0%, #000000 50%, #dc2626 100%)'
              : 'linear-gradient(180deg, #dc2626 0%, #000000 50%, #22c55e 100%)'
          }}
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            const y = e.clientY - rect.top;
            const percent = (y / rect.height) * 100;
            scrollToPercent(percent);
          }}
        >
          {/* Matrix Rain Background - Only visible with Konami code */}
          {konamiActivated && (
            <div className="absolute inset-0 font-mono text-[10px]" style={{ overflow: 'hidden', pointerEvents: 'none' }}>
              {[...Array(20)].map((_, i) => {
                const isRed = Math.random() > 0.7; // 30% chance of red
                return (
                  <div
                    key={i}
                    className="absolute"
                    style={{
                      left: `${(i * 5)}%`,
                      animation: `matrixRain ${1.5 + Math.random() * 2}s linear infinite`,
                      animationDelay: `${Math.random() * 3}s`,
                      color: isRed ? '#ff0000' : '#00ff41'
                    }}
                  >
                    {['0', '1', '0', '1', '0', '1', '0', '1', '0', '1'].map((char, j) => (
                      <div
                        key={j}
                        className="leading-tight"
                        style={{
                          opacity: j === 0 ? 1 : (10 - j) / 10 // Fade trail effect
                        }}
                      >
                        {char}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}

          {/* Scrollbar Thumb - Black 60% with white text.
              Position + height + percentage are driven directly from the scroll
              handler via refs to avoid full React re-renders on every wheel tick. */}
          <div
            ref={scrollbarThumbRef}
            className={`absolute left-0 right-0 rounded-sm ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} ${isDragging ? '' : 'transition-all duration-200'}`}
            style={{
              top: `calc(${scrollProgress}% - ${(scrollProgress / 100) * thumbHeight}px)`,
              height: `${thumbHeight}px`,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 0 20px rgba(255, 255, 255, 0.5), 0 0 40px rgba(255, 255, 255, 0.2)'
            }}
            onMouseDown={handleThumbMouseDown}
            onTouchStart={handleThumbTouchStart}
          >
            {/* Percentage display - White text on black background, hidden on mobile (too narrow) */}
            <div className="absolute inset-0 hidden sm:flex items-center justify-center font-mono text-[10px] font-bold" style={{ color: '#ffffff' }}>
              <span ref={scrollbarPercentRef}>{Math.round(scrollProgress)}%</span>
            </div>
          </div>
        </div>

        {/* Bottom Arrow - Green in normal mode (best hospitals), Red in scenario mode (most expensive at bottom) */}
        <button
          onClick={scrollToBottom}
          className={`flex items-center justify-center w-4 h-4 sm:w-8 sm:h-8 shadow-md transition-all duration-200 hover:shadow-lg hover:scale-105 ${
            isScenarioMode
              ? 'bg-gradient-to-b from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 border border-red-400/50'
              : 'bg-gradient-to-b from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 border border-green-400/50'
          }`}
          style={{ borderRadius: '0 0 8px 8px' }}
          aria-label="Scroll to bottom"
        >
          <ChevronDown className="w-3 h-3 sm:w-5 sm:h-5 text-white" strokeWidth={2.5} />
        </button>
      </div>

      {/* Procedure Drill-down Modal */}
      <ProcedureDrilldownModal
        isOpen={drilldownModal.isOpen}
        onClose={handleDrilldownClose}
        hospitalId={drilldownModal.hospitalId}
        hospitalName={drilldownModal.hospitalName}
        procedureType={drilldownModal.procedureType}
        avgMarkup={drilldownModal.avgMarkup}
      />

      {/* Scenario Coverage Breakdown Modal */}
      {isScenarioMode && (
        <ScenarioCoverageModal
          isOpen={coverageModal.isOpen}
          onClose={() => setCoverageModal({ ...coverageModal, isOpen: false })}
          hospitalId={coverageModal.hospitalId}
          hospitalName={coverageModal.hospitalName}
          scenarioDetail={scenarioDetail}
          scenarioName={scenarioMeta?.name || ''}
          coveragePct={coverageModal.coveragePct}
          matchedCodes={coverageModal.matchedCodes}
          totalCodes={coverageModal.totalCodes}
          hospitalTotalPrice={coverageModal.hospitalTotalPrice}
          nationalTotalPrice={coverageModal.nationalTotalPrice}
        />
      )}
      {/* Scenario Family Modal — variant picker for severity levels with multiple scenarios */}
      {isScenarioMode && activeScenario && (
        <ScenarioFamilyModal
          scenario={{
            id: activeScenario.id,
            name: activeScenario.name,
            description: '',
            icon_name: 'Activity',
            color: SEVERITY_COLORS[Math.min(Math.max(activeScenario.severity_level || 0, 0), 4)],
            scenario_family_id: activeScenario.scenario_family_id,
          }}
          allRawScenarios={allScenarios.map((s: any) => ({
            id: s.id,
            name: s.name,
            severity_level: s.severity_level,
            estimated_cost: s.estimated_cost,
            scenario_family_id: s.scenario_family_id,
            family_variant_label: s.family_variant_label,
            total_procedures: s.total_procedures,
          }))}
          open={showFamilyModal}
          onOpenChange={setShowFamilyModal}
          theme={{
            cardBg: isAhp ? (isDark ? '#0f172a' : '#faf9f6') : '#1a1a2e',
            border: isAhp ? (isDark ? 'rgba(197,165,90,0.2)' : '#e2e0d8') : 'rgba(255,255,255,0.1)',
            shadow: 'rgba(0,0,0,0.2)',
            red: '#B22234',
            gold: '#C5A55A',
            green: '#2b5e49',
            blue: '#002868',
            text: isAhp ? (isDark ? '#e2e8f0' : '#1B1B1B') : '#e2e8f0',
            textMuted: isAhp ? (isDark ? 'rgba(226,232,240,0.5)' : '#6b7280') : 'rgba(226,232,240,0.5)',
          }}
          onVariantSelect={(s) => {
            const id = s.id || (s as any).uuid || '';
            const newParams = new URLSearchParams(searchParams);
            newParams.set('scenario', id);
            setSearchParams(newParams);
            setCurrentPage(1);
          }}
        />
      )}
      {/* Dignity Narration Player — hidden inline trigger, only overlay visible */}
      {!isScenarioMode && (
        <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', pointerEvents: 'none' }}>
          <Suspense fallback={null}>
            <DignityNarrationPlayer pageKey="hospital-rankings" />
          </Suspense>
        </div>
      )}
    </TooltipProvider>
  );
};

export default HospitalRankings;